import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
// Radix Themes scopes all of its CSS to the `.radix-themes` element rendered by <Theme>.
// MUI's Dialog portals to document.body, which is OUTSIDE that element, so any Radix
// component inside a MUI Dialog renders completely unstyled — buttons come out as bare
// inline text and stop looking (or behaving) like buttons. Re-wrapping the dialog content
// in <Theme> restores the styles inside the portal. Same reason ScanDevicePage re-wraps.
import { Theme } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';
import { formatContactMethod } from '../utils/formatters';
import { formatFrequency } from '../utils/frequencyUtils';
import {
  CarePlanRefreshSource,
  REGENERATE_PATIENT_ID_STORAGE_KEY,
  REGENERATE_SOURCE_STORAGE_KEY,
  getCarePlanRefreshSourceOptions,
} from '../utils/carePlanRefreshSource';
import { getOnboardingCarePlanCta } from '../utils/carePlanRegenerationCta';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  Stack,
  Card,
  CardContent,
  TextareaAutosize,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Checkbox,
  Snackbar,
  Tooltip as MuiTooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Radio,
  RadioGroup,
  Collapse,
} from '@mui/material';
import {
  Close,
  KeyboardArrowDown,
  KeyboardArrowRight,
  NoteAdd,
  Assignment,
  Save,
  ExpandMore,
  Send,
  OpenInNew,
  AccessTime,
  Phone,
  PictureAsPdf,
  CloudUpload,
  PhoneInTalk,
  Sms,
  Add,
  Delete,
  Edit,
  Sync,
  Autorenew,
  SupportAgent,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { adminApi, aiInsightsApi, apiRequest, patientApi, organizationApi, billingApi, tellihealthApi } from '../services/api';
import type { SmsReachability } from '../services/api';
import { smsSubscriptionDetail, smsSubscriptionLabel, surveyFailureReason } from '../utils/smsSubscription';
import { useDwellTime } from '../hooks/useDwellTime';
import { useAuth } from '../contexts/AuthContext';
import { useOptionalOrganization } from '../contexts/OrganizationContext';
import TimeTrackingDialog from './TimeTrackingDialog';
import CarePlanApproval from './CarePlanApproval';
import PatientActionsMenu from './PatientActionsMenu';
import ConsentRequestsPanel from './ConsentRequestsPanel';
import VoiceCallDetails from './VoiceCallDetails';
import VoiceCallTranscriptAccordion, { voiceCallLogIdOf } from './VoiceCallTranscriptAccordion';
import type { SxProps, Theme as MuiTheme } from '@mui/material/styles';
import { parseBloodPressure, formatBloodPressure, getAnswerSetUrgencyScore, type BloodPressureValue } from '../utils/bloodPressureValidation';
import { generateCarePlanPdf, generateCarePlanPdfBlob } from '../utils/generateCarePlanPdf';
import { buildCarePlanPdfPayload, fetchCarePlanReviewsForPdf } from '../utils/carePlanPdfPayload';
import {
  CarePlanEhrPushStatus,
  carePlanEhrPushStateOf,
  describeCarePlanEhrPush,
  describeEhrPushFailure,
  isAwaitingEhrConfirmation,
  EHR_PUSH_STATE_LABEL,
  EHR_PUSH_PENDING_DETAIL,
  EHR_PUSH_UPLOADED_DETAIL,
  EHR_PUSH_SUBMITTED_DETAIL,
} from '../utils/carePlanEhrPushState';
import { CarePlanResponse, CarePlanReview, PatientFriendlyPlan, ProblemApprovalItem } from '../types/patient';
import { formatPatientCarePlanHeading, groupBarriersByGoal } from '../utils/carePlanDisplay';
import { maskDateInput, maskedDateToIso, isoToMaskedDate, isFutureMaskedDate, defaultExpirationMasked } from '../utils/dateInputMask';
import { useCarePlanReviewMonths } from '../hooks/useCarePlanReviewMonths';
import { EventProvenance } from './EventProvenance';
import { isValidDate } from '../utils/validateDate';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Dot,
} from 'recharts';
import PatientDeviceSection from './PatientDeviceSection';
import PatientDeviceReadings from './PatientDeviceReadings';
import AiInsightChartTab from './SmartHub/ai/AiInsightChartTab';
import { PATIENT_CHART_STICKY_HEADER_OFFSET } from './dashboardLayout';
import { PATIENT_DETAIL_TAB_INDEX } from './PatientDetailTabIndex';
import PcmIntakeReviewPanel from './PcmIntakeReviewPanel';
import { buildVopGoalEditTarget } from '../utils/voiceOfPatient';
import EhrChangesReviewPanel from './EhrChangesReviewPanel';
import GoalVitalsTable from './GoalVitalsTable';
import {
  acknowledgmentForListItem,
  buildVitalTableRows,
  consumeEhrChangeFocus,
  decisionsForRow,
  goalVitalGroup,
  withBaselineRow,
  type EhrChangeDecision,
  type EhrChangeFocus,
  type ObservationHistory,
  type PatientEhrChanges,
  type VitalTableRow,
} from '../utils/ehrChanges';

interface Patient {
  patient_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone_number?: string;
  phone_type?: 'mobile' | 'landline' | 'voip' | 'unknown' | null;
  phone_verified_at?: string | null;
  gender?: string;
  // PHA-1309: care_team_call = the scheduler is disabled for the patient; staff call.
  preferred_contact_method?: 'sms' | 'voice' | 'care_team_call';
  language?: string;
  problems?: Problem[];
  medications?: Medication[];
  care_plan_definitions?: CarePlanDefinition[];
  most_recent_surveys?: Survey[];
  custom_surveys?: Survey[];
  contact_actions?: ContactAction[];
  answers?: Answer[];
  answers_by_prompt?: AnswersByPrompt;
  custom_answers_by_prompt?: AnswersByPrompt;
  care_transition_events?: Array<{
    id: number;
    encounter_type: string;
    reported_date: string;
    reason_text?: string;
    created_at: string;
  }> | null;
  apcm_tier?: number | null;
  apcm_default_code?: string | null;
  logo_path?: string;
  organization_name?: string;
  [key: string]: any;
}

// Helper function to detect if patient needs to continue onboarding
/**
 * Does this patient still need first-time onboarding?
 *
 * PHA-945: `patient.care_plan_definitions` is populated from the APPROVED
 * care_plan_definitions table only — a draft is invisible to it. A patient whose sole
 * care plan was an unapproved draft therefore read as "never onboarded", got
 * **Continue Onboarding** instead of Regenerate, and the only way to refresh their
 * stale draft was to make a throwaway edit to the problem list to arm a hidden button.
 *
 * PHA-945 closed that by also treating a clinician-started draft as "already onboarded".
 * PHA-1073 reverts that half: a draft is work in progress, NOT evidence of onboarding.
 * A patient whose only plan is a draft has never been enrolled — program consent was
 * never collected — so this predicate must keep saying "yes, still needs onboarding",
 * which is what keeps "Continue Onboarding" on their chart and what CarePlanApproval's
 * `isFirstTimeEnrollment` ultimately traces back to.
 *
 * So the ONLY signal here is an approved plan. PHA-1119 does NOT change that: it adds a
 * second button next to Continue Onboarding for the draft-only patient (see
 * `showRegenerationCta` below) rather than reclassifying them as onboarded, and the
 * regeneration they start still collects first-time program consent because
 * `refreshHasApprovedPlan` is false for them.
 *
 * `hasManualDraftCarePlan` picks the label/behaviour within the already-onboarded branch
 * ("Complete Care Plan Regeneration" resume vs. a fresh "Regenerate Care Plan"), which is
 * what PHA-1050 needed it for, and now also gates that second button.
 */
export const needsContinueOnboarding = (patient: Patient): boolean => {
  const hasCarePlans = patient.care_plan_definitions && patient.care_plan_definitions.length > 0;
  return !hasCarePlans;
};

interface Problem {
  problem: string;
  clinical_status: string;
  onset_date: string;
  uploaded_by: string;
  uploaded_ts: string;
  icd10_code?: string;
  icd10_description?: string;
  icd10_needs_review?: boolean;
  snomed_code?: string;
  snomed_description?: string;
}

interface Medication {
  medication: string;
  dosage?: string;
  start_date: string;
  end_date?: string;
  attributed_ts: string;
  uploaded_by: string;
  uploaded_ts: string;
}

interface CarePlanDefinition {
  disease: string;
  diseases?: string[];
  /** Authoritative backend flag: generic catch-all plan (titled "General Care Plan"). */
  is_general?: boolean;
  uploaded_ts: string;
  expiration_date?: string | null;
  questions: {
    prompt: string;
    data_type: string;
    frequency: string;
  }[];
  tasks: {
    task: string;
    assignee: string;
    frequency: string;
    due_date?: string;
    completed?: boolean;
    completed_at?: string | null;
    occurrence_id?: number;
  }[];
  goals: {
    measure: string;
    test: string;
    due_date: string;
    target_value: string;
    achieved?: boolean;
    achieved_date?: string;
  }[];
  surveys?: {
    name: string;
    survey_id: string;
    frequency: string;
    url: string;
  }[];
  // PHA-773: clinical_disease tags are the title fallback when diseases[] is empty.
  icd10_codes?: { code: string; description: string; clinical_disease?: string | null }[];
  patient_friendly_plan?: PatientFriendlyPlan | null;
}

interface Survey {
  name: string;
  survey_id: string;
  frequency?: string;
  created_at?: string;
  released_at?: string;  // Timestamp when survey was sent via SMS
  is_custom?: boolean;  // True if this is a custom survey created by user
  is_apcm_hospitalization?: boolean;  // True if this is the system-generated APCM hospitalization survey
  answers: {
    prompt: string;
    cast_answer: any;
    raw_answer: string;
    raw_data?: {
      source?: 'voice_call' | 'sms' | string;
      voice_call_log_id?: string;
      [key: string]: any;
    };
  }[];
  questions?: { prompt: string; data_type: string }[];  // Questions for custom surveys
}

// PHA-1231: how a Voice AI call with no saved answers ended (voice_call_logs.outcome).
const VOICE_CALL_OUTCOME_LABELS: Record<string, string> = {
  completed: 'Ended',
  no_answer: 'No answer',
  voicemail: 'Voicemail',
  // PHA-1269: picked up, never spoke.
  greeting_dropoff: 'Hung up during greeting',
  hung_up: 'Patient hung up',
  abandoned: 'Line went silent',
  opted_out: 'Patient opted out',
  not_now: 'Not a good time',
  wrong_person: 'Someone else answered',
  failed: 'Failed'
};

const voiceCallOutcomeLabel = (outcome?: string | null): string =>
  (outcome && VOICE_CALL_OUTCOME_LABELS[outcome]) || 'Ended';

interface ContactAction {
  name: string;
  method: string;
  created_at: string;
  type?: 'answers submitted' | 'survey sent' | 'survey failed' | 'survey deleted' | 'time_tracking' | 'voice call';
  survey_id?: string;
  // PHA-1231: a finished Voice AI call with no saved answers ('voice call').
  voice_call_log_id?: string;
  call_outcome?: string | null;
  ended_reason?: string | null;
  action_status?: string;
  error?: string;
  answers?: {
    prompt: string;
    cast_answer: any;
    raw_answer: string;
    raw_data?: {
      source?: 'voice_call' | 'sms' | string;
      voice_call_log_id?: string;
      [key: string]: any;
    };
  }[];
  duration_seconds?: number;
  notes?: string;
  // PHA-1149: display name of who performed the event ("System" for automation).
  user_name?: string | null;
}

interface Answer {
  question: string;
  [date: string]: any; // Dynamic date keys with answers
}

interface AnswerValue {
  value: any;
  raw_data?: {
    source?: 'voice_call' | 'sms' | string;
    voice_call_log_id?: string;
    [key: string]: any;
  };
}

interface AnswersByPrompt {
  [question: string]: {
    [date: string]: AnswerValue | any; // AnswerValue for new format, any for backward compatibility
  };
}

// Extract answer value from new or old format
const getAnswerValue = (answerData: any): any => {
  if (answerData === null || answerData === undefined) {
    return null;
  }
  // New format: {value: ..., raw_data: ...}
  if (typeof answerData === 'object' && 'value' in answerData) {
    return answerData.value;
  }
  // Old format: direct value
  return answerData;
};

// Check if all values in an answer set are numeric
export const isNumericAnswerSet = (answerSet: any): boolean => {
  const entries = Object.entries(answerSet).filter(([key]) => key !== 'question');
  if (entries.length === 0) return false;

  // Filter out null/undefined values first
  const nonNullEntries = entries.filter(([_, answerData]) => {
    const value = getAnswerValue(answerData);
    return value !== null && value !== undefined;
  });
  if (nonNullEntries.length === 0) return false;

  // Check if all non-null values are numeric
  return nonNullEntries.every(([_, answerData]) => {
    const value = getAnswerValue(answerData);
    // Skip if it's a boolean value
    if (typeof value === 'boolean') return false;
    // Skip if it's a blood pressure object
    if (typeof value === 'object' && value !== null && ('systolic' in value || 'diastolic' in value)) {
      return false;
    }
    const numValue = Number(value);
    return !isNaN(numValue);
  });
};

// Check if answer set contains blood pressure readings
export const isBloodPressureAnswerSet = (answerSet: any): boolean => {
  const entries = Object.entries(answerSet).filter(([key]) => key !== 'question');
  if (entries.length === 0) return false;

  // Check if at least one entry has blood pressure data
  return entries.some(([_, answerData]) => {
    const value = getAnswerValue(answerData);
    if (value === null || value === undefined) return false;
    if (typeof value === 'object' && value !== null) {
      return 'systolic' in value || 'diastolic' in value;
    }
    return false;
  });
};

// Config for mapping device readings to answers_by_prompt format
export interface DeviceReadingConfig {
  label: string;
  matchKeywords: string[];
  toAnswerValue: (data: any) => any;
  /**
   * PHA-1107: does an existing question's answer set already hold values of the
   * shape this reading type produces? A keyword match alone is not enough to
   * merge into a question — see findDeviceMergeTarget. Each entry declares its
   * own predicate so adding a reading type does not mean touching the matcher.
   */
  shapePredicate: (answerSet: any) => boolean;
}

const DEVICE_READING_CONFIG: Record<string, DeviceReadingConfig> = {
  BloodPressure: {
    label: 'Blood Pressure (Device)',
    matchKeywords: ['blood pressure'],
    toAnswerValue: (data) => ({ systolic: data.systolic, diastolic: data.diastolic }),
    shapePredicate: isBloodPressureAnswerSet,
  },
  BloodGlucose: {
    label: 'Blood Glucose (Device)',
    matchKeywords: ['glucose', 'blood sugar'],
    toAnswerValue: (data) => data.glucose,
    shapePredicate: isNumericAnswerSet,
  },
  Weight: {
    label: 'Weight (Device)',
    matchKeywords: ['weight'],
    toAnswerValue: (data) => data.weight,
    shapePredicate: isNumericAnswerSet,
  },
  SpO2: {
    label: 'SpO2 (Device)',
    matchKeywords: ['oxygen', 'spo2', 'pulse ox'],
    toAnswerValue: (data) => data.spo2,
    shapePredicate: isNumericAnswerSet,
  },
  Temperature: {
    label: 'Temperature (Device)',
    matchKeywords: ['temperature'],
    toAnswerValue: (data) => data.temperature,
    shapePredicate: isNumericAnswerSet,
  },
  PeakFlow: {
    label: 'Peak Flow (Device)',
    matchKeywords: ['peak flow', 'fev1', 'pef'],
    toAnswerValue: (data) => data.pef,
    shapePredicate: isNumericAnswerSet,
  },
};

/**
 * Pick the answers_by_prompt key that a batch of device readings should merge
 * into, falling back to the reading type's own label.
 *
 * PHA-1107: a keyword match alone is not enough. Survey prompts are generated,
 * so an adherence question ("Have you taken your blood pressure medications as
 * prescribed this week?") contains 'blood pressure' just as a measurement
 * question does. Merging readings into it made the Answers card render a BP
 * chart *instead of* the Yes/No answer table, hiding the recorded adherence
 * answers entirely.
 *
 * So we also require the question's existing answers to already have the shape
 * this reading type produces, via the config's own `shapePredicate`. A boolean
 * / Yes-No answer set fails every predicate, and the readings fall through to
 * the config label — getting their own "Blood Pressure (Device)" card instead
 * of hijacking the question.
 */
export const findDeviceMergeTarget = (
  base: Record<string, any>,
  config: DeviceReadingConfig,
): string => {
  const matchingQuestion = Object.keys(base).find(question => {
    const qLower = question.toLowerCase();
    if (!config.matchKeywords.some(kw => qLower.includes(kw))) return false;
    return config.shapePredicate({ question, ...base[question] });
  });

  return matchingQuestion || config.label;
};

interface PatientDetailProps {
  // PHA-509: in `inline` (tab) mode neither `open` nor `onClose` are used —
  // the tab strip in Dashboard owns visibility / closing. Kept optional so
  // any straggler modal callers still type-check.
  open?: boolean;
  onClose?: () => void;
  patient: Patient | null;
  onPatientUpdate?: (updatedPatient: Patient) => void;
  // PHA-480: optional tab to land on at mount time (Smart Hub AckDrawer post-ack
  // chart link sends Answers/Device Readings/Care plans). Falls back to 0.
  initialTab?: number;
  // PHA-509: when true, render the body directly (no MUI <Dialog> wrapper)
  // so the patient detail can live inside a Dashboard tab.
  inline?: boolean;
  // PHA-509: bumps every time the parent tab is "re-opened" via openOrFocus.
  // Lets the initialTab effect re-fire even when the new initialTab value
  // matches the current one (e.g. user acks a second item for the same tab).
  reopenNonce?: number;
  // PHA-565: drives `useDwellTime`'s `isActive`. In tab mode every open tab
  // stays mounted (display:none for inactive ones), so without this the dwell
  // hook would never see isActive flip false on tab switch and would never
  // flush. Caller passes `activeTabId === patientId` from PatientTabsContext.
  // Defaults to legacy semantics (`open` for modal mode, always-true for inline).
  isActive?: boolean;
}

// PHA-480 + PHA-573: ordered tab list — see PatientDetailTabIndex.ts for the
// canonical source of truth (kept in its own file to break the circular import
// PatientDetail ↔ SmartHub/ai/CitationLink). This re-export keeps existing
// callers (AckDrawer, etc.) working unchanged. If you reorder or insert tabs,
// update PatientDetailTabIndex.ts AND the JSX below (~L1850).
export { PATIENT_DETAIL_TAB_INDEX } from './PatientDetailTabIndex';

const PatientDetail: React.FC<PatientDetailProps> = ({
  open,
  onClose,
  patient,
  onPatientUpdate,
  initialTab,
  inline = false,
  reopenNonce,
  isActive,
}) => {
  // PHA-509: tab mode treats the component as always-open and routes the
  // legacy Close button to a no-op (the tab × handles closing).
  const handleClose = onClose ?? (() => {});
  const isOpen = inline ? true : !!open;
  const NO_VOICE_CALL_DATA = 'no-data';
  const { user, isSessionWarning } = useAuth();
  // PHA-1241: under "All Organizations" the chart doesn't say which clinic the patient is in,
  // so show it — same gate as the Patient List's Clinic column.
  const showClinic = useOptionalOrganization()?.orgId === 'ALL';
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(initialTab ?? 0);

  // PHA-480: re-sync the tab when a fresh `initialTab` arrives (e.g., the
  // user opens patient B from Smart Hub while patient A is already open —
  // the conditional render in PatientList may not unmount between).
  useEffect(() => {
    if (initialTab !== undefined) {
      setCurrentTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, patient?.patient_id, reopenNonce]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // PHA-510: ad-hoc EHR re-sync from the patient chart. Internal-only,
  // FHIR-source patients only — see render gate below.
  const [ehrSyncBusy, setEhrSyncBusy] = useState(false);
  const [sendSurveyDialogOpen, setSendSurveyDialogOpen] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sendingSurvey, setSendingSurvey] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState<Record<string, boolean>>({});
  const [timeTrackingDialogOpen, setTimeTrackingDialogOpen] = useState(false);
  // PHA-1043: the refresh flow now asks WHICH data to regenerate from instead of encoding
  // the answer in which of two buttons the clinician happened to press.
  const [refreshSourceDialogOpen, setRefreshSourceDialogOpen] = useState(false);
  const [refreshSource, setRefreshSource] = useState<CarePlanRefreshSource>('current');
  // PHA-1072: does the PATIENT'S clinic have an EHR integration configured? Separate
  // question from `patient.source === 'FHIR'` (did this chart arrive through an import),
  // and the one that decides whether "Import latest data from the EHR" is offered to a
  // chart that was onboarded from uploaded images. Fails closed on any error, which is
  // exactly the pre-PHA-1072 behaviour.
  const [orgHasEhrIntegration, setOrgHasEhrIntegration] = useState(false);
  // PHA-1050: does this patient have a care-plan draft a CLINICIAN started (trigger_source
  // not 'ehr_cron')? Drives whether the regeneration CTA reads "Regenerate Care Plan" or
  // "Complete Care Plan Regeneration". null = not yet known, which reads as "no" so the
  // button never flickers into the wrong label.
  // PHA-1073: it no longer decides WHETHER the regeneration CTA shows at all — only an
  // approved plan does that (see needsContinueOnboarding).
  // PHA-1119: except for one case, below — a patient who is still mid-onboarding but already
  // has a draft plan gets the regeneration CTA *alongside* Continue Onboarding.
  const [hasManualDraftCarePlan, setHasManualDraftCarePlan] = useState<boolean | null>(null);

  /**
   * PHA-1119: which chart CTAs a patient who has not finished onboarding gets.
   *
   * Reported on Wallabout ODA patient 116325: re-opening a patient whose care plan was left
   * as a draft in July offered only "Continue Onboarding", which resumes at whatever step the
   * clinician left off at — for this patient, straight onto the Care Plan step. That skips
   * both the Upload step and the "which conditions should this plan focus on" step, so there
   * was no way to re-upload a current problem/medication list or to narrow the plan down.
   *
   * The two scenarios, per Moshe on the ticket:
   *
   *   1. Mid-onboarding AND a draft plan already exists → BOTH buttons. "Continue Onboarding"
   *      keeps resuming exactly where they left off; "Regenerate Care Plan" starts the same
   *      regeneration process any patient with a care plan gets — the source picker, then
   *      Upload / Review / condition selection.
   *   2. Mid-onboarding with NO care plan yet → "Continue Onboarding" ONLY. There is nothing
   *      to regenerate, and the first-time wizard already walks the whole sequence.
   *
   * `hasManualDraftCarePlan` is loaded for every patient (see loadManualDraftState), and null
   * (not yet known / fetch failed) reads as "no draft" so the second button never flickers in.
   *
   * PHA-1073's invariant is untouched: a draft is still NOT evidence of onboarding, so
   * `needsContinueOnboarding` is unchanged and these patients keep the first-time program
   * consent capture at the end of the regeneration — see `isFirstTimeEnrollment` in
   * CarePlanApproval.tsx, which is what keeps the consent step reachable for them.
   */
  const carePlanCta = getOnboardingCarePlanCta({
    hasApprovedCarePlan: !!patient && !needsContinueOnboarding(patient),
    hasManualDraftCarePlan,
  });
  const showRegenerationCta = !!patient && carePlanCta.showRegeneration;
  /**
   * PHA-1050's "re-enter the draft instead of opening the source picker" shortcut is for an
   * ENROLLED patient mid-regeneration, where opening the picker would generate a second plan
   * on top of the one they are editing. A mid-onboarding patient reaches that same draft
   * through "Continue Onboarding", so their regeneration button must always mean "start over
   * from the source picker" — that is the whole point of PHA-1119.
   */
  const regenerationCtaResumesDraft = carePlanCta.regenerationResumesDraft;
  // PHA-726/PHA-897: "care plan reviewed with patient" records, newest first. Separate from
  // patients.consent_status, which stays the one-time programme consent.
  const [carePlanAcknowledgments, setCarePlanAcknowledgments] = useState<Array<{
    acknowledged_ts: string;
    acknowledged_by_name?: string | null;
    care_plan_uploaded_ts?: string | null;
  }>>([]);
  const [editMode, setEditMode] = useState(false);
  const [editedPatient, setEditedPatient] = useState<Patient | null>(null);
  const [editDob, setEditDob] = useState('');

  // PHA-795 (flow redesign): Voice-of-patient tab is button-first. The staged wizard
  // (PcmIntakeReviewPanel) opens on demand via "+ Create Voice of Patient Goals".
  const [voiceWizardOpen, setVoiceWizardOpen] = useState(false);
  // PHA-851: VoP goals are additive. `editingVopGoal` holds the existing goal being
  // edited (null = add a new goal); `removingVopRef` disables a goal's Remove button
  // while its delete is in flight.
  const [editingVopGoal, setEditingVopGoal] = useState<any | null>(null);
  const [removingVopRef, setRemovingVopRef] = useState<string | null>(null);
  // PHA-831: lifted unsaved-changes flag so we can guard tab switches even after
  // the panel unmounts (it unmounts when the user switches to another tab).
  const [voiceTabHasUnsaved, setVoiceTabHasUnsaved] = useState(false);

  // VAPI AI Voice Call state
  const [vapiCallDialogOpen, setVapiCallDialogOpen] = useState(false);
  const [selectedSurveyName, setSelectedSurveyName] = useState<string | null>(null);
  const [selectedVapiSurveyId, setSelectedVapiSurveyId] = useState<string | null>(null);
  const [vapiPhoneNumber, setVapiPhoneNumber] = useState('');
  // PHA-1281: shown inside the call dialog. The page-level error renders behind the
  // modal, so a refused call (e.g. "All voice lines are busy") looked like nothing happened.
  const [vapiCallError, setVapiCallError] = useState<string | null>(null);
  const [callingPatient, setCallingPatient] = useState(false);
  
  // Goal/Task completion state (for optimistic updates)
  const [carePlanDefinitions, setCarePlanDefinitions] = useState<CarePlanDefinition[]>([]);
  const [pendingUpdates, setPendingUpdates] = useState<Set<string>>(new Set());

  // PHA-870: Care Plan Review — care-team review records (Care Plans tab, clinician only)
  const [carePlanReviews, setCarePlanReviews] = useState<CarePlanReview[]>([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewDateMasked, setReviewDateMasked] = useState('');
  const [reviewPatientComments, setReviewPatientComments] = useState('');
  const [reviewActionTaken, setReviewActionTaken] = useState('');
  const [savingReview, setSavingReview] = useState(false);
  // null = add mode; a review id = edit mode
  const [editingReviewId, setEditingReviewId] = useState<number | null>(null);
  const [deletingReview, setDeletingReview] = useState<CarePlanReview | null>(null);
  const [deletingReviewBusy, setDeletingReviewBusy] = useState(false);

  // PHA-397: EHR-refresh draft care plan review
  const [pendingDraftCarePlans, setPendingDraftCarePlans] = useState<CarePlanResponse | null>(null);
  // PHA-1225: reloads the consent-by-text list after the Actions menu sends one.
  const [consentRequestsRefreshKey, setConsentRequestsRefreshKey] = useState(0);
  const [showDraftReviewModal, setShowDraftReviewModal] = useState(false);
  // PHA-1074: goal editing directly on an approved care plan (pencil-edit staged draft)
  // was reverted per product decision — see PHA-1074 comment thread. Editing a goal now
  // requires purposefully starting a new draft via Regenerate Care Plan.
  const [draftPlanGenerated, setDraftPlanGenerated] = useState(false);
  // PHA-895 follow-up: persisted onboarding/manual drafts surfaced READ-ONLY in the Care
  // Plans tab (and driving the Download PDF) when the patient has no approved plan yet.
  // Distinct from pendingDraftCarePlans (EHR-refresh banner, ehrOnly=true).
  const [draftCarePlansForView, setDraftCarePlansForView] = useState<CarePlanDefinition[]>([]);
  // PHA-951: "we haven't loaded the draft yet" and "the draft fetch failed" are NOT the same
  // thing as "this patient has no care plan", but an empty array says all three. A clinician
  // presenting to a customer saw the Care Plans tab render as empty because a transient BFF
  // 502 was swallowed into []. Track the load outcome so the tab can say which one it is.
  const [draftCarePlanLoadState, setDraftCarePlanLoadState] =
    useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  // PHA-1285: the review date of a draft lives in care_plan_draft_meta (MM/DD/YYYY) until
  // approval copies it onto the plan, so a draft row's expiration_date is always null. Read it
  // from the meta, or the Care Plans tab says "not set" for a date the clinician already chose.
  const [draftReviewDateMasked, setDraftReviewDateMasked] = useState<string | null>(null);
  // The fetch is not awaited by the patient-switch reset, so a late answer for the previous
  // patient must not land on this one (PHA-951).
  const patientIdRef = useRef(patient?.patient_id);
  patientIdRef.current = patient?.patient_id;
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  // PHA-1141 / PHA-1142: EHR changes reviewed item by item in the chart. `ehrChanges` feeds the
  // Problems / Medications review panels; `observationHistory` feeds the goal vitals tables.
  const [ehrChanges, setEhrChanges] = useState<PatientEhrChanges | null>(null);
  const [observationHistory, setObservationHistory] = useState<ObservationHistory | null>(null);
  // What the Smart Hub asked the chart to show, consumed once (see utils/ehrChanges).
  const [ehrFocus, setEhrFocus] = useState<EhrChangeFocus | null>(null);
  const [highlightedEhrSection, setHighlightedEhrSection] = useState<'problems' | 'medications' | null>(null);
  // Goals accordions are controlled so a Smart Hub deep link can open them. Keyed by plan index.
  const [goalsAccordionOpen, setGoalsAccordionOpen] = useState<Record<number, boolean>>({});
  // Open goal vitals tables, keyed `${planIndex}:${goalIndex}`.
  const [openGoalVitals, setOpenGoalVitals] = useState<Set<string>>(new Set());
  const [otherReadingsOpen, setOtherReadingsOpen] = useState(false);
  const [vitalAckBusy, setVitalAckBusy] = useState<Set<string>>(new Set());
  // PHA-983: whether this patient's clinic can push care plans to the EHR, and what the last
  // push did. null until the status call answers, which keeps the button hidden rather than
  // flashing it for clinics that will turn out not to have the feature.
  const [carePlanPushStatus, setCarePlanPushStatus] = useState<CarePlanEhrPushStatus | null>(null);
  const [pushingToEhr, setPushingToEhr] = useState(false);
  // PHA-1213: the button waits for the EHR's verdict on ITS push (by id) in a modal. Closing
  // the modal only stops waiting — the Care Plans tab keeps showing Pending until it lands.
  const [ehrPushWait, setEhrPushWait] = useState<{ pushId: string; timedOut: boolean } | null>(null);
  // PHA-1195: a first print in the patient's language waits on translation; show it is working.
  const [downloadingCarePlan, setDownloadingCarePlan] = useState(false);
  // PHA-737: Save as Draft loading state for the care-plan panel quick-save button
  const [savingDraftInPanel, setSavingDraftInPanel] = useState(false);
  
  // Device readings for interleaving in Answers tab
  const [deviceReadings, setDeviceReadings] = useState<any[]>([]);

  // Linked RPM device (for annotating blood pressure questions)
  const [patientLinkedDevice, setPatientLinkedDevice] = useState<any>(null);

  // PHA-902: the "Call Patient (AI Assistant)" action is gated on the clinic's org-level Voice AI
  // flag (voiceEnabled, below) — NOT the global REACT_APP_ENABLE_VAPI_CALLS build flag, which is
  // off by default on dev/staging and so wrongly hid the button for voice-AI-enabled clinics.
  // Manually calling a patient with voice AI is always an option when the clinic has Voice AI on;
  // it is independent of the patient's preferred_contact_method (that preference is scheduling-only).

  // Organization-level voice enabled
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  // PHA-795: whether the patient's org has Voice of Patient intake enabled
  const [voiceOfPatientEnabled, setVoiceOfPatientEnabled] = useState(false);
  // PHA-866: whether the patient's org hides target dates on goals & tasks
  const [noTargetDatesEnabled, setNoTargetDatesEnabled] = useState(false);
  // PHA-1080: the clinic's care plan review cycle, for the approval wizard's review-date
  // default and the Print Draft PDF's projected date. Gated on isOpen like the org-flag
  // effects below, so a closed chart doesn't fire the request.
  const carePlanReviewMonths = useCarePlanReviewMonths(
    patient?.organization_id || patient?.organization,
    isOpen,
  );
  const [aiInsightsEnabled, setAiInsightsEnabled] = useState(false);

  // Supported languages for the organization
  const [supportedLanguages, setSupportedLanguages] = useState<Array<{code: string, name: string, native_name: string}>>([]);

  // PHA-584: in inline mode the DialogTitle (heading + action buttons) must be
  // sticky so buttons stay reachable as the chart scrolls. Track the rendered
  // height so the data tabs can pin directly beneath it without a hardcoded offset.
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleHeightPx, setTitleHeightPx] = useState(64);
  useLayoutEffect(() => {
    if (!inline || !titleRef.current) return;
    const el = titleRef.current;
    setTitleHeightPx(el.offsetHeight);
    // Defer the height write to the next frame so it can't synchronously
    // re-trigger this observer ("ResizeObserver loop completed with undelivered
    // notifications" — benign, but CRA's dev overlay surfaces it as an error).
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTitleHeightPx(el.offsetHeight));
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [inline]);

  // Dwell-time tracking — centralized hook (PHA-441).
  // Skipped for non-onboarded patients (matches legacy logPageViewSession guard).
  // PHA-565: if the caller passes `isActive` explicitly (tab mode, where every
  // tab stays mounted and `isOpen` is always true), use it so switching tabs
  // flushes the deactivated patient. Otherwise fall back to legacy modal
  // semantics so non-tab callers keep working.
  const dwellIsActive = isActive ?? (isOpen && !!patient);
  useDwellTime({
    patientId: patient?.patient_id ?? null,
    isActive: dwellIsActive,
    minimumSeconds: 5,
    skip: !!patient && needsContinueOnboarding(patient),
    // PHA-710: pause dwell accumulation while the session warning modal is shown
    // or the screen is locked, so idle/warning time is not billed as care time.
    paused: isSessionWarning,
  });


  // Action report state for displaying manual and auto-tracked time in event log
  const [actionReportData, setActionReportData] = useState<any[]>([]);
  const [loadingActionReport, setLoadingActionReport] = useState(false);
  const [eventLogFilter, setEventLogFilter] = useState<'all' | 'manual' | 'auto' | 'surveys'>('all');

  // Voice call details state for showing transcripts and recordings
  const [voiceCallDetails, setVoiceCallDetails] = useState<Record<string, any>>({});
  const [loadingVoiceCallDetails, setLoadingVoiceCallDetails] = useState<Record<string, boolean>>({});
  const [voiceCallModalOpen, setVoiceCallModalOpen] = useState(false);
  const [selectedVoiceCallLogId, setSelectedVoiceCallLogId] = useState<string | null>(null);

  // Custom survey modal state
  const [customSurveyModalOpen, setCustomSurveyModalOpen] = useState(false);
  const [customSurveyName, setCustomSurveyName] = useState('');
  const [customSurveyQuestions, setCustomSurveyQuestions] = useState<{
    prompt: string;
    data_type: string;
  }[]>([]);
  const [creatingSurvey, setCreatingSurvey] = useState(false);
  const [editingSurveyId, setEditingSurveyId] = useState<string | null>(null);  // null = create mode, string = edit mode
  const [deletingSurveyId, setDeletingSurveyId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // PHA-831: guard tab switches when the VoP panel has unsaved changes.
    // The panel unmounts on switch so we read the lifted flag here.
    if (
      voiceTabHasUnsaved &&
      currentTab === PATIENT_DETAIL_TAB_INDEX.VOICE_OF_PATIENT
    ) {
      if (
        !window.confirm(
          'You have unsaved changes in the Voice of the Patient tab. Leave without saving?'
        )
      ) {
        return;
      }
      setVoiceTabHasUnsaved(false);
    }
    setCurrentTab(newValue);
  };

  // Initialize edited patient when patient changes
  useEffect(() => {
    if (patient) {
      setEditedPatient(patient);
    }
  }, [patient]);
  
  // Sync local carePlanDefinitions with patient data for optimistic updates
  useEffect(() => {
    if (patient?.care_plan_definitions) {
      setCarePlanDefinitions(patient.care_plan_definitions);
    }
  }, [patient?.care_plan_definitions]);

  // PHA-951: the care-plan loaders below now deliberately KEEP their last good data when a
  // fetch fails, instead of blanking the tab. That is only safe while the data belongs to the
  // patient on screen — so drop it the moment the chart switches patients. Without this, a
  // failed fetch for patient B would leave patient A's draft care plan rendered under B's name,
  // which is far worse than the empty state this ticket set out to fix. Declared BEFORE the
  // loader effect so it runs first on a patient change.
  useEffect(() => {
    setDraftCarePlansForView([]);
    setDraftCarePlanLoadState('idle');
    setDraftReviewDateMasked(null);
    setCarePlanReviews([]);
    setPendingDraftCarePlans(null);
    // PHA-983: clear for the same reason — a stale "already sent to eCW" carried across a
    // patient switch would tell a clinician this chart's plan is filed when it is not.
    setCarePlanPushStatus(null);
    setEhrPushWait(null);
  }, [patient?.patient_id]);

  // PHA-983: resolve whether the "Push Care Plan to EMR" button applies to this patient's clinic.
  // Failure leaves the state null, which keeps the button hidden — the safe direction, since
  // offering a push we cannot perform is worse than not offering one.
  useEffect(() => {
    if (!isOpen || !patient?.patient_id) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await patientApi.getCarePlanEhrPushStatus(patient.patient_id);
        if (!cancelled) setCarePlanPushStatus(status);
      } catch (err) {
        console.error('PHA-983: care plan EHR push status fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, patient?.patient_id]);

  // PHA-1212 / PHA-1213: the EHR's verdict on an upload arrives by webhook, normally within a
  // minute. While the current plan's latest push is Pending, re-read the status so the tab
  // (and the PHA-1213 modal, which reads the same state) turns to Uploaded/Failed on its own.
  // Stops once the push resolves or goes stale (describeCarePlanEhrPush) — every response is a
  // new object, so each tick re-evaluates that condition.
  const awaitingEhrConfirmation = isAwaitingEhrConfirmation(carePlanPushStatus);
  const awaitedPushId = carePlanPushStatus?.last_push?.push_id;
  useEffect(() => {
    const patientId = patient?.patient_id;
    if (!isOpen || !patientId || !awaitingEhrConfirmation) return;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const status = await patientApi.getCarePlanEhrPushStatus(patientId);
        // The BFF answers a backend blip with a 200 "{ push_enabled: false, last_push: null }"
        // fallback. Taking that would hide the button and stop this poll mid-wait, so a
        // reply that has lost the push we are waiting on is treated as a failed tick.
        if (!status?.push_enabled || !status?.last_push) return;
        if (!cancelled) setCarePlanPushStatus(status);
      } catch {
        // Transient; the next tick retries.
      }
    }, 4000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [isOpen, patient?.patient_id, awaitingEhrConfirmation, awaitedPushId]);

  // PHA-1213: give up waiting in the modal after 90s. The push stays Pending in the tab.
  const waitingPushId = ehrPushWait?.pushId;
  useEffect(() => {
    if (!waitingPushId) return;
    const timer = window.setTimeout(() => {
      setEhrPushWait(prev => (prev && prev.pushId === waitingPushId ? { ...prev, timedOut: true } : prev));
    }, 90000);
    return () => window.clearTimeout(timer);
  }, [waitingPushId]);

  // PHA-1050: the CTA lives in the chart header, which is visible on every tab, so this
  // cannot hang off the Care Plans tab the way the other draft loaders do.
  useEffect(() => {
    const patientId = patient?.patient_id;
    if (!isOpen || !patientId) return;
    setHasManualDraftCarePlan(null);
    void loadManualDraftState(patientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient?.patient_id]);

  // PHA-397: Load pending EHR-refresh draft care plans when the Care Plans tab is active
  useEffect(() => {
    if (currentTab === 2 && patient?.patient_id) {
      void loadPendingDraftCarePlans();
      void loadCarePlanReviews();
      void loadCarePlanAcknowledgments();
      // PHA-895 follow-up: with no approved plan, surface the persisted draft read-only.
      if (!(patient?.care_plan_definitions && patient.care_plan_definitions.length > 0)) {
        void loadDraftCarePlansForView();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, patient?.patient_id]);

  // PHA-1141 / PHA-1142: EHR changes to review and the vitals history. Both are chart-wide (the
  // Problems, Medications and Care plans tabs all read them), so they load with the patient,
  // not with a tab. A failed read keeps what is on screen — an empty list would claim there
  // is nothing to review.
  const loadEhrChanges = async (patientId: string) => {
    try {
      setEhrChanges(await patientApi.getEhrChanges(patientId));
    } catch (err) {
      console.error('PHA-1141: EHR changes fetch failed:', err);
    }
  };
  const loadObservationHistory = async (patientId: string) => {
    try {
      setObservationHistory(await patientApi.getObservationHistory(patientId));
    } catch (err) {
      console.error('PHA-1142: observation history fetch failed:', err);
    }
  };

  useEffect(() => {
    const patientId = patient?.patient_id;
    if (!isOpen || !patientId) return;
    setEhrChanges(null);
    setObservationHistory(null);
    setOpenGoalVitals(new Set());
    setGoalsAccordionOpen({});
    setOtherReadingsOpen(false);
    void loadEhrChanges(patientId);
    void loadObservationHistory(patientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient?.patient_id]);

  // A Smart Hub deep link: open the chart where the change is, and refresh — the chart may
  // have been open (with stale data) since before the nightly sync found it.
  useEffect(() => {
    const patientId = patient?.patient_id;
    if (!patientId) return;
    const focus = consumeEhrChangeFocus(patientId);
    if (!focus) return;
    setEhrFocus(focus);
    setHighlightedEhrSection(focus.section === 'vitals' ? null : focus.section);
    void loadEhrChanges(patientId);
    void loadObservationHistory(patientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.patient_id, reopenNonce, initialTab]);

  // Vitals deep link: once the history and the plan are on screen, open the goal tables the
  // new readings belong to (or "Other readings") and scroll to the first.
  useEffect(() => {
    if (ehrFocus?.section !== 'vitals' || currentTab !== PATIENT_DETAIL_TAB_INDEX.CARE_PLANS) return;
    if (!observationHistory) return;
    const plans = carePlanDefinitions.length > 0 ? carePlanDefinitions : draftCarePlansForView;
    if (plans.length === 0) return;
    const wanted = new Set(
      ehrFocus.documentTypes && ehrFocus.documentTypes.length > 0
        ? ehrFocus.documentTypes
        : observationHistory.readings.filter((r) => r.pending && r.pending.length > 0).map((r) => r.document_type),
    );
    const nextOpen = new Set(openGoalVitals);
    const nextAccordions = { ...goalsAccordionOpen };
    let target: string | null = null;
    const covered = new Set<string>();
    plans.forEach((plan, planIndex) => {
      (plan.goals || []).forEach((goal: any, goalIndex: number) => {
        const group = goalVitalGroup(goal?.measure);
        if (!group) return;
        group.documentTypes.forEach((t) => covered.add(t));
        if (!group.documentTypes.some((t) => wanted.has(t))) return;
        nextOpen.add(`${planIndex}:${goalIndex}`);
        nextAccordions[planIndex] = true;
        if (!target) target = `goal-vitals-${planIndex}-${goalIndex}`;
      });
    });
    if (Array.from(wanted).some((t) => !covered.has(t))) {
      setOtherReadingsOpen(true);
      if (!target) target = 'ehr-other-readings';
    }
    setOpenGoalVitals(nextOpen);
    setGoalsAccordionOpen(nextAccordions);
    setEhrFocus(null);
    const scrollTo = target;
    if (scrollTo) {
      window.setTimeout(() => {
        document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 350);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehrFocus, currentTab, observationHistory, carePlanDefinitions, draftCarePlansForView]);

  // Problems / Medications deep link: scroll to the review panel.
  useEffect(() => {
    if (!ehrFocus || ehrFocus.section === 'vitals' || !ehrChanges) return;
    const tabForSection =
      ehrFocus.section === 'problems' ? PATIENT_DETAIL_TAB_INDEX.PROBLEMS : PATIENT_DETAIL_TAB_INDEX.MEDICATIONS;
    if (currentTab !== tabForSection) return;
    const id = `ehr-review-${ehrFocus.section === 'problems' ? 'problem_list' : 'medication_list'}`;
    setEhrFocus(null);
    window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
  }, [ehrFocus, ehrChanges, currentTab]);

  /** Record decisions, then refresh everything they touch: lists, tables, the Smart Hub. */
  const submitEhrDecisions = async (decisions: EhrChangeDecision[]) => {
    if (!patient || decisions.length === 0) return;
    const patientId = patient.patient_id;
    try {
      const result = await patientApi.acknowledgeEhrChanges(patientId, decisions);
      await Promise.all([loadEhrChanges(patientId), loadObservationHistory(patientId)]);
      if ((result?.lists_updated || []).length > 0 && onPatientUpdate) {
        onPatientUpdate(await patientApi.get(patientId));
      }
      window.dispatchEvent(new CustomEvent('phanova:smart-hub-refresh'));
      setSnackbarSeverity('success');
      setSnackbarMessage(
        result?.skipped
          ? `Saved. ${result.skipped} change${result.skipped === 1 ? ' was' : 's were'} already acknowledged by someone else.`
          : 'EHR changes acknowledged.'
      );
    } catch (err: any) {
      console.error('PHA-1141: acknowledging EHR changes failed:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage(err?.message || 'Could not save the acknowledgment. Please try again.');
      throw err;
    }
  };

  const acknowledgeVitalRows = async (rows: VitalTableRow[]) => {
    const keys = rows.map((r) => r.key);
    setVitalAckBusy((prev) => new Set([...Array.from(prev), ...keys]));
    try {
      await submitEhrDecisions(rows.flatMap(decisionsForRow));
    } catch {
      // Reported by submitEhrDecisions.
    } finally {
      setVitalAckBusy((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }
  };

  // PHA-1229: whether the PATIENT's org has Voice AI enabled — not the clinic switcher's.
  // Asking without an org id resolved to the header org, so under "All Organizations" a
  // voice patient read voice_enabled=false and the contact-method Select rendered blank.
  // The session org remains the fallback only for a patient with no org.
  useEffect(() => {
    if (!isOpen) return;
    const orgId = patient?.organization_id || patient?.organization;
    let cancelled = false;
    (async () => {
      try {
        const result = await patientApi.checkOrgVoiceEnabled(orgId ? String(orgId) : undefined);
        if (!cancelled) setVoiceEnabled(result.voice_enabled === true);
      } catch {
        if (!cancelled) setVoiceEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient?.organization_id, patient?.organization]);

  // PHA-1187: has Twilio reported this patient's number as unable to receive SMS?
  // Re-checked when the phone number changes — saving a new number clears the flag.
  const [smsReachability, setSmsReachability] = useState<SmsReachability | null>(null);
  useEffect(() => {
    if (!isOpen || !patient?.patient_id) {
      setSmsReachability(null);
      return;
    }
    let cancelled = false;
    const orgId = patient?.organization_id || patient?.organization;
    patientApi
      .getPatientSmsReachability(patient.patient_id, orgId ? String(orgId) : undefined)
      .then((result) => {
        if (!cancelled) setSmsReachability(result);
      })
      .catch(() => {
        // Advisory badge only: a failed lookup must never block the chart.
        if (!cancelled) setSmsReachability(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, patient?.patient_id, patient?.phone_number, patient?.organization_id, patient?.organization]);

  // PHA-795: Check if the patient's org has Voice of Patient intake enabled
  useEffect(() => {
    if (!isOpen) return;
    const orgId = patient?.organization_id || patient?.organization;
    if (!orgId) {
      setVoiceOfPatientEnabled(false);
      return;
    }
    const checkVoiceOfPatient = async () => {
      try {
        const result = await patientApi.getOrgVoiceOfPatientEnabled(String(orgId));
        setVoiceOfPatientEnabled(result.voice_of_patient_enabled === true);
      } catch {
        setVoiceOfPatientEnabled(false);
      }
    };
    checkVoiceOfPatient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient?.organization_id, patient?.organization]);

  // PHA-1072: whether the patient's org can reach an EHR at all. Resolved from the
  // PATIENT's organization, never the clinic switcher — PHA-959 is the bug where the
  // header's org and the flag's org disagreed. `_resolve_org_flag_target` answers False
  // for an empty org id, so an unresolvable org simply hides the EHR option.
  useEffect(() => {
    if (!isOpen) return;
    const orgId = patient?.organization_id || patient?.organization;
    if (!orgId) {
      setOrgHasEhrIntegration(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await patientApi.checkOrgEhrIntegration(String(orgId));
        if (!cancelled) setOrgHasEhrIntegration(result.has_ehr_integration === true);
      } catch {
        if (!cancelled) setOrgHasEhrIntegration(false);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient?.organization_id, patient?.organization]);

  // PHA-866: whether the patient's org hides target dates on goals & tasks.
  // Prefer the value delivered inline with the patient payload (atomic with the
  // data, no separate request, no race). Fall back to a dedicated request — with
  // a retry so a transient blip doesn't fail-open to showing dates — only when
  // the inline value is absent (e.g. an older cached payload).
  useEffect(() => {
    if (!isOpen) return;

    const inline = (patient as any)?.no_target_dates_enabled;
    if (typeof inline === 'boolean') {
      setNoTargetDatesEnabled(inline);
      return;
    }

    const orgId = patient?.organization_id || patient?.organization;
    if (!orgId) {
      setNoTargetDatesEnabled(false);
      return;
    }

    let cancelled = false;
    const checkNoTargetDates = async (attempt = 1): Promise<void> => {
      try {
        const result = await patientApi.getOrgNoTargetDatesEnabled(String(orgId));
        if (!cancelled) setNoTargetDatesEnabled(result.no_target_dates_enabled === true);
      } catch {
        if (cancelled) return;
        if (attempt < 3) {
          setTimeout(() => { void checkNoTargetDates(attempt + 1); }, 400 * attempt);
          return;
        }
        setNoTargetDatesEnabled(false);
      }
    };
    void checkNoTargetDates();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, (patient as any)?.no_target_dates_enabled, patient?.organization_id, patient?.organization]);

  // Check if current organization has SmartHub AI Insights enabled
  useEffect(() => {
    if (!isOpen) return;
    const checkAiInsights = async () => {
      try {
        const data = await aiInsightsApi.list();
        setAiInsightsEnabled(Boolean(data?.enabled));
      } catch {
        setAiInsightsEnabled(false);
      }
    };
    checkAiInsights();
  }, [isOpen]);

  // Fetch the patient's org supported languages (PHA-1229: patient org, not the switcher)
  useEffect(() => {
    if (!isOpen) return;
    const orgId = patient?.organization_id || patient?.organization;
    let cancelled = false;
    const fetchLanguages = async () => {
      try {
        const data = await organizationApi.getSupportedLanguages(orgId ? String(orgId) : undefined);
        if (!cancelled && Array.isArray(data)) {
          setSupportedLanguages(data);
        }
      } catch (err) {
        console.error('Failed to fetch supported languages:', err);
        if (!cancelled) setSupportedLanguages([{ code: 'en-US', name: 'English', native_name: 'English' }]);
      }
    };
    fetchLanguages();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient?.organization_id, patient?.organization]);

  // Fetch action report when patient changes to populate event log with time tracking entries
  useEffect(() => {
    const fetchActionReport = async () => {
      if (!patient?.patient_id) {
        setActionReportData([]);
        return;
      }
      
      setLoadingActionReport(true);
      try {
        // PHA-891 (addi, 2026-09-01): the Event Log needs the UNMERGED per-event
        // rows (each with its own real timestamp) — actionReportData is used
        // nowhere else in this component, so this is the only fetch to repoint.
        const result = await billingApi.getEventLog(patient.patient_id);
        setActionReportData(result.records || []);
      } catch (error) {
        console.error('Failed to fetch action report:', error);
        setActionReportData([]);
      } finally {
        setLoadingActionReport(false);
      }
    };
    
    fetchActionReport();
  }, [patient?.patient_id]);

  // Load device readings for interleaving in Answers tab
  useEffect(() => {
    if (!patient?.patient_id) return;
    tellihealthApi.getPatientReadings(patient.patient_id, { limit: 500 })
      .then((data) => setDeviceReadings(data.readings || []))
      .catch(() => setDeviceReadings([]));
  }, [patient?.patient_id]);

  // Merge device readings into answers_by_prompt format
  const mergedAnswersByPrompt = useMemo(() => {
    const base: Record<string, any> = { ...(patient?.answers_by_prompt || {}) };

    // Group device readings by type
    const readingsByType: Record<string, any[]> = {};
    for (const reading of deviceReadings) {
      const type = reading.reading_type;
      if (!readingsByType[type]) readingsByType[type] = [];
      readingsByType[type].push(reading);
    }

    for (const [readingType, readings] of Object.entries(readingsByType)) {
      const config = DEVICE_READING_CONFIG[readingType];
      if (!config) continue; // Skip unsupported types (e.g. SleepingMat)

      const targetQuestion = findDeviceMergeTarget(base, config);

      if (!base[targetQuestion]) {
        base[targetQuestion] = {};
      }

      // Add each device reading as a date entry
      for (const reading of readings) {
        const dateKey = new Date(reading.time).toISOString();
        base[targetQuestion][dateKey] = {
          value: config.toAnswerValue(reading.data),
          raw_data: {
            source: 'device',
            reading_type: readingType,
            device_id: reading.device_id,
            full_data: reading.data,
          },
        };
      }
    }

    return base;
  }, [patient?.answers_by_prompt, deviceReadings]);

  // Refresh action report (Event Log, unmerged) after manual time entry
  const refreshActionReport = async () => {
    if (!patient?.patient_id) return;

    setLoadingActionReport(true);
    try {
      const result = await billingApi.getEventLog(patient.patient_id);
      setActionReportData(result.records || []);
    } catch (error) {
      console.error('Failed to refresh action report:', error);
    } finally {
      setLoadingActionReport(false);
    }
  };

  const handleOpenSendSurveyDialog = (surveyId: string) => {
    setSelectedSurveyId(surveyId);
    setPhoneNumber(patient?.phone_number || '');
    setSendSurveyDialogOpen(true);
  };

  const handleCloseSendSurveyDialog = () => {
    setSendSurveyDialogOpen(false);
    setSelectedSurveyId(null);
    setPhoneNumber('');
    setError(null);
  };

  const handleSendSurvey = async () => {
    if (!selectedSurveyId || !patient || !phoneNumber) return;
    
    setSendingSurvey(true);
    setError(null);
    
    const actionStartTime = new Date();
    
    try {
      await patientApi.sendSurveyLink(selectedSurveyId, patient.patient_id, phoneNumber);
      
      // Log the activity (5 minutes default for sending survey)
      try {
        await patientApi.logAction({
          action: 'Form Submission - Survey Sent',
          action_begin_ts: actionStartTime.toISOString(),
          action_end_ts: new Date().toISOString(),
          action_user: user?.name || user?.email || 'Unknown User',
          patient_id: patient.patient_id,
          details: { survey_id: selectedSurveyId, phone_number: phoneNumber },
        });
        console.log('✅ Survey send activity logged');
      } catch (logErr) {
        console.warn('⚠️ Time tracking endpoint not yet implemented on backend');
        console.log('Activity not logged:', { action: 'Survey Sent', patient: patient.patient_id });
      }
      
      handleCloseSendSurveyDialog();
      // Show success message
      setSuccessMessage('Survey link sent successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to send survey link');
    } finally {
      setSendingSurvey(false);
    }
  };

  const handleShowSurveyLink = async (surveyId: string) => {
    if (!patient) return;
    
    setLoadingLinks(prev => ({ ...prev, [surveyId]: true }));
    setError(null);
    
    try {
      const response = await patientApi.createSurveyLink(surveyId, patient.patient_id, true);
      console.log('Survey link response:', response);
      
      // The response might be the link directly or have a 'link' property
      const link = typeof response === 'string' ? response : (response.link || response.url);

      if (link) {
        window.open(link, '_blank');
      } else {
        console.error('No link found in response:', response);
        setError('Failed to get survey link from response');
      }
    } catch (err: any) {
      console.error('Error creating survey link:', err);
      setError(err.message || 'Failed to create survey link');
    } finally {
      setLoadingLinks(prev => ({ ...prev, [surveyId]: false }));
    }
  };

  // VAPI AI Voice Call handlers
  const handleOpenVapiCallDialog = (surveyId: string, surveyName: string) => {
    setSelectedSurveyName(surveyName);
    setSelectedVapiSurveyId(surveyId);
    setVapiPhoneNumber(patient?.phone_number || '');
    setVapiCallError(null);
    setVapiCallDialogOpen(true);
  };

  const handleCloseVapiCallDialog = () => {
    setVapiCallDialogOpen(false);
    setSelectedSurveyName(null);
    setSelectedVapiSurveyId(null);
    setVapiPhoneNumber('');
    setError(null);
  };

  const handleStartVapiCall = async () => {
    if (!patient || !vapiPhoneNumber || !selectedVapiSurveyId) return;

    setCallingPatient(true);
    setError(null);
    setVapiCallError(null);

    try {
      const patientName = `${patient.first_name} ${patient.last_name}`;
      const organizationName = patient.organization_name;

      const response = await patientApi.startVapiCall(
        patient.patient_id,
        vapiPhoneNumber,
        patientName,
        organizationName,
        selectedSurveyName || 'Blood Pressure Check',
        selectedVapiSurveyId,
        patient.language
      );

      console.log('✅ VAPI call initiated:', response);

      handleCloseVapiCallDialog();
      // Show success message
      setError(response.status === 'mock_initiated'
        ? 'Demo: AI call initiated (VAPI not configured)'
        : 'AI voice call initiated successfully!');
      setTimeout(() => setError(null), 5000);
    } catch (err: any) {
      console.error('Error starting VAPI call:', err);
      setVapiCallError(err.message || 'Failed to start AI voice call');
    } finally {
      setCallingPatient(false);
    }
  };

  // Custom survey modal handlers
  const handleOpenCustomSurveyModal = () => {
    setEditingSurveyId(null);  // Ensure we're in create mode
    setCustomSurveyName('');
    setCustomSurveyQuestions([{ prompt: '', data_type: '' }]);
    setCustomSurveyModalOpen(true);
  };

  const handleAddQuestion = () => {
    setCustomSurveyQuestions(prev => [
      ...prev,
      { prompt: '', data_type: '' }
    ]);
  };

  const handleRemoveQuestion = (index: number) => {
    setCustomSurveyQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const handleQuestionChange = (index: number, field: string, value: string) => {
    setCustomSurveyQuestions(prev => prev.map((q, i) =>
      i === index ? { ...q, [field]: value } : q
    ));
  };

  const handleCreateCustomSurvey = async () => {
    if (!patient || !customSurveyName.trim() || customSurveyQuestions.length === 0) return;

    setCreatingSurvey(true);
    setError(null);

    try {
      await patientApi.createCustomSurvey(patient.patient_id, {
        name: customSurveyName,
        questions: customSurveyQuestions.filter(q => q.prompt.trim())
      });

      // Refresh patient data to show new survey
      const updatedPatient = await patientApi.get(patient.patient_id);
      if (onPatientUpdate) onPatientUpdate(updatedPatient);

      setCustomSurveyModalOpen(false);
      setSnackbarMessage('Custom survey created successfully!');
      setSnackbarSeverity('success');
    } catch (err: any) {
      setError(err.message || 'Failed to create custom survey');
    } finally {
      setCreatingSurvey(false);
    }
  };

  const handleEditCustomSurvey = (survey: Survey) => {
    setEditingSurveyId(survey.survey_id);
    setCustomSurveyName(survey.name);
    // Use questions from survey object if available
    if (survey.questions && survey.questions.length > 0) {
      setCustomSurveyQuestions(survey.questions);
    } else {
      setCustomSurveyQuestions([{ prompt: '', data_type: '' }]);
    }
    setCustomSurveyModalOpen(true);
  };

  const handleUpdateCustomSurvey = async () => {
    if (!patient || !editingSurveyId || !customSurveyName.trim()) return;

    setCreatingSurvey(true);
    setError(null);

    try {
      await patientApi.updateCustomSurvey(patient.patient_id, editingSurveyId, {
        name: customSurveyName,
        questions: customSurveyQuestions.filter(q => q.prompt.trim()).length > 0
          ? customSurveyQuestions.filter(q => q.prompt.trim())
          : undefined
      });

      const updatedPatient = await patientApi.get(patient.patient_id);
      if (onPatientUpdate) onPatientUpdate(updatedPatient);

      setCustomSurveyModalOpen(false);
      setEditingSurveyId(null);
      setSnackbarMessage('Custom survey updated successfully!');
      setSnackbarSeverity('success');
    } catch (err: any) {
      setError(err.message || 'Failed to update custom survey');
    } finally {
      setCreatingSurvey(false);
    }
  };

  const handleDeleteCustomSurvey = (surveyId: string) => {
    setDeletingSurveyId(surveyId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteCustomSurvey = async () => {
    if (!patient || !deletingSurveyId) return;

    try {
      await patientApi.deleteCustomSurvey(patient.patient_id, deletingSurveyId);

      const updatedPatient = await patientApi.get(patient.patient_id);
      if (onPatientUpdate) onPatientUpdate(updatedPatient);

      setSnackbarMessage('Custom survey deleted successfully!');
      setSnackbarSeverity('success');
    } catch (err: any) {
      setError(err.message || 'Failed to delete custom survey');
    } finally {
      setDeleteConfirmOpen(false);
      setDeletingSurveyId(null);
    }
  };

  // PHA-851: open the staged wizard to EDIT an existing Voice-of-the-Patient goal,
  // pre-filled with its GAS scale, tracking question, and its own barriers.
  const openVopGoalEditor = (g: any) => {
    const goalCount = Array.isArray(patient?.person_centered_goals)
      ? (patient!.person_centered_goals as any[]).length
      : 0;
    setEditingVopGoal(
      buildVopGoalEditTarget(
        g,
        Array.isArray(patient?.gas_definitions) ? (patient!.gas_definitions as any[]) : null,
        Array.isArray(patient?.pcm_barriers) ? (patient!.pcm_barriers as any[]) : null,
        goalCount,
      )
    );
    setVoiceWizardOpen(true);
  };

  // PHA-851: remove one VoP goal. The backend cascade-deletes its GAS def,
  // measurements (answers) and barriers; peer goals are untouched.
  const handleRemoveVopGoal = async (g: any) => {
    if (!patient) return;
    if (
      !window.confirm(
        `Remove the goal "${g.goal_text}"? Its tracking question and recorded answers will be deleted.`
      )
    ) {
      return;
    }
    setRemovingVopRef(g.pco_ref);
    try {
      await patientApi.removePcmGoal(patient.patient_id, g.pco_ref);
      const updated = await patientApi.get(patient.patient_id);
      if (onPatientUpdate) onPatientUpdate(updated);
      setSnackbarMessage('Goal removed.');
      setSnackbarSeverity('success');
    } catch (err: any) {
      setError(err?.message || 'Failed to remove goal');
    } finally {
      setRemovingVopRef(null);
    }
  };

  // Fetch voice call details (transcript, recording, etc.)
  const fetchVoiceCallDetails = async (voiceCallLogId: string) => {
    // Return cached details if already loaded
    if (voiceCallDetails[voiceCallLogId]) {
      return voiceCallDetails[voiceCallLogId];
    }

    setLoadingVoiceCallDetails(prev => ({ ...prev, [voiceCallLogId]: true }));

    try {
      const response = await apiRequest(`/voice-calls/${voiceCallLogId}`, {
        method: 'GET',
      });
      const data = await response.json();

      setVoiceCallDetails(prev => ({ ...prev, [voiceCallLogId]: data }));
      return data;
    } catch (err: any) {
      console.error('Error fetching voice call details:', err);
      return null;
    } finally {
      setLoadingVoiceCallDetails(prev => ({ ...prev, [voiceCallLogId]: false }));
    }
  };

  // Open voice call details modal
  const handleOpenVoiceCallModal = async (voiceCallLogId: string | null) => {
    setSelectedVoiceCallLogId(voiceCallLogId);
    setVoiceCallModalOpen(true);
    if (voiceCallLogId && voiceCallLogId !== NO_VOICE_CALL_DATA) {
      await fetchVoiceCallDetails(voiceCallLogId);
    }
  };

  const handleCloseVoiceCallModal = () => {
    setVoiceCallModalOpen(false);
    setSelectedVoiceCallLogId(null);
  };

  // PHA-1234: the collapsed transcript + recording section, shared by the Surveys tab
  // and the Event Log. Nothing when the event has no call to show.
  const renderVoiceCallTranscript = (voiceCallLogId?: string | null, sx?: SxProps<MuiTheme>) =>
    voiceCallLogId ? (
      <VoiceCallTranscriptAccordion
        voiceCallLogId={voiceCallLogId}
        details={voiceCallDetails[voiceCallLogId]}
        loading={loadingVoiceCallDetails[voiceCallLogId]}
        onExpand={fetchVoiceCallDetails}
        sx={sx}
      />
    ) : null;

  // Goal Achievement toggle handler with optimistic updates
  const handleGoalAchievementToggle = async (
    carePlanDefinition: CarePlanDefinition,
    goal: { measure: string; test: string; due_date: string; target_value: string; achieved?: boolean; achieved_date?: string },
    achieved: boolean
  ) => {
    if (!patient) return;
    
    const updateKey = `goal-${goal.measure}`;
    
    // Prevent double-clicking while update is in progress
    if (pendingUpdates.has(updateKey)) return;
    
    // Optimistic update
    const previousDefinitions = [...carePlanDefinitions];
    setPendingUpdates(prev => new Set(prev).add(updateKey));
    
    setCarePlanDefinitions(prev => prev.map(cpd => {
      if (cpd.disease !== carePlanDefinition.disease || cpd.uploaded_ts !== carePlanDefinition.uploaded_ts) {
        return cpd;
      }
      return {
        ...cpd,
        goals: cpd.goals?.map(g => 
          g.measure === goal.measure 
            ? { ...g, achieved, achieved_date: achieved ? new Date().toISOString() : undefined }
            : g
        )
      };
    }));
    
    try {
      // Backend expects date-only format (YYYY-MM-DD), not full ISO datetime
      const today = new Date().toISOString().split('T')[0];
      
      await patientApi.persistGoalAchievement({
        patient_id: patient.patient_id,
        disease: carePlanDefinition.disease,
        uploaded_ts: carePlanDefinition.uploaded_ts,
        measure: goal.measure,
        achieved,
        achieved_date: achieved ? today : '',
        attesting_user: user?.name || user?.email || null,
      });
      
      setSnackbarMessage(achieved ? 'Goal marked as achieved' : 'Goal marked as not achieved');
      setSnackbarSeverity('success');
    } catch (err: any) {
      console.error('Error updating goal achievement:', err);
      // Revert optimistic update on error
      setCarePlanDefinitions(previousDefinitions);
      setSnackbarMessage(err.message || 'Failed to update goal');
      setSnackbarSeverity('error');
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(updateKey);
        return next;
      });
    }
  };

  // Task Completion toggle handler with optimistic updates
  const handleTaskCompletionToggle = async (
    carePlanDefinition: CarePlanDefinition,
    task: { task: string; assignee: string; frequency: string; due_date?: string; completed?: boolean; completed_at?: string | null; occurrence_id?: number },
    completed: boolean
  ) => {
    if (!patient) return;
    
    const updateKey = `task-${task.occurrence_id}`;
    
    // Prevent double-clicking while update is in progress
    if (pendingUpdates.has(updateKey)) return;
    
    // Check if task has occurrence_id (required for backend)
    if (!task.occurrence_id) {
      setSnackbarMessage('Task cannot be updated: missing occurrence ID');
      setSnackbarSeverity('error');
      return;
    }
    
    // Optimistic update
    const previousDefinitions = [...carePlanDefinitions];
    setPendingUpdates(prev => new Set(prev).add(updateKey));
    
    setCarePlanDefinitions(prev => prev.map(cpd => {
      if (cpd.disease !== carePlanDefinition.disease || cpd.uploaded_ts !== carePlanDefinition.uploaded_ts) {
        return cpd;
      }
      return {
        ...cpd,
        tasks: cpd.tasks?.map(t => 
          t.occurrence_id === task.occurrence_id 
            ? { ...t, completed, completed_at: completed ? new Date().toISOString() : undefined }
            : t
        )
      };
    }));
    
    try {
      await patientApi.persistTaskCompletion({
        patient_id: patient.patient_id,
        disease: carePlanDefinition.disease,
        uploaded_ts: carePlanDefinition.uploaded_ts,
        task: task.task,
        normal: true, // Assuming normal completion
        occurrence_id: task.occurrence_id,
        completed_at: completed ? new Date().toISOString() : null,
        completion_user: user?.name || user?.email || null,
      });
      
      setSnackbarMessage(completed ? 'Task marked as completed' : 'Task marked as not completed');
      setSnackbarSeverity('success');
    } catch (err: any) {
      console.error('Error updating task completion:', err);
      // Revert optimistic update on error
      setCarePlanDefinitions(previousDefinitions);
      setSnackbarMessage(err.message || 'Failed to update task');
      setSnackbarSeverity('error');
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(updateKey);
        return next;
      });
    }
  };

  // PHA-397: Load pending EHR-refresh draft care plans for the current patient
  const loadPendingDraftCarePlans = async () => {
    try {
      // ehrOnly=true: the banner must only reflect drafts from the daily EHR refresh,
      // not manual "Regenerate" / onboarding drafts (which leave trigger_source NULL).
      const candidates = await patientApi.getCarePlanCandidates(patient!.patient_id, true);
      if (candidates && candidates.length > 0) {
        setPendingDraftCarePlans({ care_plan_definitions: candidates, surveys: [] });
        setDraftPlanGenerated(true);
      } else {
        setPendingDraftCarePlans(null);
        setDraftPlanGenerated(false);
      }
    } catch (err) {
      // PHA-951: a failed fetch is not the same as "no pending updates". Leave whatever the
      // banner already showed in place rather than clearing it on a transient blip — an
      // absent banner is recoverable, but flipping a real one off is misinformation.
      console.error('PHA-951: pending EHR-refresh draft fetch failed; keeping previous banner state:', err);
    }
  };

  // PHA-895 follow-up: load persisted drafts (ehrOnly=false, so onboarding/manual drafts with
  // trigger_source NULL are included — same call the onboarding resume flow uses) for the
  // read-only chart view + draft PDF. Only meaningful when there's no approved plan.
  const loadDraftCarePlansForView = async () => {
    setDraftCarePlanLoadState('loading');
    try {
      const candidates = await patientApi.getCarePlanCandidates(patient!.patient_id, false);
      setDraftCarePlansForView(Array.isArray(candidates) ? candidates : []);
      setDraftCarePlanLoadState('loaded');
    } catch (err) {
      // PHA-951: do NOT collapse a failed fetch into []. The BFF deliberately refuses to
      // fabricate an empty candidates list (PHA-797, server.js) precisely so the clinician is
      // never shown "no care plan" for a patient who has one; swallowing the error here undid
      // that. Keep any previously loaded draft on screen and let the tab render an error.
      console.error('PHA-951: draft care plan fetch failed:', err);
      setDraftCarePlanLoadState('error');
    }
    // PHA-1285: best effort, and outside the try so it can never flip the tab to 'error'.
    // Without it the line reads "not set", as it did before.
    const patientId = patient!.patient_id;
    try {
      const meta = await patientApi.getCarePlanDraftMeta(patientId);
      if (patientIdRef.current === patientId) setDraftReviewDateMasked(meta.expiration_date);
    } catch (err) {
      console.error('PHA-1285: draft review date fetch failed:', err);
    }
  };

  // PHA-1050: is there a clinician-started regeneration in flight? Loaded for EVERY
  // patient, unlike loadDraftCarePlansForView which only runs when there is no approved
  // plan — the whole point here is the patient who HAS an approved plan and a draft on
  // top of it. manualOnly excludes the nightly ehr_cron review queue, which is a
  // different surface and must not rename the button.
  const loadManualDraftState = async (patientId: string) => {
    try {
      const candidates = await patientApi.getCarePlanCandidates(patientId, false, true);
      setHasManualDraftCarePlan(Array.isArray(candidates) && candidates.length > 0);
    } catch (err) {
      // PHA-951: a failed fetch is not "no draft". Leave the flag as-is (null on first
      // load) so the CTA falls back to plain "Regenerate Care Plan" rather than claiming
      // a regeneration is in progress, or hiding the button from a draft-only patient.
      console.error('PHA-1050: manual draft state fetch failed:', err);
    }
  };

  // PHA-726: acknowledgment history for the Care Plans tab.
  const loadCarePlanAcknowledgments = async () => {
    if (!patient?.patient_id) return;
    try {
      const result = await patientApi.listCarePlanAcknowledgments(patient.patient_id);
      setCarePlanAcknowledgments(
        Array.isArray(result?.acknowledgments) ? result.acknowledgments : []
      );
    } catch (err) {
      // PHA-951: keep whatever was already on screen rather than blanking it — an empty
      // list reads as "never reviewed with the patient", which is a clinical claim.
      console.error('PHA-726: acknowledgment fetch failed:', err);
    }
  };

  // PHA-870: Load the patient's care-plan review records (newest first).
  const loadCarePlanReviews = async () => {
    if (!patient?.patient_id) return;
    try {
      const result = await patientApi.getCarePlanReviews(patient.patient_id);
      setCarePlanReviews(Array.isArray(result?.reviews) ? result.reviews : []);
    } catch (err) {
      // PHA-951: keep previously loaded reviews rather than blanking them on a transient
      // failure — these ride into the care-plan PDF, so an empty array is a silent omission.
      console.error('PHA-951: care plan review list fetch failed; keeping previous reviews:', err);
    }
  };

  const handleOpenReviewModal = () => {
    // Add mode: default the review date to today (a review records a past/present event).
    setEditingReviewId(null);
    setReviewDateMasked(isoToMaskedDate(new Date().toISOString().slice(0, 10)));
    setReviewPatientComments('');
    setReviewActionTaken('');
    setReviewModalOpen(true);
  };

  const handleOpenEditReviewModal = (review: CarePlanReview) => {
    setEditingReviewId(review.id);
    setReviewDateMasked(review.review_date ? isoToMaskedDate(review.review_date) : '');
    setReviewPatientComments(review.patient_comments ?? '');
    setReviewActionTaken(review.action_taken ?? '');
    setReviewModalOpen(true);
  };

  const handleSaveCarePlanReview = async () => {
    if (!patient?.patient_id) return;
    const reviewDateIso = maskedDateToIso(reviewDateMasked);
    if (!reviewDateIso) {
      setSnackbarMessage('Please enter a valid review date (MM/DD/YYYY).');
      setSnackbarSeverity('error');
      return;
    }
    setSavingReview(true);
    try {
      const result =
        editingReviewId != null
          ? await patientApi.updateCarePlanReview(
              patient.patient_id,
              editingReviewId,
              reviewDateIso,
              reviewPatientComments.trim(),
              reviewActionTaken.trim(),
            )
          : await patientApi.saveCarePlanReview(
              patient.patient_id,
              reviewDateIso,
              reviewPatientComments.trim(),
              reviewActionTaken.trim(),
            );
      if (result?.error) {
        setSnackbarMessage(String(result.error));
        setSnackbarSeverity('error');
        return;
      }
      setReviewModalOpen(false);
      await loadCarePlanReviews();
      setSnackbarMessage(editingReviewId != null ? 'Care plan note updated.' : 'Care plan note saved.');
      setSnackbarSeverity('success');
    } catch {
      setSnackbarMessage('Failed to save care plan note.');
      setSnackbarSeverity('error');
    } finally {
      setSavingReview(false);
    }
  };

  const handleConfirmDeleteReview = async () => {
    if (!patient?.patient_id || !deletingReview) return;
    setDeletingReviewBusy(true);
    try {
      const result = await patientApi.deleteCarePlanReview(patient.patient_id, deletingReview.id);
      if (result?.error) {
        setSnackbarMessage(String(result.error));
        setSnackbarSeverity('error');
        return;
      }
      setDeletingReview(null);
      await loadCarePlanReviews();
      setSnackbarMessage('Care plan note deleted.');
      setSnackbarSeverity('success');
    } catch {
      setSnackbarMessage('Failed to delete care plan note.');
      setSnackbarSeverity('error');
    } finally {
      setDeletingReviewBusy(false);
    }
  };

  // PHA-397: Called after CarePlanApproval successfully approves the draft
  const handleDraftCarePlanApproval = async (_surveys: any[], _consentGiven: boolean) => {
    setShowDraftReviewModal(false);
    setPendingDraftCarePlans(null);
    setDraftPlanGenerated(false);
    // PHA-967: land on the Care Plans tab so the just-approved plan is on screen. The modal can
    // be opened from the header (any tab), so this is not always a no-op.
    setCurrentTab(PATIENT_DETAIL_TAB_INDEX.CARE_PLANS);
    // PHA-1204: approval may have just uploaded the new plan to the EHR automatically, so
    // re-read the push status — otherwise the caption keeps describing the previous plan.
    patientApi.getCarePlanEhrPushStatus(patient!.patient_id)
      .then(setCarePlanPushStatus)
      .catch(() => { /* cosmetic; the next chart load answers it */ });
    // Reload approved care plans
    try {
      const updatedPatient = await patientApi.get(patient!.patient_id);
      if (updatedPatient?.care_plan_definitions) {
        setCarePlanDefinitions(updatedPatient.care_plan_definitions);
      }
    } catch (err) {
      // PHA-967: the whole point of this step is "see the plan you just approved", so a failed
      // reload leaving the old plan on screen must not be silent — it reads as the approval
      // having done nothing.
      console.error('PHA-967: failed to reload care plans after approval:', err);
      setSnackbarMessage('Care plan approved, but the chart could not be refreshed. Reload to see it.');
      setSnackbarSeverity('error');
    }
  };

  // PHA-397: Generates a new draft care plan (forwarded to CarePlanApproval)
  const generateCarePlanDraft = async (selectedDiseases?: string[]): Promise<CarePlanResponse> => {
    const response = await patientApi.generateCarePlan(patient!.patient_id, selectedDiseases);
    setPendingDraftCarePlans(response);
    setDraftPlanGenerated(true);
    return response;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'warning';
      case 'controlled': return 'success';
      case 'completed': return 'success';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    // PHA-866: guard against null/empty — `new Date(null)` is the Unix epoch,
    // which used to render a bogus "Jan 01, 1970" for date-less goals/tasks.
    if (!dateString) return 'N/A';
    try {
      // For date-only strings (YYYY-MM-DD), parse components directly
      // to avoid timezone conversion issues
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return format(date, 'MMM dd, yyyy');
      }
      // For other date formats, use standard parsing
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatAnswerDisplay = (value: string): string => {
    // Detect date answers: "YYYY-MM-DD 00:00:00±HH:MM" or "YYYY-MM-DDT00:00:00±HH:MM"
    const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})[\sT]00:00:00/);
    if (dateMatch) {
      return formatDate(dateMatch[1]);
    }
    // Also handle bare "YYYY-MM-DD" strings
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return formatDate(value);
    }
    return value;
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // Display in user's local timezone with explicit timezone offset
      return format(date, 'MMM dd, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  // Format duration in seconds to human-readable string.
  // PHA-882: show fractional minutes (1 dp) for multi-minute events so the
  // event log reconciles with the Detailed and Billing reports, which display
  // decimal minutes (toFixed(1)). The previous Math.floor(seconds / 60) dropped
  // up to 59s per event — a 1.9-min dwell rendered as "1 min", a 2.5-min dwell
  // as "2 min" — so the summed event log read lower than the reports and broke
  // billing-audit reconciliation. Sub-minute events keep exact seconds (they
  // already reconcile) to preserve at-a-glance readability.
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} sec`;
    }
    const totalMinutes = seconds / 60;
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes - hours * 60;
      return remainingMinutes >= 0.05
        ? `${hours} hr ${remainingMinutes.toFixed(1)} min`
        : `${hours} hr`;
    }
    return `${totalMinutes.toFixed(1)} min`;
  };

  // Helper function to normalize action display names for audit compliance.
  // PHA-891 (addi, 2026-09-01): actionReportData is now the UNMERGED per-event
  // report — one bare action name per row, never a collapsed ', '-joined list —
  // so this is just the legacy-label mapping, no count-suffix stripping needed.
  const getDisplayActionName = (action: string): string =>
    action === 'dwell in patient record' ? 'Active Care Management Time' : action;

  // Merge and normalize action report entries with contact actions for unified event log
  const getMergedEventLog = (): ContactAction[] => {
    const events: ContactAction[] = [];

    // Add existing contact actions (surveys, SMS, etc.)
    if (patient?.contact_actions) {
      events.push(...patient.contact_actions);
    }

    // Add action report entries (manual and auto-tracked time). PHA-891
    // (addi, 2026-09-01): one row per REAL event with its own real timestamp —
    // a manual phone call is its own entry, each auto-tracked segment is its
    // own entry, each Smart Hub Acknowledgement is its own entry. No merging.
    if (actionReportData && actionReportData.length > 0) {
      const autoTrackedNames = [
        'Active Care Management Time',
        'dwell in patient record',
        'Smart Hub Acknowledgement',
      ];
      const timeTrackingEvents: ContactAction[] = actionReportData.map((record: any) => {
        const beginTs = record['Begin timestamp'];
        const action = record['Action'];
        // PHA-800: guard against missing/non-numeric duration so the UI never renders
        // "NaN min" (Number(undefined) -> NaN). Only use a finite value; otherwise leave
        // duration undefined so the Duration line is omitted entirely.
        const spentMinutesNum = Number(record['spent (minutes)']);
        const durationSeconds = Number.isFinite(spentMinutesNum)
          ? Math.round(spentMinutesNum * 60)
          : undefined;
        const notes = record['Notes'];

        // A single real event is unambiguously one or the other — no more
        // "every part must agree" logic needed now that rows never merge.
        const isAutoTracked = autoTrackedNames.includes(action);

        return {
          name: getDisplayActionName(action),
          method: isAutoTracked ? 'Auto-tracked' : 'Manual entry',
          created_at: beginTs,
          type: 'time_tracking' as const,
          duration_seconds: durationSeconds,
          notes: notes || undefined,
          // PHA-1149: who performed it (display name resolved server-side).
          user_name: record['User name'] || undefined,
          // Add a flag to distinguish manual vs auto-tracked
          isAutoTracked: isAutoTracked,
        } as ContactAction & { isAutoTracked?: boolean };
      });

      events.push(...timeTrackingEvents);
    }

    return events;
  };
  
  // Filter events based on selected filter
  const getFilteredEventLog = (): ContactAction[] => {
    const allEvents = getMergedEventLog();
    
    switch (eventLogFilter) {
      case 'manual':
        return allEvents.filter(e => 
          e.type === 'time_tracking' && !(e as any).isAutoTracked
        );
      case 'auto':
        return allEvents.filter(e => 
          e.type === 'time_tracking' && (e as any).isAutoTracked
        );
      case 'surveys':
        return allEvents.filter(e => 
          e.type === 'survey sent' || e.type === 'survey failed' || e.type === 'answers submitted' || e.type === 'survey deleted' || e.type === 'voice call'
        );
      case 'all':
      default:
        return allEvents;
    }
  };

  interface SurveyEventGroup {
    kind: 'survey_group';
    groupKey: string;
    surveyName: string;
    surveyId: string;
    sentEvent?: ContactAction;
    submissions: ContactAction[];
    hasResponse: boolean;
    latestTimestamp: string;
  }

  type EventLogItem = { kind: 'standalone'; event: ContactAction } | SurveyEventGroup;

  const getGroupedEventLog = (): EventLogItem[] => {
    const filtered = getFilteredEventLog();

    // Separate groupable survey events from standalone events
    const standaloneEvents: ContactAction[] = [];
    const groupableEvents: ContactAction[] = [];

    for (const event of filtered) {
      const isGroupable =
        event.survey_id &&
        (event.type === 'survey sent' || event.type === 'answers submitted');
      if (isGroupable) {
        groupableEvents.push(event);
      } else {
        standaloneEvents.push(event);
      }
    }

    // Sort groupable events chronologically (oldest first) to build temporal groups
    groupableEvents.sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    // Group by survey_id: each "survey sent" starts a new group,
    // subsequent "answers submitted" (same survey_id) belong to it
    const groupsBySurveyId = new Map<string, SurveyEventGroup[]>();

    for (const event of groupableEvents) {
      const sid = event.survey_id!;
      if (!groupsBySurveyId.has(sid)) {
        groupsBySurveyId.set(sid, []);
      }
      const groups = groupsBySurveyId.get(sid)!;

      if (event.type === 'survey sent') {
        // Start a new group for this sent event
        groups.push({
          kind: 'survey_group',
          groupKey: `${sid}-${event.created_at}`,
          surveyName: event.name,
          surveyId: sid,
          sentEvent: event,
          submissions: [],
          hasResponse: false,
          latestTimestamp: event.created_at,
        });
      } else if (event.type === 'answers submitted') {
        // Attach to the most recent group for this survey_id
        if (groups.length === 0) {
          // Submission without a prior sent event — create an orphan group
          groups.push({
            kind: 'survey_group',
            groupKey: `${sid}-orphan-${event.created_at}`,
            surveyName: event.name,
            surveyId: sid,
            sentEvent: undefined,
            submissions: [],
            hasResponse: true,
            latestTimestamp: event.created_at,
          });
        }
        const currentGroup = groups[groups.length - 1];
        currentGroup.submissions.push(event);
        currentGroup.hasResponse = true;
        if (new Date(event.created_at) > new Date(currentGroup.latestTimestamp)) {
          currentGroup.latestTimestamp = event.created_at;
        }
      }
    }

    // Collect all groups
    const allGroups: SurveyEventGroup[] = [];
    groupsBySurveyId.forEach((groups) => allGroups.push(...groups));

    // Combine into a single list: standalone items + grouped items
    const items: EventLogItem[] = [
      ...standaloneEvents.map((e) => ({ kind: 'standalone' as const, event: e })),
      ...allGroups,
    ];

    // Sort by latest timestamp descending
    items.sort((a, b) => {
      const tsA = a.kind === 'standalone' ? a.event.created_at : a.latestTimestamp;
      const tsB = b.kind === 'standalone' ? b.event.created_at : b.latestTimestamp;
      return new Date(tsB).getTime() - new Date(tsA).getTime();
    });

    return items;
  };

  // Check if an item is overdue
  const isOverdue = (dueDate: string): boolean => {
    if (!dueDate) return false;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Parse date-only strings locally (same as formatDate) to avoid UTC midnight
      // shifting calendar dates back one day in US timezones (PHA-772).
      const dateMatch = dueDate.match(/^(\d{4}-\d{2}-\d{2})/);
      let due: Date;
      if (dateMatch) {
        const [year, month, day] = dateMatch[1].split('-').map(Number);
        due = new Date(year, month - 1, day);
      } else {
        due = new Date(dueDate);
        due.setHours(0, 0, 0, 0);
      }
      return due < today;
    } catch {
      return false;
    }
  };

  // PHA-1107: isNumericAnswerSet / isBloodPressureAnswerSet now live at module
  // scope (next to DEVICE_READING_CONFIG) so the device-reading merge, which
  // runs in a useMemo declared above this point, can reuse them without hitting
  // the temporal dead zone. Call sites below are unchanged.

  // Prepare chart data for numeric answer sets
  const prepareChartData = (answerSet: any) => {
    const entries = Object.entries(answerSet)
      .filter(([key]) => key !== 'question')
      .filter(([date]) => !isNaN(new Date(date).getTime()))
      .map(([date, answerData]) => {
        const dateObj = new Date(date);
        const value = getAnswerValue(answerData);
        const rawData = getRawData(answerData);
        const numValue = value !== null && value !== undefined ? Number(value) : null;
        return {
          date: dateObj,
          dateStr: date,
          value: numValue,
          hasValue: numValue !== null,
          displayDate: format(dateObj, 'MMM dd, yyyy h:mm a'),
          rawData: rawData, // Include raw_data for tooltip
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // Add placeholder Y value for missing data points (invisible hover target)
    return entries.map(e => ({
      ...e,
      placeholderY: 0,
    }));
  };

  // Prepare chart data for blood pressure readings
  const prepareBloodPressureChartData = (answerSet: any) => {
    const entries = Object.entries(answerSet)
      .filter(([key]) => key !== 'question')
      .filter(([date]) => !isNaN(new Date(date).getTime()))
      .map(([date, answerData]) => {
        const dateObj = new Date(date);
        const value = getAnswerValue(answerData);
        const rawData = getRawData(answerData);
        const dataPoint: any = {
          date: dateObj,
          dateStr: date,
          // Format date in user's local timezone
          displayDate: format(dateObj, 'MMM dd, yyyy h:mm a'),
          rawData: rawData, // Include raw_data for tooltip
        };

        // Parse and validate the blood pressure value
        const parsedBP = parseBloodPressure(value);
        dataPoint.systolic = parsedBP.systolic || null;
        dataPoint.diastolic = parsedBP.diastolic || null;
        dataPoint.isValid = parsedBP.isValid;
        dataPoint.isInvalid = parsedBP.isInvalid;
        dataPoint.rawInput = parsedBP.rawInput;
        dataPoint.validationError = parsedBP.validationError;

        return dataPoint;
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return entries;
  };

  const getXAxisPadding = (dataPointCount: number): { left: number; right: number } => {
    if (dataPointCount <= 1) return { left: 200, right: 200 };
    if (dataPointCount === 2) return { left: 300, right: 300 };
    if (dataPointCount === 3) return { left: 200, right: 200 };
    if (dataPointCount === 4) return { left: 120, right: 120 };
    if (dataPointCount === 5) return { left: 60, right: 60 };
    return { left: 10, right: 10 };
  };

  // Format answer for display
  const formatAnswer = (answer: any): string => {
    if (answer === null || answer === undefined) {
      return 'No response';
    }

    if (typeof answer === 'boolean') {
      return answer ? 'Yes' : 'No';
    }

    if (typeof answer === 'object' && answer !== null) {
      // Check if it's a blood pressure reading
      if ('systolic' in answer || 'diastolic' in answer) {
        const parts = [];
        if (answer.systolic !== undefined && answer.systolic !== null) {
          parts.push(`${answer.systolic}`);
        }
        if (answer.diastolic !== undefined && answer.diastolic !== null) {
          parts.push(`${answer.diastolic}`);
        }
        return parts.length > 0 ? parts.join('/') : 'Invalid blood pressure';
      }
      // PHA-992: an unfilled form.io container submits {} (and older rows stored it verbatim),
      // which used to render to clinicians as a literal "{}". An empty object is the absence of
      // an answer, not an answer.
      const hasContent = Object.values(answer).some(v => v !== null && v !== undefined && v !== '');
      if (!hasContent) {
        return 'No response';
      }
      // PHA-992: form.io resolved a component key containing '.' as a lodash path, so an
      // answer to a question whose prompt had a period was stored nested under the rest of
      // that prompt: {" Please reply 'Yes' to consent...": true}. Key generation is fixed,
      // but rows collected before the fix keep the nested shape. Unwrap them so a consent
      // that WAS given reads "Yes" rather than "[object Object]" — reading it as
      // "No response" would be worse than the bug. Recurses for a doubly-nested prompt
      // ("(e.g., ...)" splits twice).
      const keys = Object.keys(answer);
      if (keys.length === 1) {
        return formatAnswer(answer[keys[0]]);
      }
      // For other objects, try to stringify them nicely
      return JSON.stringify(answer);
    }

    return String(answer);
  };

  // Check if a voice call answer was conditionally skipped (question was never asked)
  const isConditionallySkipped = (answer: any): boolean => {
    if (answer.raw_data?.source !== 'voice_call') return false;
    const val = answer.cast_answer ?? answer.raw_answer;
    return val === 'skipped' || val === 'no_response' || answer.raw_data?.skipped === true;
  };

  // PHA-1107: getAnswerValue moved to module scope alongside the answer-shape
  // predicates that depend on it. Call sites below are unchanged.

  // Extract raw_data from answer
  const getRawData = (answerData: any): any => {
    if (answerData && typeof answerData === 'object' && 'raw_data' in answerData) {
      return answerData.raw_data;
    }
    return null;
  };

  // Hospitalization answer constants
  const HOSPITALIZATION_QUESTION = 'In the last 7 days, have you been in a hospital, emergency room, or stayed overnight in another medical facility?';

  const HOSPITALIZATION_REASON_QUESTION = 'What was the visit for?';

  const HOSPITALIZATION_ANSWER_LABELS: Record<string, string> = {
    yes: 'Yes',
    yes_hospital: 'Yes',
    yes_emergency_room: 'Yes',
    yes_urgent_care: 'Yes',
    yes_other_facility: 'Yes',
    no: 'No',
    false: 'No',
    true: 'Yes',
  };

  const ENCOUNTER_TYPE_FROM_ANSWER: Record<string, string> = {
    yes_hospital: 'hospital',
    yes_emergency_room: 'emergency_room',
    yes_urgent_care: 'urgent_care',
    yes_other_facility: 'other_facility',
  };

  // PHA-993: the encounter-type keys are only the LEGACY multi-choice shape. The
  // Yes/No shape (and every voice call) answers a plain "yes", and the
  // options-less render coerces to "true" (PHA-857) — leaving those out ranked a
  // reported hospitalization as if the patient had said no. The backend's
  // authoritative version of this set lives in backend/app_server/hospitalization.py.
  const POSITIVE_HOSPITALIZATION_ANSWERS = new Set([
    ...Object.keys(ENCOUNTER_TYPE_FROM_ANSWER),
    'yes',
    'true',
  ]);

  const ENCOUNTER_TYPE_LABELS: Record<string, string> = {
    hospital: 'Hospital',
    emergency_room: 'Emergency Room',
    urgent_care: 'Urgent Care',
    other_facility: 'Other Facility',
  };

  const ENCOUNTER_TYPE_CHIP_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
    hospital: 'error',
    emergency_room: 'warning',
    urgent_care: 'default',
    other_facility: 'default',
  };

  const findMatchingCareTransitionEvent = (answerDateKey: string, events: any[]) => {
    if (!events || events.length === 0) return null;
    const answerTime = new Date(answerDateKey).getTime();
    if (isNaN(answerTime)) return null;

    let closestEvent = null;
    let closestDiff = Infinity;
    const FIVE_MINUTES = 5 * 60 * 1000;

    for (const event of events) {
      const eventTime = new Date(event.created_at).getTime();
      if (isNaN(eventTime)) continue;
      const diff = Math.abs(answerTime - eventTime);
      if (diff <= FIVE_MINUTES && diff < closestDiff) {
        closestDiff = diff;
        closestEvent = event;
      }
    }
    return closestEvent;
  };

  // Get collection method icon
  const getCollectionMethodIcon = (rawData: any) => {
    if (!rawData) {
      // No raw_data at all - likely SMS/web form (default)
      return (
        <Sms
          sx={{
            fontSize: 16,
            color: '#2196f3',
            ml: 0.5,
            verticalAlign: 'middle'
          }}
          titleAccess="SMS"
        />
      );
    }

    if (rawData.source === 'voice_call') {
      return (
        <PhoneInTalk
          sx={{
            fontSize: 16,
            color: '#9c27b0',
            ml: 0.5,
            verticalAlign: 'middle'
          }}
          titleAccess="Voice call"
        />
      );
    }

    if (rawData.source === 'staff_entry') {
      return (
        <SupportAgent
          sx={{
            fontSize: 16,
            color: '#6d4c41',
            ml: 0.5,
            verticalAlign: 'middle'
          }}
          titleAccess="Entered by care team"
        />
      );
    }

    // If raw_data exists but no source field, it's SMS/web form (has form data directly)
    if (!rawData.source) {
      return (
        <Sms
          sx={{
            fontSize: 16,
            color: '#2196f3',
            ml: 0.5,
            verticalAlign: 'middle'
          }}
          titleAccess="SMS"
        />
      );
    }

    return null;
  };

  if (!patient) return null;

  // PHA-895 follow-up: when no approved care plan exists, surface the persisted draft
  // read-only in the Care Plans tab and drive the Download PDF from it (so the draft-state
  // PDF matches the post-approval one). An approved plan always takes precedence.
  const hasApprovedCarePlans = carePlanDefinitions.length > 0;
  const showDraftCarePlans = !hasApprovedCarePlans && draftCarePlansForView.length > 0;
  const carePlansToRender = hasApprovedCarePlans ? carePlanDefinitions : draftCarePlansForView;
  // PHA-951: only the draft path is fetched asynchronously, so these are scoped to the
  // no-approved-plan case. Both are false once a draft loads, so a real plan still wins.
  const draftCarePlanLoadFailed = !hasApprovedCarePlans && draftCarePlanLoadState === 'error';
  const draftCarePlanLoadPending =
    !hasApprovedCarePlans && (draftCarePlanLoadState === 'idle' || draftCarePlanLoadState === 'loading');

  // PHA-983: one payload builder shared by "Download Care Plan" and "Push Care Plan to EMR", so the
  // copy filed into the medical record can never drift from the copy clinicians print.
  const resolveCarePlanPdfPayload = async (isEhrCopy: boolean) => {
    // PHA-1290: Care Plan Notes are fetched on click, like the draft below. carePlanReviews only
    // loads when the Care Plans tab opens, so printing from any other tab dropped the notes.
    const carePlanReviewsPromise = fetchCarePlanReviewsForPdf(patient.patient_id, carePlanReviews);
    // PHA-895 follow-up: the draft is only preloaded on the Care Plans tab, but these buttons
    // live in the header (available on every tab). For a draft-only patient acted on from
    // another tab, draftCarePlansForView is empty, so fetch the draft on demand — otherwise
    // the PDF renders "No care plans defined".
    let draftDefs = draftCarePlansForView;
    let draftReviewDate = draftReviewDateMasked;
    if (!hasApprovedCarePlans && !draftReviewDate) {
      try {
        draftReviewDate = (await patientApi.getCarePlanDraftMeta(patient.patient_id)).expiration_date;
      } catch (err) {
        console.error('PHA-1285: on-demand draft review date fetch for PDF failed:', err);
      }
    }
    if (!hasApprovedCarePlans && draftDefs.length === 0) {
      try {
        const candidates = await patientApi.getCarePlanCandidates(patient.patient_id, false);
        if (Array.isArray(candidates) && candidates.length > 0) {
          draftDefs = candidates;
          setDraftCarePlansForView(candidates);
        }
      } catch (err) {
        console.error('PHA-895: on-demand draft candidates fetch for PDF failed:', err);
      }
    }
    return buildCarePlanPdfPayload({
      patient,
      carePlanDefinitions,
      draftCarePlanDefinitions: draftDefs,
      hasApprovedCarePlans,
      voiceOfPatientEnabled,
      noTargetDatesEnabled,
      carePlanReviews: await carePlanReviewsPromise,
      isEhrCopy,
      // PHA-1080: only reached for a DRAFT (an approved plan carries its stored date), where
      // the projected review date must match what approval will store for this clinic.
      carePlanReviewMonths,
      // PHA-1285: the clinician's date for this draft, ahead of the clinic default.
      draftReviewDate: draftReviewDate ? maskedDateToIso(draftReviewDate) : null,
      // PHA-1032: shown as "Generated and reviewed by" only when this render turns out to be a draft.
      draftReviewerName: user?.name || user?.email || null,
    });
  };

  const handlePushCarePlanToEhr = async (force: boolean) => {
    setPushingToEhr(true);
    try {
      // is_ehr_copy suppresses the PHA-942 "printed and handed" note — see the payload helper.
      const payload = await resolveCarePlanPdfPayload(true);
      const { blob, filename } = await generateCarePlanPdfBlob(payload);
      const result = await patientApi.pushCarePlanToEhr(patient.patient_id, blob, filename, force);
      // PHA-1213: Cobalt has only ACCEPTED the upload; the EHR can still reject it (unknown
      // MRN, missing folder) and says so by webhook within about a minute. Wait for that
      // verdict in a modal rather than reporting success now. The chart's Pending poller
      // feeds the modal, so it needs the fresh status that shows this push as Pending.
      if (result?.push_id) {
        setEhrPushWait({ pushId: String(result.push_id), timedOut: false });
      } else {
        setSnackbarMessage(EHR_PUSH_SUBMITTED_DETAIL);
        setSnackbarSeverity('success');
      }
      try {
        setCarePlanPushStatus(await patientApi.getCarePlanEhrPushStatus(patient.patient_id));
      } catch {
        // The poller cannot start without it; the modal times out to the Pending message.
      }
    } catch (err: any) {
      if (err?.alreadySent && !force) {
        // Not a failure — this exact plan version is already in the chart. Make the second
        // copy an explicit choice rather than something a double-click can cause.
        if (window.confirm(`${err.message}\n\nSend another copy to the EHR?`)) {
          await handlePushCarePlanToEhr(true);
          return;
        }
      } else {
        setSnackbarMessage(err?.message || 'Failed to send the care plan to the EHR');
        setSnackbarSeverity('error');
      }
    } finally {
      setPushingToEhr(false);
    }
  };

  const body = (
    <>
      <DialogTitle
        ref={titleRef}
        sx={inline ? {
          position: 'sticky',
          top: `${PATIENT_CHART_STICKY_HEADER_OFFSET}px`,
          zIndex: 35,
          backgroundColor: 'background.paper',
          boxShadow: 1,
        } : undefined}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}>
          <Typography variant="h5" sx={{ mr: 'auto' }}>
            Patient Details - {patient.first_name} {patient.last_name}
          </Typography>
          {/* Without wrapping, these action buttons get squeezed until their labels break
              mid-word ("LOG TIM E") and stack three lines deep. Let the row wrap and keep
              each label on one line instead. */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              '& .MuiButton-root': {
                whiteSpace: 'nowrap',
                flexShrink: 0,
              },
            }}
          >
            {needsContinueOnboarding(patient) && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={() => {
                  console.log('Continue onboarding clicked for patient:', patient.patient_id);
                  // Close dialog first (no-op in inline/tab mode)
                  handleClose();
                  // Set sessionStorage and navigate (both are synchronous)
                  sessionStorage.setItem('continuingOnboardingPatientId', patient.patient_id);
                  console.log('Navigating to onboarding tab with patient ID in sessionStorage:', sessionStorage.getItem('continuingOnboardingPatientId'));
                  console.log('Current URL before navigation:', window.location.href);
                  // PHA-797: append a changing _resume nonce so React Router always sees a
                  // searchParams change, even when the URL is already /?tab=onboarding. Dashboard's
                  // useEffect([searchParams]) then calls setActiveTabId('onboarding'), and the
                  // _resume key on <OnboardingWorkflow> forces a fresh unmount+remount so the
                  // mount effect re-runs and consumes the sessionStorage patient ID.
                  navigate('/?tab=onboarding&_resume=' + Date.now());
                }}
              >
                Continue Onboarding
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={downloadingCarePlan ? <CircularProgress size={14} /> : <PictureAsPdf />}
              disabled={downloadingCarePlan}
              onClick={async () => {
                setDownloadingCarePlan(true);
                try {
                  await generateCarePlanPdf(await resolveCarePlanPdfPayload(false));
                } catch {
                  setSnackbarMessage('Failed to generate Care Plan PDF');
                  setSnackbarSeverity('error');
                } finally {
                  setDownloadingCarePlan(false);
                }
              }}
            >
              {downloadingCarePlan ? 'Preparing Care Plan…' : 'Download Care Plan'}
            </Button>
            {/* PHA-1105 moved the EHR push button out of this header and into the Care
                plans tab (see currentTab === 2). The header is rendered on every tab, so
                from here it offered to file a care plan while a clinician was looking at
                device readings — the action now sits next to the thing it acts on. */}
            {/* PHA-737: Print Draft + Save as Draft — shown when draft candidates are available */}
            {pendingDraftCarePlans && pendingDraftCarePlans.care_plan_definitions.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdf />}
                onClick={async () => {
                  // PHA-737: the draft's program label must match the post-approval PDF. The persisted
                  // patient.apcm_tier reflects the LAST approval, not the current draft (and is NULL pre-
                  // first-approval), so fetch the prospective tier the current draft would yield. Awaited
                  // on click to avoid any race; falls back to the persisted value on error.
                  let prospectiveApcmTier = patient.apcm_tier ?? null;
                  // PHA-1290: notes fetched on click — this header button shows on every tab.
                  const carePlanReviewsPromise = fetchCarePlanReviewsForPdf(patient.patient_id, carePlanReviews);
                  try {
                    prospectiveApcmTier = await patientApi.getDraftApcmTier(patient.patient_id);
                  } catch (err) {
                    console.error('PHA-737: failed to fetch prospective APCM tier; using persisted value:', err);
                  }
                  const freshCarePlanReviews = await carePlanReviewsPromise;
                  generateCarePlanPdf({
                    ...patient,
                    voice_of_patient_enabled: voiceOfPatientEnabled,
                    no_target_dates_enabled: noTargetDatesEnabled,
                    // PHA-870: care-team review records — rendered after "Your Care Plan in Details".
                    care_plan_reviews: freshCarePlanReviews,
                    // PHA-1032: not yet approved — DRAFT banner/header/filename, and the
                    // current user stands in for the (nonexistent) approving clinician.
                    is_draft: true,
                    draft_reviewer_name: user?.name || user?.email || null,
                    // PHA-737: prospective program label — matches the final post-approval PDF.
                    apcm_tier: prospectiveApcmTier,
                    // PHA-737: drafts carry no expiration; reuse the existing approved plan's date
                    // if regenerating, else the same today+cycle default the backend applies on
                    // approve. PHA-1080: the cycle is this clinic's, not a hardcoded six.
                    expiration_date:
                      carePlanDefinitions?.find((d) => d.expiration_date)?.expiration_date ??
                      maskedDateToIso(defaultExpirationMasked(carePlanReviewMonths)),
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    care_plan_definitions: pendingDraftCarePlans.care_plan_definitions as any, // PHA-737: draft defs bridge to the PDF util's local CarePlanDefinition shape
                  }).catch(() => {
                    setSnackbarMessage('Failed to generate draft PDF');
                    setSnackbarSeverity('error');
                  });
                }}
              >
                Print Draft
              </Button>
            )}
            {pendingDraftCarePlans && pendingDraftCarePlans.care_plan_definitions.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Save />}
                disabled={savingDraftInPanel}
                onClick={async () => {
                  setSavingDraftInPanel(true);
                  try {
                    // Transform raw CarePlanDefinition objects to include the
                    // goal_index/task_index/question_index fields required by the
                    // backend CarePlanDefinitionCandidate Pydantic model (PHA-737).
                    const indexedDefinitions = pendingDraftCarePlans.care_plan_definitions.map((plan) => ({
                      disease: plan.disease,
                      uploaded_ts: plan.uploaded_ts,
                      goals: plan.goals.map((goal, i) => ({
                        ...goal,
                        goal_index: i,
                        patient_id: patient.patient_id,
                        disease: plan.disease,
                        uploaded_ts: plan.uploaded_ts,
                      })),
                      questions: plan.questions.map((question, i) => ({
                        ...question,
                        question_index: i,
                        patient_id: patient.patient_id,
                        disease: plan.disease,
                        uploaded_ts: plan.uploaded_ts,
                      })),
                      tasks: plan.tasks.map((task, i) => ({
                        ...task,
                        task_index: i,
                        patient_id: patient.patient_id,
                        disease: plan.disease,
                        uploaded_ts: plan.uploaded_ts,
                      })),
                    }));
                    await patientApi.saveCarePlanDraft(patient.patient_id, indexedDefinitions);
                    setSnackbarMessage('Draft saved successfully.');
                    setSnackbarSeverity('success');
                  } catch {
                    setSnackbarMessage('Failed to save draft. Please try again.');
                    setSnackbarSeverity('error');
                  } finally {
                    setSavingDraftInPanel(false);
                  }
                }}
              >
                {savingDraftInPanel ? 'Saving…' : 'Save as Draft'}
              </Button>
            )}
            {/* PHA-1043: ONE regeneration CTA. It used to be two — "Regenerate Care Plan"
                (current data) and "Reload patient data from stored images" (re-parse the
                originals) — which made the data source something the clinician had to infer
                from the button label. Now the source is an explicit choice in a dialog.
                The separate "Refresh from EHR" button below is NOT part of this: it is the
                plain ad-hoc sync with no regeneration, and it stays.
                PHA-1119: also shown, next to "Continue Onboarding", for a patient who is
                still mid-onboarding but already has a draft plan — see `showRegenerationCta`. */}
            {showRegenerationCta && (
              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<Autorenew />}
                onClick={() => {
                  // PHA-1050: with a regeneration already in flight, re-enter the saved
                  // draft. Opening the source picker here would generate a SECOND plan and
                  // orphan the clinician's edits, which is exactly what this ticket is for.
                  // PHA-1119: only for an already-enrolled patient — a mid-onboarding one
                  // resumes their draft through "Continue Onboarding" instead, so this
                  // button is their (only) way back to the source picker.
                  if (regenerationCtaResumesDraft) {
                    handleClose();
                    sessionStorage.setItem('continuingOnboardingPatientId', patient.patient_id);
                    navigate('/?tab=onboarding&_resume=' + Date.now());
                    return;
                  }
                  setRefreshSource('current');
                  setRefreshSourceDialogOpen(true);
                }}
              >
                {regenerationCtaResumesDraft
                  ? 'Complete Care Plan Regeneration'
                  : 'Regenerate Care Plan'}
              </Button>
            )}
            {!needsContinueOnboarding(patient) && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AccessTime />}
                onClick={() => setTimeTrackingDialogOpen(true)}
              >
                Log Time
              </Button>
            )}
            {/* PHA-397: ad-hoc EHR refresh, available to every clinician who
                can access the patient (not internal-only). Visible for
                FHIR-source patients only. Backend may return 409 if a sync
                is already running. */}
            {(patient as any)?.source === 'FHIR' && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Sync />}
                disabled={ehrSyncBusy}
                onClick={async () => {
                  const orgId = (patient as any)?.organization_id || (patient as any)?.organization;
                  if (!orgId) {
                    setError('Cannot resolve patient organization for EHR re-sync');
                    return;
                  }
                  setEhrSyncBusy(true);
                  setError(null);
                  setSuccessMessage(null);
                  try {
                    await patientApi.refreshFromEhr({
                      organization_id: String(orgId),
                      patient_id: patient.patient_id,
                    });
                    setSuccessMessage(
                      'EHR refresh queued. Changes will appear in the Smart Hub within a few minutes.',
                    );
                  } catch (e: any) {
                    const status = e?.errorData?.status ?? e?.status;
                    setError(
                      status === 409
                        ? 'An EHR sync is already running for this patient.'
                        : status === 403
                          ? 'You do not have access to refresh this patient from the EHR.'
                          : e?.message || 'Failed to start EHR refresh',
                    );
                  } finally {
                    setEhrSyncBusy(false);
                  }
                }}
              >
                {ehrSyncBusy ? 'Starting…' : 'Refresh from EHR'}
              </Button>
            )}
            {/* PHA-626: chart-level actions, available at every stage of the patient. */}
            <PatientActionsMenu
              patientId={patient.patient_id}
              organizationId={patient.organization_id || patient.organization}
              patient={patient as any}
              approvedDefinitions={carePlanDefinitions}
              draftDefinitions={pendingDraftCarePlans?.care_plan_definitions}
              phoneNumber={patient.phone_number}
              phoneType={(patient as any).phone_type}
              patientLanguage={patient.language}
              supportedLanguages={supportedLanguages}
              onConsentSent={() => setConsentRequestsRefreshKey((k) => k + 1)}
            />
            {!inline && (
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            )}
          </Box>
        </Box>
      </DialogTitle>

      {/* PHA-543: in inline mode, override MUI's default `overflow-y: auto` on
          DialogContent — that creates an inner scroll context which would
          confine the sticky data tab bar inside DialogContent instead of
          letting it stick to the viewport while the page scrolls. The dialog
          mode keeps the auto behavior so the modal still scrolls internally. */}
      <DialogContent sx={inline ? { overflow: 'visible' } : undefined}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Patient Information Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Patient Information</Typography>
              {!editMode ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setEditMode(true);
                    setEditDob(isoToMaskedDate(patient.date_of_birth));
                  }}
                >
                  Edit
                </Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setEditMode(false);
                      setEditedPatient(patient);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={async () => {
                      if (!editedPatient) return;
                      // Validate required fields
                      if (!editedPatient.first_name?.trim()) {
                        setError('First name is required.');
                        return;
                      }
                      if (!editedPatient.last_name?.trim()) {
                        setError('Last name is required.');
                        return;
                      }
                      const dobIso = maskedDateToIso(editDob);
                      if (!dobIso || !isValidDate(editDob)) {
                        setError('Please enter a valid date of birth (MM/DD/YYYY).');
                        return;
                      }
                      if (isFutureMaskedDate(editDob)) {
                        setError('Date of birth cannot be in the future.');
                        return;
                      }
                      setLoading(true);
                      try {
                        // Call the update API
                        const updateResult = await patientApi.update({
                          patient_id: editedPatient.patient_id,
                          first_name: editedPatient.first_name.trim(),
                          last_name: editedPatient.last_name.trim(),
                          date_of_birth: dobIso,
                          phone_number: editedPatient.phone_number,
                          gender: editedPatient.gender,
                          preferred_contact_method: editedPatient.preferred_contact_method,
                          language: editedPatient.language,
                        });
                        // Handle phone type verification result
                        const updatedPatient = { ...editedPatient, first_name: editedPatient.first_name.trim(), last_name: editedPatient.last_name.trim(), date_of_birth: dobIso };
                        if (updateResult?.phone_type) {
                          updatedPatient.phone_type = updateResult.phone_type;
                          // PHA-1309: mirrors the backend — a landline never moves a
                          // care-team-call patient to Voice AI.
                          if (updateResult.phone_type === 'landline' && updatedPatient.preferred_contact_method !== 'care_team_call') {
                            updatedPatient.preferred_contact_method = 'voice';
                          }
                        }
                        setEditMode(false);
                        // Update parent component if callback provided
                        if (onPatientUpdate) {
                          onPatientUpdate(updatedPatient);
                        }
                        // Show success message
                        if (updateResult?.phone_type === 'landline') {
                          setSuccessMessage('Patient updated. Landline detected — contact method switched to Voice.');
                        } else {
                          setSuccessMessage('Patient information updated successfully!');
                        }
                        setTimeout(() => setSuccessMessage(null), 5000);
                      } catch (err: any) {
                        setError(err.message || 'Failed to update patient');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    Save
                  </Button>
                </Stack>
              )}
            </Box>
            <Stack direction="row" spacing={3}>
              {editMode ? (
                <>
                  <TextField
                    label="First Name"
                    value={editedPatient?.first_name || ''}
                    onChange={(e) => {
                      if (editedPatient) {
                        setEditedPatient({ ...editedPatient, first_name: e.target.value });
                      }
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Last Name"
                    value={editedPatient?.last_name || ''}
                    onChange={(e) => {
                      if (editedPatient) {
                        setEditedPatient({ ...editedPatient, last_name: e.target.value });
                      }
                    }}
                    sx={{ flex: 1 }}
                  />
                </>
              ) : (
                <TextField
                  label="Name"
                  value={`${patient.first_name} ${patient.last_name}`}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
              )}
              {editMode ? (
                <TextField
                  label="Date of Birth"
                  value={editDob}
                  onChange={(e) => setEditDob(maskDateInput(e.target.value))}
                  placeholder="MM/DD/YYYY"
                  sx={{ flex: 1 }}
                />
              ) : (
                <TextField
                  label="Date of Birth"
                  value={formatDate(patient.date_of_birth)}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
              )}
              <TextField
                label="MRN"
                value={patient.patient_id}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1 }}
              />
              {showClinic && (
                <TextField
                  label="Clinic"
                  value={patient.organization_name || 'N/A'}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <TextField
                  label="Phone Number"
                  value={editMode ? (editedPatient?.phone_number || '') : (patient.phone_number || 'Not provided')}
                  onChange={(e) => {
                    if (editMode && editedPatient) {
                      setEditedPatient({ ...editedPatient, phone_number: e.target.value });
                    }
                  }}
                  InputProps={{ readOnly: !editMode }}
                  fullWidth
                  placeholder="Enter phone number"
                />
                {!editMode && patient.phone_type === 'landline' && (
                  <Chip label="Landline — SMS unavailable" size="small" color="warning" sx={{ mt: 0.5, alignSelf: 'flex-start' }} />
                )}
                {!editMode && patient.phone_type === 'mobile' && (
                  <Chip label="Mobile — verified" size="small" color="success" sx={{ mt: 0.5, alignSelf: 'flex-start' }} />
                )}
                {!editMode && patient.phone_type === 'voip' && (
                  <Chip label="VoIP" size="small" color="info" sx={{ mt: 0.5, alignSelf: 'flex-start' }} />
                )}
                {/* PHA-790: SMS consent state. Read-only on purpose - only the patient can re-subscribe (START). */}
                {!editMode && smsReachability && smsSubscriptionLabel(smsReachability) && (
                  <MuiTooltip title={smsSubscriptionDetail(smsReachability) ?? ''}>
                    <Chip
                      label={smsSubscriptionLabel(smsReachability)}
                      size="small"
                      color={smsReachability.sms_subscription_status === 'unsubscribed' ? 'error' : 'default'}
                      variant={smsReachability.sms_subscription_status === 'unsubscribed' ? 'filled' : 'outlined'}
                      data-testid="sms-subscription-chip"
                      sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                    />
                  </MuiTooltip>
                )}
                {!editMode && smsReachability?.sms_undeliverable && (
                  <MuiTooltip
                    title={
                      `Twilio could not deliver texts to this number` +
                      (smsReachability.suppressed_since
                        ? ` (since ${new Date(smsReachability.suppressed_since).toLocaleDateString()})`
                        : '') +
                      `. Survey texts are paused. Update the phone number to a mobile number to resume them.`
                    }
                  >
                    <Chip
                      label={
                        `SMS undeliverable — ${smsReachability.error_label || 'number cannot receive texts'}` +
                        (smsReachability.surveys_route_to_voice ? ' · surveys go by Voice AI' : '')
                      }
                      size="small"
                      color="error"
                      data-testid="sms-undeliverable-chip"
                      sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                    />
                  </MuiTooltip>
                )}
              </Box>
              {editMode ? (
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    label="Gender"
                    value={editedPatient?.gender || ''}
                    onChange={(e) => {
                      if (editedPatient) {
                        setEditedPatient({ ...editedPatient, gender: e.target.value as string });
                      }
                    }}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Gender"
                  value={patient.gender ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1) : ''}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
              )}
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Survey Contact Method</InputLabel>
                <Select
                  label="Survey Contact Method"
                  value={editMode ? (editedPatient?.preferred_contact_method || 'sms') : (patient.preferred_contact_method || 'sms')}
                  onChange={(e) => {
                    if (editMode && editedPatient) {
                      setEditedPatient({ ...editedPatient, preferred_contact_method: e.target.value as 'sms' | 'voice' | 'care_team_call' });
                    }
                  }}
                  disabled={!editMode}
                >
                  <MenuItem value="sms">SMS (text message)</MenuItem>
                  {/* PHA-1309: the scheduler is disabled for this patient — no SMS, no Voice AI. */}
                  <MenuItem value="care_team_call">Care Team Call (staff call the patient)</MenuItem>
                  {/* PHA-1229: a saved 'voice' must always have an option to render — without
                      one the Select shows blank and an edit can only save 'sms'. When the org
                      has voice off it stays visible but can't be newly picked. */}
                  {(voiceEnabled || patient.preferred_contact_method === 'voice') && (
                    <MenuItem value="voice" disabled={!voiceEnabled}>
                      {voiceEnabled ? 'Voice AI (phone call)' : 'Voice AI (not enabled for this clinic)'}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              {supportedLanguages.length > 1 && (
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Preferred Language</InputLabel>
                  <Select
                    label="Preferred Language"
                    value={editMode ? (editedPatient?.language || 'en-US') : (patient.language || 'en-US')}
                    onChange={(e) => {
                      if (editMode && editedPatient) {
                        setEditedPatient({ ...editedPatient, language: e.target.value as string });
                      }
                    }}
                    disabled={!editMode}
                  >
                    {supportedLanguages.map(lang => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name} ({lang.native_name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {patient.apcm_tier && (
                <TextField
                  label="APCM Tier"
                  value={`Tier ${patient.apcm_tier}`}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
              <TextField
                label="Consent Status"
                value={
                  patient.consent_status === 'collected' ? 'Collected' :
                  patient.consent_status === 'denied' ? 'Denied' :
                  'Not recorded'
                }
                InputProps={{ readOnly: true }}
                sx={{ flex: 1 }}
              />
              {patient.consent_date && (
                <TextField
                  label="Consent Date"
                  value={formatDate(patient.consent_date)}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
              )}
            </Stack>
            {/* PHA-1225: Program Confirmation Forms sent from the Actions menu, and the patient's
                answers. Below, and separate from, the verbal Consent Status above. */}
            <ConsentRequestsPanel patientId={patient.patient_id} refreshKey={consentRequestsRefreshKey} />
            {patient.last_successful_data_collection && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Last Successful Data Collection"
                  value={formatDateTime(patient.last_successful_data_collection)}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* RPM Device */}
        <PatientDeviceSection
          patientId={patient.patient_id}
          organizationId={patient.organization_id || patient.organization}
          onDeviceChange={setPatientLinkedDevice}
        />

        {/* Data Tabs — PHA-543: sticky directly under the patient-tab strip
            once the patient demographics scroll out of view, so the data tab
            navigation stays reachable without losing the patient context. */}
        <Box
          sx={(theme) => ({
            borderBottom: 1,
            borderColor: 'divider',
            position: inline ? 'sticky' : 'static',
            top: inline ? `${PATIENT_CHART_STICKY_HEADER_OFFSET + titleHeightPx}px` : undefined,
            backgroundColor: theme.palette.background.paper,
            zIndex: 30,
          })}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Problems" />
            <Tab label="Medications" />
            <Tab label="Care plans" />
            {/* PHA-795: Voice of the patient — always mounted so indices 4-9 are stable.
                display:none hides it for orgs without VoP enabled; the Tab still occupies
                index 3, keeping every subsequent tab's positional value correct. */}
            <Tab label="Voice of the patient" sx={{ display: voiceOfPatientEnabled ? undefined : 'none' }} />
            <Tab label="Surveys" />
            <Tab label="Patient Outreach" />
            <Tab label="Event log" />
            <Tab label="Answers" />
            <Tab label="Device Readings" />
            {/* PHA-573: only show tab for orgs with smart_hub_ai_enabled */}
            {aiInsightsEnabled && <Tab label="AI Insights" />}
          </Tabs>
        </Box>

        {/* Problems Tab */}
        {currentTab === 0 && (
          <Box sx={{ mt: 2 }}>
            {/* PHA-1141: problems the EHR sync found, reviewed in place. */}
            {ehrChanges && ehrChanges.problems.length > 0 && (
              <Box id="ehr-review-problem_list">
                <EhrChangesReviewPanel
                  documentType="problem_list"
                  items={ehrChanges.problems}
                  highlighted={highlightedEhrSection === 'problems'}
                  onSubmit={(decisions) => submitEhrDecisions(decisions).catch(() => undefined)}
                />
              </Box>
            )}
            {patient.problems && patient.problems.length > 0 ? (
              <TableContainer component={Paper}>
                <Table sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '22%' }}>Problem</TableCell>
                      <TableCell sx={{ width: '12%' }}>ICD-10 Code</TableCell>
                      <TableCell sx={{ width: '12%' }}>SNOMED Code</TableCell>
                      <TableCell sx={{ width: '10%' }}>Clinical Status</TableCell>
                      <TableCell sx={{ width: '10%' }}>Onset Date</TableCell>
                      <TableCell sx={{ width: '17%' }}>Uploaded By</TableCell>
                      <TableCell sx={{ width: '17%' }}>Upload Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patient.problems.map((problem, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {problem.problem}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {problem.icd10_code ? (
                            <MuiTooltip title={problem.icd10_description || ''} placement="top">
                              <Chip label={problem.icd10_code} size="small" color="primary" variant="outlined" />
                            </MuiTooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {problem.snomed_code ? (
                            <MuiTooltip title={problem.snomed_description || ''} placement="top">
                              <Chip label={problem.snomed_code} size="small" color="secondary" variant="outlined" />
                            </MuiTooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={problem.clinical_status}
                            color={getStatusColor(problem.clinical_status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(problem.onset_date)}</TableCell>
                        <TableCell sx={{
                          maxWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {(() => {
                            // PHA-1141: a change acknowledged from the EHR names the clinician
                            // who acknowledged it (the row's upload stamp is that moment).
                            // Only while the row still carries that acknowledgment's stamp — a
                            // later re-approval re-stamps it and the name must follow.
                            const found = ehrChanges
                              ? acknowledgmentForListItem(ehrChanges.acknowledgments, 'problem_list', problem)
                              : null;
                            const ack = found && found.acknowledged_by === problem.uploaded_by ? found : null;
                            const who = ack?.acknowledged_by_name || problem.uploaded_by || '';
                            return (
                              <MuiTooltip
                                title={ack ? `Acknowledged EHR change — ${who}` : problem.uploaded_by || ''}
                                placement="top"
                              >
                                {/* PHA-1149: who and when */}
                                <span>
                                  {who}
                                  {problem.uploaded_ts ? ` · ${formatDateTime(problem.uploaded_ts)}` : ''}
                                </span>
                              </MuiTooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{
                          maxWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          <MuiTooltip title={formatDateTime(problem.uploaded_ts)} placement="top">
                            <span>{formatDateTime(problem.uploaded_ts)}</span>
                          </MuiTooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No problems recorded for this patient.
              </Typography>
            )}
          </Box>
        )}

        {/* Medications Tab */}
        {currentTab === 1 && (
          <Box sx={{ mt: 2 }}>
            {/* PHA-1141: medication changes the EHR sync found (new / stopped / dose / removed). */}
            {ehrChanges && ehrChanges.medications.length > 0 && (
              <Box id="ehr-review-medication_list">
                <EhrChangesReviewPanel
                  documentType="medication_list"
                  items={ehrChanges.medications}
                  highlighted={highlightedEhrSection === 'medications'}
                  onSubmit={(decisions) => submitEhrDecisions(decisions).catch(() => undefined)}
                />
              </Box>
            )}
            {patient.medications && patient.medications.length > 0 ? (
              <TableContainer component={Paper}>
                <Table sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '25%' }}>Medication</TableCell>
                      <TableCell sx={{ width: '25%' }}>Dosage</TableCell>
                      <TableCell sx={{ width: '12%' }}>Start Date</TableCell>
                      <TableCell sx={{ width: '12%' }}>End Date</TableCell>
                      <TableCell sx={{ width: '13%' }}>Uploaded By</TableCell>
                      <TableCell sx={{ width: '13%' }}>Upload Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patient.medications.map((medication, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {medication.medication}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={medication.dosage ? 'text.primary' : 'text.secondary'}>
                            {medication.dosage || 'Not specified'}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(medication.start_date)}</TableCell>
                        <TableCell>
                          {medication.end_date ? formatDate(medication.end_date) : 'Ongoing'}
                        </TableCell>
                        <TableCell sx={{
                          maxWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {(() => {
                            // PHA-1141 requirement 4: a medication change shows who acknowledged
                            // it (see the Problems tab for the stamp rule).
                            const found = ehrChanges
                              ? acknowledgmentForListItem(ehrChanges.acknowledgments, 'medication_list', medication)
                              : null;
                            const ack = found && found.acknowledged_by === medication.uploaded_by ? found : null;
                            const who = ack?.acknowledged_by_name || medication.uploaded_by || '';
                            return (
                              <MuiTooltip
                                title={ack ? `Acknowledged EHR change — ${who}` : medication.uploaded_by || ''}
                                placement="top"
                              >
                                {/* PHA-1149: who and when */}
                                <span>
                                  {who}
                                  {medication.uploaded_ts ? ` · ${formatDateTime(medication.uploaded_ts)}` : ''}
                                </span>
                              </MuiTooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{
                          maxWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          <MuiTooltip title={formatDate(medication.uploaded_ts)} placement="top">
                            <span>{formatDate(medication.uploaded_ts)}</span>
                          </MuiTooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No medications recorded for this patient.
              </Typography>
            )}
          </Box>
        )}

        {/* Care Plans Tab */}
        {currentTab === 2 && (
          <Box sx={{ mt: 2 }}>
            {/* PHA-397: Banner for pending EHR-refresh draft care plans */}
            {pendingDraftCarePlans && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={async () => {
                      if (!draftPlanGenerated || !pendingDraftCarePlans) {
                        await loadPendingDraftCarePlans();
                      }
                      setShowDraftReviewModal(true);
                    }}
                  >
                    Review Updates
                  </Button>
                }
              >
                Care plan updates from an EHR refresh are pending your review.
              </Alert>
            )}
            {carePlansToRender.length > 0 ? (
              <Stack spacing={2}>
                {/* PHA-895 follow-up: read-only draft surfaced when no approved plan exists. */}
                {showDraftCarePlans && (
                  <Alert severity="warning" sx={{ mb: 0 }}>
                    Draft — pending approval. This care plan has not been finalized; complete onboarding to approve it.
                  </Alert>
                )}
                {carePlansToRender.map((carePlan, index) => {
                  // PHA-795 (flow redesign): person-centered (Voice of the Patient) goals
                  // now render in their OWN "Voice of the patient" section below the care
                  // plans (see after this Stack), NOT merged into the General care plan card.
                  const clinicalGoalCount = (carePlan.goals && carePlan.goals.length) || 0;
                  return (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      {/* PHA-1105: the plan title and the EHR push share this row, matching
                          the "Add Care Plan Note" header in the frame below. */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                          {formatPatientCarePlanHeading(carePlan.disease, carePlan.diseases, carePlan.icd10_codes, carePlan.is_general)}
                        </Typography>
                        {/* Only on the first card: the push files the patient's whole
                            approved plan as ONE document, so it is not a per-plan action.
                            Repeating it per card would suggest each plan files separately.

                            The label is FIXED — it never names the EHR (clinics run
                            different systems) and never switches to a "…again" variant once
                            filed, because a control that renames itself is one you have to
                            re-read before every press. The already-sent state that label
                            used to carry moves to the caption below it, which says more by
                            naming the date and the destination. */}
                        {index === 0 && carePlanPushStatus?.push_enabled && hasApprovedCarePlans && (
                          <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CloudUpload />}
                              disabled={pushingToEhr}
                              onClick={() => handlePushCarePlanToEhr(false)}
                            >
                              {pushingToEhr ? 'Sending…' : 'Push Care Plan to EMR'}
                            </Button>
                            {/* PHA-1212: the upload's state — Pending / Uploaded / Failed — for
                                the plan on screen. Uploading is asynchronous: Cobalt accepts the
                                PDF, and only its webhook says whether the EHR filed it, so an
                                accepted push is Pending, never Uploaded. PHA-1193: a rejected
                                push must show, or it is indistinguishable from never sent. */}
                            {(() => {
                              const ehrPush = describeCarePlanEhrPush(carePlanPushStatus);
                              const lastPush = ehrPush.push;
                              if (ehrPush.state === 'none' || !lastPush) return null;
                              const on = (iso?: string | null) => (iso ? ` on ${formatDateTime(iso)}` : '');
                              const folder = carePlanPushStatus?.folder_name ? ` (${carePlanPushStatus.folder_name})` : '';
                              // PHA-1149
                              const by = lastPush.pushed_by_name ? ` by ${lastPush.pushed_by_name}` : '';
                              // PHA-1204: say when nobody pressed the button, so a failure
                              // reads as theirs to finish.
                              const what = lastPush.automatic ? 'Automatic upload' : 'Upload';
                              let detail: string;
                              let chipColor: 'info' | 'success' | 'error';
                              if (ehrPush.state === 'uploaded') {
                                chipColor = 'success';
                                detail = `${EHR_PUSH_UPLOADED_DETAIL}${on(lastPush.confirmed_at || lastPush.requested_at)}${folder}${by}.`;
                              } else if (ehrPush.state === 'failed') {
                                // PHA-1223: clinician wording, and whether Phanova will retry by itself.
                                const failure = describeEhrPushFailure(lastPush);
                                chipColor = 'error';
                                detail =
                                  `${what} unsuccessful${on(lastPush.requested_at)}: ${failure.reason}. ` +
                                  (failure.autoRetryScheduled
                                    ? failure.next
                                    : `${failure.next} Use the button above to push it again.`);
                              } else if (ehrPush.stale) {
                                // Normally confirmed within a minute; past the window, say so
                                // rather than promise a confirmation that may never come.
                                chipColor = 'info';
                                detail =
                                  `${what} submitted${on(lastPush.requested_at)}${by}, but the EHR has not ` +
                                  'confirmed it yet. Check the EHR, or push again if it is missing.';
                              } else {
                                chipColor = 'info';
                                detail = `${EHR_PUSH_PENDING_DETAIL} ${what} sent${on(lastPush.requested_at)}${by}.`;
                              }
                              return (
                                <Box data-testid="care-plan-ehr-push-status" sx={{ mt: 0.5, maxWidth: 360, ml: 'auto' }}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color={chipColor}
                                    label={EHR_PUSH_STATE_LABEL[ehrPush.state]}
                                    icon={ehrPush.state === 'pending' && !ehrPush.stale ? <CircularProgress size={12} /> : undefined}
                                  />
                                  <Typography
                                    variant="caption"
                                    color={ehrPush.state === 'failed' ? 'error' : 'text.secondary'}
                                    sx={{ display: 'block', mt: 0.5 }}
                                  >
                                    {detail}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </Box>
                        )}
                      </Box>
                      {/* PHA-944: an APPROVED plan shows when it was approved, not when it
                          was generated. approved_ts is null for a draft and for anything
                          approved before PHA-944, and both fall back to the generation
                          timestamp — which is why no backfill was needed.
                          PHA-727: when the patient has an older plan, also show the date
                          care management actually started for them, so a regenerated plan
                          does not look like a brand-new enrollment. */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Created: {formatDateTime((carePlan as any).approved_ts || carePlan.uploaded_ts)}
                      </Typography>
                      {/* PHA-1149: who drafted, last edited and approved this plan. A draft
                          carries its provenance in `provenance`; an approved plan has it on
                          the row itself (copied from the draft at approval). */}
                      {(() => {
                        const cp = carePlan as any;
                        const prov = cp.provenance || cp;
                        const approvedBy = cp.reviewed_by_name;
                        return (
                          <Box sx={{ mb: 1 }} data-testid="care-plan-provenance">
                            <EventProvenance label="Drafted by" name={prov.created_by_name} at={prov.created_ts} />
                            <EventProvenance label="Last edited by" name={prov.last_edited_by_name} at={prov.last_edited_ts} />
                            <EventProvenance label="Approved by" name={approvedBy} at={cp.approved_ts} />
                          </Box>
                        );
                      })()}
                      {(() => {
                        // PHA-727: read the server-computed MIN(uploaded_ts) across ALL of
                        // the patient's plans. Deriving it from care_plan_definitions here
                        // cannot work: patients_with_details is distinct-on(patient_id,
                        // disease) and every merged plan is disease='global', so the payload
                        // holds exactly one plan — the newest — and a client-side MIN() is
                        // always this plan's own date. That is why this line never rendered.
                        const firstCreated = (patient as any)?.first_care_plan_ts;
                        if (!firstCreated) return null;
                        // Only worth showing once it differs from what "Created" already says.
                        const shownCreated = (carePlan as any).approved_ts || carePlan.uploaded_ts;
                        if (new Date(firstCreated).getTime() >= new Date(shownCreated).getTime()) {
                          return null;
                        }
                        return (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            First created: {formatDateTime(firstCreated)}
                          </Typography>
                        );
                      })()}

                      {/* PHA-726/PHA-897: the acknowledgment for THIS plan, above the review
                          date. Absent means we have no record the plan was reviewed with the
                          patient — which is the honest reading for every plan approved before
                          PHA-897 shipped, and is said plainly rather than left blank. */}
                      {(() => {
                        const forThisPlan = carePlanAcknowledgments.find(
                          (a) => a.care_plan_uploaded_ts === carePlan.uploaded_ts
                        );
                        const latest = forThisPlan || carePlanAcknowledgments[0];
                        if (!latest) return null;
                        return (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Reviewed with patient: {formatDateTime(latest.acknowledged_ts)}
                            {latest.acknowledged_by_name ? ` by ${latest.acknowledged_by_name}` : ''}
                            {!forThisPlan ? ' (previous care plan)' : ''}
                          </Typography>
                        );
                      })()}

                      {/* PHA-1074: the next review date is part of the approved plan, so it
                          follows the same rule as goals — no inline edit here. Changing it
                          requires purposefully starting a new draft via Regenerate Care Plan. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {carePlan.expiration_date
                            ? `Next Review Date: ${isoToMaskedDate(carePlan.expiration_date)}`
                            // the autosave stores whatever is in the field, half-typed included
                            : showDraftCarePlans && maskedDateToIso(draftReviewDateMasked ?? '')
                              ? `Next Review Date: ${draftReviewDateMasked} (draft)`
                              : 'Next Review Date: not set'}
                        </Typography>
                      </Box>

                      {/* Goals Section — clinical goals only (Voice of the Patient goals
                          render in their own section below the care plans). */}
                      {clinicalGoalCount > 0 && (
                        <Accordion
                          expanded={!!goalsAccordionOpen[index]}
                          onChange={(_e, open) => setGoalsAccordionOpen((prev) => ({ ...prev, [index]: open }))}
                        >
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              Goals ({clinicalGoalCount})
                            </Typography>
                            {(() => {
                              // PHA-1142: say on the collapsed header that readings await review.
                              const pendingTypes = new Set(
                                (observationHistory?.readings || [])
                                  .filter((r) => r.pending && r.pending.length > 0)
                                  .map((r) => r.document_type)
                              );
                              const count = (carePlan.goals || []).filter((g: any) =>
                                (goalVitalGroup(g?.measure)?.documentTypes || []).some((t) => pendingTypes.has(t))
                              ).length;
                              return count > 0 ? (
                                <Chip
                                  label={`New EHR readings on ${count} goal${count === 1 ? '' : 's'}`}
                                  size="small"
                                  color="warning"
                                  sx={{ ml: 2 }}
                                />
                              ) : null;
                            })()}
                          </AccordionSummary>
                          <AccordionDetails>
                            {clinicalGoalCount > 0 && (
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    {/* PHA-1142: expander for the goal's vitals table */}
                                    <TableCell padding="checkbox" />
                                    <TableCell>Measure</TableCell>
                                    <TableCell>Target Value</TableCell>
                                    <TableCell>Test</TableCell>
                                    {/* PHA-866: hide date/achievement columns when the org disables target dates */}
                                    {!noTargetDatesEnabled && (
                                      <>
                                        <TableCell>Due Date</TableCell>
                                        <TableCell>Achieved?</TableCell>
                                        <TableCell>Achievement Date</TableCell>
                                      </>
                                    )}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {carePlan.goals.map((goal, goalIndex) => {
                                    // PHA-866: no target dates ⇒ never flag a goal as overdue.
                                    const goalOverdue = !noTargetDatesEnabled && isOverdue(goal.due_date) && !goal.achieved;
                                    // PHA-1142: the readings this goal is measured by, with the
                                    // reading its target was calculated from pinned first.
                                    const vitalGroup = goalVitalGroup(goal.measure);
                                    const vitalsKey = `${index}:${goalIndex}`;
                                    const vitalsOpen = openGoalVitals.has(vitalsKey);
                                    const vitalRows: VitalTableRow[] = vitalGroup
                                      ? withBaselineRow(
                                          buildVitalTableRows(observationHistory?.readings || [], vitalGroup.documentTypes),
                                          vitalGroup,
                                          (showDraftCarePlans
                                            ? observationHistory?.baselines?.draft
                                            : observationHistory?.baselines?.approved)?.vitals
                                        )
                                      : [];
                                    const pendingVitalRows = vitalRows.filter((r) => r.pending.length > 0).length;
                                    const goalColumnCount = noTargetDatesEnabled ? 4 : 7;

                                    return (
                                      <React.Fragment key={goalIndex}>
                                      <TableRow
                                        sx={{
                                          bgcolor: goal.achieved ? 'success.50' :
                                                   goalOverdue ? 'error.50' : 'inherit',
                                          borderLeft: goalOverdue ? '4px solid #d32f2f' : 'none',
                                          opacity: goal.achieved ? 0.8 : 1
                                        }}
                                      >
                                        <TableCell padding="checkbox">
                                          {vitalGroup && (
                                            <MuiTooltip title={vitalsOpen ? 'Hide readings' : `Show ${vitalGroup.label.toLowerCase()} readings`}>
                                              <IconButton
                                                size="small"
                                                aria-label={`${vitalsOpen ? 'Hide' : 'Show'} ${vitalGroup.label} readings`}
                                                onClick={() =>
                                                  setOpenGoalVitals((prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(vitalsKey)) next.delete(vitalsKey);
                                                    else next.add(vitalsKey);
                                                    return next;
                                                  })
                                                }
                                              >
                                                {vitalsOpen ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                                              </IconButton>
                                            </MuiTooltip>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            sx={{
                                              textDecoration: goal.achieved ? 'line-through' : 'none',
                                              color: goal.achieved ? 'text.secondary' :
                                                     goalOverdue ? 'error.main' : 'text.primary'
                                            }}
                                          >
                                            {goal.measure}
                                          </Typography>
                                          {pendingVitalRows > 0 && (
                                            <Chip
                                              label={`${pendingVitalRows} new reading${pendingVitalRows === 1 ? '' : 's'}`}
                                              size="small"
                                              color="warning"
                                              sx={{ mt: 0.5 }}
                                            />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {/* PHA-1074: no inline edit here — per product decision, a
                                              goal on an approved plan can only change via Regenerate
                                              Care Plan, not a pencil on the target value itself. */}
                                          <Chip
                                            label={goal.target_value}
                                            size="small"
                                            color={goal.achieved ? 'success' :
                                                   goalOverdue ? 'error' : 'success'}
                                            variant="outlined"
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            sx={{
                                              color: goal.achieved ? 'text.secondary' :
                                                     goalOverdue ? 'error.main' : 'text.primary'
                                            }}
                                          >
                                            {goal.test}
                                          </Typography>
                                        </TableCell>
                                        {/* PHA-866: hide date/achievement cells when the org disables target dates */}
                                        {!noTargetDatesEnabled && (
                                          <>
                                            <TableCell>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography
                                                  sx={{
                                                    color: goal.achieved ? 'text.secondary' :
                                                           goalOverdue ? 'error.main' : 'text.primary'
                                                  }}
                                                >
                                                  {formatDate(goal.due_date)}
                                                </Typography>
                                                {goalOverdue && (
                                                  <Chip
                                                    label="OVERDUE"
                                                    size="small"
                                                    color="error"
                                                    variant="filled"
                                                    sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                                                  />
                                                )}
                                              </Box>
                                            </TableCell>
                                            <TableCell padding="checkbox">
                                              {/* PHA-895 follow-up: draft is read-only — no achievement toggle. */}
                                              {showDraftCarePlans ? (
                                                <Typography color="text.secondary">—</Typography>
                                              ) : (
                                                <Checkbox
                                                  checked={goal.achieved || false}
                                                  onChange={(e) => handleGoalAchievementToggle(carePlan, goal, e.target.checked)}
                                                  disabled={pendingUpdates.has(`goal-${goal.measure}`)}
                                                  size="small"
                                                  color="success"
                                                />
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {goal.achieved_date ? (
                                                <Typography sx={{ color: goal.achieved ? 'text.secondary' : 'text.primary' }}>
                                                  {formatDate(goal.achieved_date)}
                                                </Typography>
                                              ) : (
                                                <Typography color="text.secondary">—</Typography>
                                              )}
                                            </TableCell>
                                          </>
                                        )}
                                      </TableRow>
                                      {vitalGroup && (
                                        <TableRow>
                                          <TableCell colSpan={goalColumnCount} sx={{ py: 0, borderBottom: vitalsOpen ? undefined : 'none' }}>
                                            <Collapse in={vitalsOpen} timeout="auto" unmountOnExit>
                                              <Box id={`goal-vitals-${index}-${goalIndex}`} sx={{ pl: 5, pr: 1, py: 1.5 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                  {vitalGroup.label} readings from the EHR and entered in the chart, newest first
                                                </Typography>
                                                <GoalVitalsTable
                                                  rows={vitalRows}
                                                  onAcknowledge={
                                                    observationHistory?.review_available ? acknowledgeVitalRows : undefined
                                                  }
                                                  busyKeys={vitalAckBusy}
                                                  emptyText={`No ${vitalGroup.label.toLowerCase()} readings on file yet.`}
                                                />
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                      </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Questions Section — PHA-701: show all questions; badge device-covered ones. */}
                      {carePlan.questions && carePlan.questions.length > 0 && (
                        <Accordion sx={{ mt: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              Assessment Questions ({carePlan.questions?.length || 0})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1}>
                              {(carePlan.questions ?? []).map((question: any, questionIndex: number) => {
                                // PHA-701: blood pressure questions are device-covered when patient has an active linked device.
                                const isDeviceCovered = patientLinkedDevice && question.data_type === 'blood pressure';
                                return (
                                  <Box key={questionIndex} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                      {question.prompt}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      <Chip
                                        label={`Type: ${question.data_type}`}
                                        size="small"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={`Frequency: ${formatFrequency(question.frequency)}`}
                                        size="small"
                                        variant="outlined"
                                        color={(!question.frequency || question.frequency === 'unscheduled') ? 'default' : 'primary'}
                                      />
                                      {isDeviceCovered && (
                                        <Chip
                                          label="Device covered"
                                          size="small"
                                          color="info"
                                          variant="outlined"
                                          title="Sent automatically from the patient's connected BP device — excluded from scheduled survey links"
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Tasks Section */}
                      {carePlan.tasks && carePlan.tasks.length > 0 && (() => {
                        const sortTasks = (tasks: typeof carePlan.tasks) =>
                          [...tasks].sort((a, b) => {
                            const aOverdue = a.due_date && isOverdue(a.due_date) && !a.completed;
                            const bOverdue = b.due_date && isOverdue(b.due_date) && !b.completed;
                            if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
                            if (a.due_date && b.due_date) return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
                            if (a.due_date) return -1;
                            if (b.due_date) return 1;
                            return 0;
                          });
                        const clinicianTasks = sortTasks(carePlan.tasks.filter(t => (t.assignee || '').toLowerCase() !== 'patient'));
                        const patientTasks = sortTasks(carePlan.tasks.filter(t => (t.assignee || '').toLowerCase() === 'patient'));

                        const renderTaskCard = (task: typeof carePlan.tasks[0], taskIndex: number) => {
                          // PHA-866: no target dates ⇒ never flag a task as overdue.
                          const taskOverdue = !noTargetDatesEnabled && task.due_date && isOverdue(task.due_date) && !task.completed;
                          const freq = formatFrequency(task.frequency);
                          const showFrequency = freq && freq !== 'N/A' && freq !== 'custom schedule';

                          return (
                            <Box
                              key={taskIndex}
                              sx={{
                                p: 1,
                                bgcolor: task.completed ? 'success.50' :
                                         taskOverdue ? 'error.50' : 'grey.50',
                                borderRadius: 1,
                                borderLeft: taskOverdue ? '4px solid #d32f2f' : 'none',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                opacity: task.completed ? 0.8 : 1,
                              }}
                            >
                              {/* PHA-895 follow-up: draft is read-only — no task completion toggle. */}
                              {!showDraftCarePlans && (
                                <Checkbox
                                  checked={task.completed || false}
                                  onChange={(e) => handleTaskCompletionToggle(carePlan, task, e.target.checked)}
                                  disabled={pendingUpdates.has(`task-${task.occurrence_id}`) || !task.occurrence_id}
                                  size="small"
                                  color="success"
                                  sx={{ mt: -0.5 }}
                                />
                              )}
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mb: 1,
                                    textDecoration: task.completed ? 'line-through' : 'none',
                                    color: task.completed ? 'text.secondary' :
                                           taskOverdue ? 'error.main' : 'text.primary',
                                  }}
                                >
                                  {task.task}
                                  {taskOverdue && (
                                    <Chip
                                      label="OVERDUE"
                                      size="small"
                                      color="error"
                                      variant="filled"
                                      sx={{
                                        ml: 1,
                                        fontWeight: 'bold',
                                        fontSize: '0.7rem',
                                        verticalAlign: 'middle'
                                      }}
                                    />
                                  )}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {showFrequency && (
                                    <Chip
                                      label={`Frequency: ${freq}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {/* PHA-866: org hides target dates ⇒ always show "Due: N/A" */}
                                  {noTargetDatesEnabled ? (
                                    <Chip
                                      label="Due: N/A"
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : task.due_date && (
                                    <Chip
                                      label={`Due: ${formatDate(task.due_date)}`}
                                      size="small"
                                      variant="outlined"
                                      color={taskOverdue ? 'error' : 'warning'}
                                    />
                                  )}
                                  {task.completed && task.completed_at && (
                                    <Chip
                                      label={`Completed: ${formatDate(task.completed_at)}`}
                                      size="small"
                                      variant="filled"
                                      color="success"
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        };

                        return (
                          <Accordion sx={{ mt: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Tasks ({carePlan.tasks.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Stack spacing={2}>
                                {clinicianTasks.length > 0 && (
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: '6px 12px', bgcolor: '#e3f2fd', borderRadius: 1, borderLeft: '4px solid #1976d2' }}>
                                      <Typography sx={{ fontWeight: 700, color: '#1565c0', fontSize: '0.875rem' }}>Clinician Tasks</Typography>
                                      <Chip label={clinicianTasks.length} size="small" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, height: 20, fontSize: '0.75rem' }} />
                                    </Box>
                                    <Stack spacing={1}>
                                      {clinicianTasks.map((task, i) => renderTaskCard(task, i))}
                                    </Stack>
                                  </Box>
                                )}
                                {patientTasks.length > 0 && (
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: '6px 12px', bgcolor: '#e8f5e9', borderRadius: 1, borderLeft: '4px solid #388e3c' }}>
                                      <Typography sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '0.875rem' }}>Patient Tasks</Typography>
                                      <Chip label={patientTasks.length} size="small" sx={{ bgcolor: '#388e3c', color: 'white', fontWeight: 600, height: 20, fontSize: '0.75rem' }} />
                                    </Box>
                                    <Stack spacing={1}>
                                      {patientTasks.map((task, i) => renderTaskCard(task, i))}
                                    </Stack>
                                  </Box>
                                )}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })()}

                      {/* Surveys Section */}
                      {carePlan.surveys && carePlan.surveys.length > 0 && (
                        <Accordion sx={{ mt: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              Surveys ({carePlan.surveys.length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1}>
                              {[...carePlan.surveys]
                                .sort((a, b) => {
                                  // Ensure Daily surveys appear before Weekly surveys
                                  const aIsDaily = a.name?.toLowerCase().includes('daily');
                                  const bIsDaily = b.name?.toLowerCase().includes('daily');
                                  if (aIsDaily && !bIsDaily) return -1;
                                  if (!aIsDaily && bIsDaily) return 1;
                                  return 0;
                                })
                                .map((survey, surveyIndex) => (
                                <Box key={surveyIndex} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 'medium',
                                      mb: 1,
                                      color: 'primary.main',
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                    onClick={() => setCurrentTab(PATIENT_DETAIL_TAB_INDEX.SURVEYS)}
                                  >
                                    {survey.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                      label={`Frequency: ${formatFrequency(survey.frequency)}`} 
                                      size="small" 
                                      variant="outlined" 
                                    />
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
                {(() => {
                  // PHA-1142: readings no goal is measured by (heart rate on a diabetes plan,
                  // height, ...). They still arrive from the EHR and still need acknowledging,
                  // so they get a table of their own rather than nowhere to go.
                  const covered = new Set<string>();
                  carePlansToRender.forEach((plan) =>
                    (plan.goals || []).forEach((g: any) =>
                      (goalVitalGroup(g?.measure)?.documentTypes || []).forEach((t) => covered.add(t))
                    )
                  );
                  const readings = (observationHistory?.readings || []).filter((r) => !covered.has(r.document_type));
                  if (readings.length === 0) return null;
                  const rows = buildVitalTableRows(readings, Array.from(new Set(readings.map((r) => r.document_type))));
                  const pendingCount = rows.filter((r) => r.pending.length > 0).length;
                  return (
                    <Accordion
                      id="ehr-other-readings"
                      expanded={otherReadingsOpen}
                      onChange={(_e, open) => setOtherReadingsOpen(open)}
                      variant="outlined"
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          Other vitals and labs from the EHR ({rows.length})
                        </Typography>
                        {pendingCount > 0 && (
                          <Chip label={`${pendingCount} new`} size="small" color="warning" sx={{ ml: 2 }} />
                        )}
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Readings not tied to any goal on this care plan.
                        </Typography>
                        <GoalVitalsTable
                          rows={rows}
                          onAcknowledge={observationHistory?.review_available ? acknowledgeVitalRows : undefined}
                          busyKeys={vitalAckBusy}
                        />
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}
              </Stack>
            ) : draftCarePlanLoadFailed ? (
              /* PHA-951: the fetch failed — say so. Rendering the "no care plans" copy here
                 tells the clinician the patient has no plan, which may be flatly untrue. */
              <Alert
                severity="error"
                sx={{ my: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => { void loadDraftCarePlansForView(); }}>
                    Retry
                  </Button>
                }
              >
                Couldn&apos;t load this patient&apos;s care plan. This is a temporary connection problem —
                nothing has been deleted. Retry to load it again.
              </Alert>
            ) : draftCarePlanLoadPending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 4 }}>
                <CircularProgress size={20} />
                <Typography color="text.secondary">Loading care plans…</Typography>
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No care plans available for this patient.
              </Typography>
            )}

            {/* PHA-795 (flow redesign): dedicated "Voice of the patient" care-plan card.
                Presentation-only — sourced from the patient's approved PCO tables
                (person_centered_goals / gas_definitions / pcm_barriers) + the VoP survey,
                rendered with the SAME card + accordion look as the disease care plans
                (not merged into the General card, not a disease CPD row). */}
            {((Array.isArray(patient?.person_centered_goals) && patient.person_centered_goals.length > 0) ||
              (Array.isArray(patient?.pcm_barriers) && patient.pcm_barriers.length > 0)) && (() => {
              const pcoGoals = [...((patient?.person_centered_goals as any[]) ?? [])]
                .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
              const pcoBarriers = [...((patient?.pcm_barriers as any[]) ?? [])]
                .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
              const gasDefs = (patient?.gas_definitions as any[]) ?? [];
              // PHA-850: barriers shown in the context of their goal; `unassigned`
              // covers barriers not attributable to a single goal (multi-goal, no link).
              const { perGoal: pcoPerGoal, unassigned: pcoUnassignedBarriers } =
                groupBarriersByGoal(pcoGoals, pcoBarriers);
              // PHA-837: Voice-of-the-Patient GAS questions are now folded into the
              // cadence-matched Plan Check-In survey (no dedicated "Voice of the Patient"
              // survey). Surface whichever survey carries the GAS tracking questions —
              // identified by the `likert` data_type, which is used only for GAS/VoP.
              const vopSurveys = ((patient?.most_recent_surveys as any[]) ?? []).filter(
                (s: any) =>
                  (s.name ?? '').toLowerCase().includes('voice of the patient') ||
                  ((s.questions as any[]) ?? []).some(
                    (q: any) => (q.data_type ?? '') === 'likert'
                  )
              );
              return (
                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Voice of the patient
                    </Typography>

                    {/* Goals */}
                    {pcoGoals.length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            Goals ({pcoGoals.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1.5}>
                            {pcoPerGoal.map(({ goal, barriers }: any, gi: number) => {
                              const gasDef = gasDefs.find((d: any) => d.pco_ref === goal.pco_ref);
                              const trackingLabel = goal.tracking_question || gasDef?.level_zero || null;
                              return (
                                <Box key={goal.pco_ref ?? gi}>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {goal.goal_text}
                                  </Typography>
                                  {trackingLabel && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      How we track progress: {trackingLabel} (measured on a 5-point scale)
                                    </Typography>
                                  )}
                                  {/* PHA-850: this goal's barriers, nested in context. */}
                                  {barriers.length > 0 && (
                                    <Box sx={{ mt: 0.5, pl: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 'medium' }}>
                                        Barriers we&apos;re working on together:
                                      </Typography>
                                      {barriers.map((barrier: any, bi: number) => (
                                        <Typography key={barrier.barrier_ref ?? bi} variant="body2" sx={{ pl: 1 }}>
                                          - {barrier.barrier_text}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                  <Link
                                    component="button"
                                    type="button"
                                    variant="caption"
                                    onClick={() => setCurrentTab(PATIENT_DETAIL_TAB_INDEX.ANSWERS)}
                                  >
                                    View tracking in Answers
                                  </Link>
                                </Box>
                              );
                            })}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Assessment Questions — the GAS tracking questions (one per goal) */}
                    {pcoGoals.length > 0 && (
                      <Accordion sx={{ mt: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            Assessment Questions ({pcoGoals.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1}>
                            {pcoGoals.map((goal: any, gi: number) => {
                              const gasDef = gasDefs.find((d: any) => d.pco_ref === goal.pco_ref);
                              const tq = goal.tracking_question || gasDef?.level_zero ||
                                `How well are you managing your goal: ${goal.goal_text}?`;
                              return (
                                <Box key={`gas-q-${goal.pco_ref ?? gi}`} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    {tq}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip label="Type: GAS (5-point scale)" size="small" variant="outlined" />
                                    <Chip label="Voice of the Patient" size="small" color="primary" variant="outlined" />
                                  </Box>
                                </Box>
                              );
                            })}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Barriers — PHA-850: only those not attributable to a single goal
                        (multi-goal + no stored link). Goal-linked barriers render nested
                        under their goal in the Goals accordion above. */}
                    {pcoUnassignedBarriers.length > 0 && (
                      <Accordion sx={{ mt: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            Barriers ({pcoUnassignedBarriers.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={0.5}>
                            {pcoUnassignedBarriers.map((barrier: any, i: number) => (
                              <Typography key={barrier.barrier_ref ?? i} variant="body2">
                                • {barrier.barrier_text}
                              </Typography>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Surveys — the generated Voice of the Patient survey(s) */}
                    {vopSurveys.length > 0 && (
                      <Accordion sx={{ mt: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            Surveys ({vopSurveys.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1}>
                            {vopSurveys.map((survey: any, si: number) => (
                              <Box key={survey.survey_id ?? si} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 'medium',
                                    mb: 1,
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                  onClick={() => setCurrentTab(PATIENT_DETAIL_TAB_INDEX.SURVEYS)}
                                >
                                  {survey.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Chip
                                    label={`Frequency: ${formatFrequency(survey.frequency)}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* PHA-870: Care Plan Notes (renamed from "Care Plan Review" in PHA-1109 so it
                is not read as the header's "Next Review Date") — care-team-authored notes shown
                below the care plans (after Voice of the patient), and rendered on the
                Care Plan PDF as a section following "Your Care Plan in Details"
                (threaded via generateCarePlanPdf care_plan_reviews). */}
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" color="primary">
                    Care Plan Notes
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleOpenReviewModal}
                  >
                    Add Care Plan Note
                  </Button>
                </Box>
                {carePlanReviews.length > 0 ? (
                  <Stack spacing={1.5}>
                    {carePlanReviews.map((review) => (
                      <Box
                        key={review.id}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Note Date: {review.review_date ? isoToMaskedDate(review.review_date) : '—'}
                          </Typography>
                          <Box sx={{ flexShrink: 0 }}>
                            <IconButton
                              size="small"
                              aria-label="Edit note"
                              onClick={() => handleOpenEditReviewModal(review)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label="Delete note"
                              onClick={() => setDeletingReview(review)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                          <strong>Patient Comments:</strong>{' '}
                          {review.patient_comments?.trim() ? review.patient_comments : '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          <strong>Notes / Actions Taken:</strong>{' '}
                          {review.action_taken?.trim() ? review.action_taken : '—'}
                        </Typography>
                        {/* PHA-1149: author + time, and the last editor when it differs. */}
                        <Box sx={{ mt: 1 }}>
                          <EventProvenance label="Added by" name={review.added_by_name} at={review.added_ts} />
                          {review.was_edited && (
                            <EventProvenance label="Edited by" name={review.edited_by_name} at={review.created_ts} />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    No care plan notes yet.
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* PHA-397: Draft care plan review modal */}
            <Dialog
              // The goal/task/question editors inside are Radix dialogs that portal to
              // document.body, i.e. outside this MUI Dialog. Without this, MUI's focus trap
              // yanks focus back and their inputs cannot be typed into.
              disableEnforceFocus
              open={showDraftReviewModal}
              onClose={() => setShowDraftReviewModal(false)}
              maxWidth="lg"
              fullWidth
              PaperProps={{ sx: { minHeight: '80vh' } }}
            >
              <DialogTitle>
                Review Care Plan Updates
                <IconButton
                  aria-label="close"
                  onClick={() => setShowDraftReviewModal(false)}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {/* CarePlanApproval is built from Radix Themes components. This MUI Dialog
                    portals outside the app's <Theme>, so without re-wrapping here every
                    Radix control inside renders unstyled — see the import comment. */}
                <Theme accentColor="blue" grayColor="slate" radius="medium" appearance="light">
                {pendingDraftCarePlans && draftPlanGenerated ? (
                  <CarePlanApproval
                    carePlanData={pendingDraftCarePlans}
                    patientId={patient.patient_id}
                    onApprove={handleDraftCarePlanApproval}
                    onDeclineConsent={() => setShowDraftReviewModal(false)}
                    onGenerateCarePlan={generateCarePlanDraft}
                    // PHA-1162: a VoP goal saved from the approval step — refresh the patient so
                    // the goals reach the section, the approve check and the draft PDF.
                    onVoiceOfPatientSaved={async () => {
                      if (!onPatientUpdate) return;
                      onPatientUpdate(await patientApi.get(patient.patient_id));
                    }}
                    carePlanLoading={false}
                    carePlanGenerated={draftPlanGenerated}
                    approvedProblems={(patient.problems || []) as unknown as ProblemApprovalItem[]}
                    noTargetDatesEnabled={noTargetDatesEnabled}
                    carePlanReviewMonths={carePlanReviewMonths}
                    patientForPdf={{
                      first_name: patient.first_name,
                      last_name: patient.last_name,
                      date_of_birth: patient.date_of_birth,
                      patient_id: patient.patient_id,
                      apcm_tier: patient.apcm_tier,
                      has_rpm_device: (patient as any).has_rpm_device,
                      language: patient.language,
                      logo_path: patient.logo_path,
                      organization_name: patient.organization_name,
                      // PHA-832: carry person-centered data into the draft PDF.
                      voice_of_patient_enabled: voiceOfPatientEnabled,
                      // PHA-866: hide target dates in the draft PDF too.
                      no_target_dates_enabled: noTargetDatesEnabled,
                      // PHA-865: carry full problem/medication lists into the draft PDF (PCMH).
                      problems: patient.problems,
                      medications: patient.medications,
                      person_centered_goals: (patient as any).person_centered_goals,
                      pcm_barriers: (patient as any).pcm_barriers,
                      gas_definitions: (patient as any).gas_definitions,
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography>Loading draft care plan…</Typography>
                  </Box>
                )}
                </Theme>
              </DialogContent>
            </Dialog>
          </Box>
        )}

        {/* Surveys Tab */}
        {currentTab === 4 && (
          <Box sx={{ mt: 2 }}>
            {patient.most_recent_surveys && patient.most_recent_surveys.length > 0 ? (
              <Stack spacing={2}>
                {[...patient.most_recent_surveys]
                  .sort((a: any, b: any) => {
                    const parseSortKey = (s: any) => {
                      const freq = (s.frequency || '').toLowerCase();
                      const name = (s.name || '').toLowerCase();
                      if (freq === 'unscheduled' || name.includes('unscheduled') || name.includes('on-demand')) return { type: 3, day: 0, hour: 0, minute: 0 };
                      // Try parsing cron (5-part format)
                      const parts = freq.split(' ');
                      if (parts.length === 5) {
                        const minute = parseInt(parts[0]) || 0;
                        const hour = parseInt(parts[1]) || 0;
                        if (parts[4] === '*') return { type: 0, day: 0, hour, minute }; // daily
                        const day = parseInt(parts[4]) || 0;
                        return { type: 1, day: day === 0 ? 7 : day, hour, minute }; // weekly
                      }
                      // Fallback: detect from name and parse time from name pattern "Daily HH:MM" / "Weekly Day HH:MM"
                      const timeMatch = name.match(/(\d{1,2}):(\d{2})/);
                      const fHour = timeMatch ? parseInt(timeMatch[1]) : 0;
                      const fMin = timeMatch ? parseInt(timeMatch[2]) : 0;
                      const dayNames: Record<string, number> = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7 };
                      const dayMatch = Object.entries(dayNames).find(([d]) => name.includes(d));
                      if (name.includes('daily') || freq.includes('daily')) return { type: 0, day: 0, hour: fHour, minute: fMin };
                      if (name.includes('weekly') || freq.includes('weekly')) return { type: 1, day: dayMatch ? dayMatch[1] : 0, hour: fHour, minute: fMin };
                      return { type: 2, day: 0, hour: 0, minute: 0 };
                    };
                    const ka = parseSortKey(a);
                    const kb = parseSortKey(b);
                    if (ka.type !== kb.type) return ka.type - kb.type;
                    if (ka.day !== kb.day) return ka.day - kb.day;
                    if (ka.hour !== kb.hour) return ka.hour - kb.hour;
                    return ka.minute - kb.minute;
                  })
                  .map((survey, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="h6">
                            {(survey.name || '').replace(/\bAPCM\s*/gi, '')}
                          </Typography>
                          {survey.is_custom && !survey.is_apcm_hospitalization && (
                            <Chip label="Custom" size="small" color="secondary" />
                          )}
                        </Box>
                        {/* PHA-497: Show schedule subtitle for legacy surveys (new format has time in title like "Daily 10:00") */}
                        {survey.frequency && survey.frequency !== 'unscheduled' && !(/\d{1,2}:\d{2}\s*(AM|PM)/i.test(survey.name || '')) && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontStyle: 'italic' }}>
                            Scheduled: {formatFrequency(survey.frequency)}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {survey.answers && survey.answers.length > 0
                            ? `${survey.answers.length} answer${survey.answers.length !== 1 ? 's' : ''} collected`
                            : 'No answers collected'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          Survey ID: {survey.survey_id}
                        </Typography>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Send />}
                            onClick={() => handleOpenSendSurveyDialog(survey.survey_id)}
                            size="small"
                          >
                            Send Survey
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<OpenInNew />}
                            onClick={() => handleShowSurveyLink(survey.survey_id)}
                            disabled={loadingLinks[survey.survey_id]}
                            size="small"
                          >
                            {loadingLinks[survey.survey_id] ? 'Loading...' : 'Show survey'}
                          </Button>
                          {voiceEnabled && (
                            <Button
                              variant="outlined"
                              color="secondary"
                              startIcon={callingPatient ? <CircularProgress size={16} /> : <Phone />}
                              onClick={() => handleOpenVapiCallDialog(survey.survey_id, survey.name)}
                              disabled={callingPatient}
                              size="small"
                            >
                              {callingPatient ? 'Calling...' : 'Call Patient (AI Assistant)'}
                            </Button>
                          )}
                          {survey.is_custom && !survey.is_apcm_hospitalization && (
                            <>
                              <Button
                                variant="outlined"
                                color="inherit"
                                startIcon={<Edit />}
                                onClick={() => handleEditCustomSurvey(survey)}
                                size="small"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Delete />}
                                onClick={() => handleDeleteCustomSurvey(survey.survey_id)}
                                size="small"
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </Stack>

                        {survey.is_custom && !survey.is_apcm_hospitalization && (!survey.answers || survey.answers.length === 0) ? (
                          null
                        ) : survey.created_at ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Completed: {formatDateTime(survey.created_at)}
                          </Typography>
                        ) : survey.released_at ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Sent: {formatDateTime(survey.released_at)}
                          </Typography>
                        ) : null}
                      </Box>
                      
                      {survey.answers && survey.answers.length > 0 && (
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle2">
                              View Answers ({survey.answers.filter(a => !isConditionallySkipped(a)).length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1}>
                              {survey.answers.filter(a => !isConditionallySkipped(a)).map((answer, answerIndex) => (
                                <Box key={answerIndex} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    Q: {answer.prompt}
                                    {getCollectionMethodIcon(answer.raw_data)}
                                  </Typography>
                                  <Typography variant="body2" color="primary">
                                    {/* PHA-992: fall back to raw_answer when the cast produced
                                        nothing, matching the sibling render paths below. */}
                                    A: {formatAnswer(answer.cast_answer ?? answer.raw_answer)}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Voice Call Transcript & Recording */}
                      {renderVoiceCallTranscript(voiceCallLogIdOf(survey.answers))}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No recent surveys available for this patient.
              </Typography>
            )}

          </Box>
        )}

        {/* Patient Outreach Tab */}
        {currentTab === 5 && (
          <Box sx={{ mt: 2 }}>
            {patient.custom_surveys && patient.custom_surveys.length > 0 ? (
              <Stack spacing={2}>
                {[...patient.custom_surveys]
                  .sort((a, b) => {
                    if (a.created_at && b.created_at) {
                      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                    }
                    return 0;
                  })
                  .map((survey, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6">
                            {survey.name}
                          </Typography>
                          <Chip label="Custom" size="small" color="secondary" />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {survey.answers && survey.answers.length > 0
                            ? `${survey.answers.length} answer${survey.answers.length !== 1 ? 's' : ''} collected`
                            : 'No answers collected'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          Survey ID: {survey.survey_id}
                        </Typography>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Send />}
                            onClick={() => handleOpenSendSurveyDialog(survey.survey_id)}
                            size="small"
                          >
                            Send Survey
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<OpenInNew />}
                            onClick={() => handleShowSurveyLink(survey.survey_id)}
                            size="small"
                          >
                            Show survey
                          </Button>
                          {voiceEnabled && (
                            <Button
                              variant="outlined"
                              color="secondary"
                              startIcon={<Phone />}
                              onClick={() => handleOpenVapiCallDialog(survey.survey_id, survey.name)}
                              size="small"
                            >
                              Call Patient (AI Assistant)
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={() => handleEditCustomSurvey(survey)}
                            size="small"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => handleDeleteCustomSurvey(survey.survey_id)}
                            size="small"
                          >
                            Delete
                          </Button>
                        </Stack>

                        {survey.created_at && (!survey.answers || survey.answers.length === 0) ? (
                          null
                        ) : survey.created_at ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Completed: {formatDateTime(survey.created_at)}
                          </Typography>
                        ) : null}
                      </Box>

                      {survey.answers && survey.answers.length > 0 && (
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle2">
                              View Answers ({survey.answers.filter(a => !isConditionallySkipped(a)).length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1}>
                              {survey.answers.filter(a => !isConditionallySkipped(a)).map((answer, answerIndex) => (
                                <Box key={answerIndex} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    Q: {answer.prompt}
                                    {getCollectionMethodIcon(answer.raw_data)}
                                  </Typography>
                                  <Typography variant="body2" color="primary">
                                    {/* PHA-992: fall back to raw_answer when the cast produced
                                        nothing, matching the sibling render paths below. */}
                                    A: {formatAnswer(answer.cast_answer ?? answer.raw_answer)}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No custom surveys created for this patient.
              </Typography>
            )}

            {/* Create Custom Survey Button */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Add />}
                onClick={handleOpenCustomSurveyModal}
              >
                Create Custom Survey
              </Button>
            </Box>
          </Box>
        )}

        {/* Event Log Tab */}
        {currentTab === 6 && (
          <Box sx={{ mt: 2 }}>
            {/* Filter Controls */}
            <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label="All Events"
                onClick={() => setEventLogFilter('all')}
                color={eventLogFilter === 'all' ? 'primary' : 'default'}
                variant={eventLogFilter === 'all' ? 'filled' : 'outlined'}
              />
              <Chip 
                label="⏱️ Manual Time"
                onClick={() => setEventLogFilter('manual')}
                color={eventLogFilter === 'manual' ? 'primary' : 'default'}
                variant={eventLogFilter === 'manual' ? 'filled' : 'outlined'}
              />
              <Chip 
                label="🕐 Auto-tracked"
                onClick={() => setEventLogFilter('auto')}
                color={eventLogFilter === 'auto' ? 'primary' : 'default'}
                variant={eventLogFilter === 'auto' ? 'filled' : 'outlined'}
              />
              <Chip 
                label="📋 Surveys"
                onClick={() => setEventLogFilter('surveys')}
                color={eventLogFilter === 'surveys' ? 'primary' : 'default'}
                variant={eventLogFilter === 'surveys' ? 'filled' : 'outlined'}
              />
            </Box>

            {loadingActionReport ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : getGroupedEventLog().length > 0 ? (
              <Stack spacing={2}>
                {getGroupedEventLog().map((item, index) => {
                  if (item.kind === 'survey_group') {
                    // Combined survey card
                    const group = item;
                    // PHA-1261: a response only staff entered (e.g. read out over the phone) is
                    // not the patient responding to the SMS — badge it apart.
                    const onlyStaffEntered =
                      group.submissions.length > 0 && group.submissions.every((s) => s.method === 'staff_entry');
                    const statusBadge = !group.hasResponse
                      ? { icon: '⏳', label: 'Awaiting Response', bgcolor: '#fff8e1', color: '#f57f17' }
                      : onlyStaffEntered
                        ? { icon: '🎧', label: 'Entered by Care Team', bgcolor: '#efebe9', color: '#5d4037' }
                        : { icon: '✅', label: 'Responded', bgcolor: '#e8f5e9', color: '#2e7d32' };
                    const method = group.sentEvent?.method || group.submissions[0]?.method;

                    return (
                      <Card key={`group-${group.groupKey}`} variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle1" color="primary">
                                  {group.surveyName}
                                </Typography>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    bgcolor: statusBadge.bgcolor,
                                    color: statusBadge.color,
                                  }}
                                >
                                  {statusBadge.icon} {statusBadge.label}
                                </Box>
                              </Box>
                              {method && (
                                <Typography variant="body2" color="text.secondary">
                                  Method: {formatContactMethod(method)}
                                </Typography>
                              )}
                              {group.sentEvent && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  Sent: {formatDateTime(group.sentEvent.created_at)}
                                  {group.sentEvent.user_name ? ` by ${group.sentEvent.user_name}` : ''}
                                </Typography>
                              )}
                              {group.submissions.map((sub, subIdx) => (
                                <Typography key={subIdx} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  Response {group.submissions.length > 1 ? `#${subIdx + 1}` : ''}: {formatDateTime(sub.created_at)}
                                  {sub.method === 'voice_call' && ' (Voice Call)'}
                                  {sub.method === 'voice_call' && sub.user_name ? ` — call started by ${sub.user_name}` : ''}
                                  {sub.method === 'staff_entry' &&
                                    ` — entered by ${sub.user_name || 'care team'} on the patient's behalf`}
                                </Typography>
                              ))}
                            </Box>
                          </Box>

                          {/* Answers accordions for each submission */}
                          {group.submissions.map((sub, subIdx) => {
                            if (!sub.answers || sub.answers.length === 0) return null;
                            return (
                              <React.Fragment key={subIdx}>
                                <Accordion sx={subIdx > 0 ? { mt: 1 } : undefined}>
                                  <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="subtitle2">
                                      {group.submissions.length > 1
                                        ? `Response #${subIdx + 1} (${sub.answers.filter(a => a.prompt && !isConditionallySkipped(a)).length} answers)`
                                        : `Responses (${sub.answers.filter(a => a.prompt && !isConditionallySkipped(a)).length})`}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Stack spacing={1}>
                                      {sub.answers.filter(a => a.prompt && !isConditionallySkipped(a)).map((answer, answerIndex) => {
                                        const isVoiceCall = answer.raw_data?.source === 'voice_call';
                                        const voiceCallLogId = answer.raw_data?.voice_call_log_id;
                                        return (
                                          <Box key={answerIndex} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                              Q: {answer.prompt}
                                              {isVoiceCall ? (
                                                <MuiTooltip
                                                  title={
                                                    voiceCallLogId
                                                      ? 'View call transcript and recording'
                                                      : 'No transcript or recording data available'
                                                  }
                                                >
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA)}
                                                    sx={{
                                                      color: '#9c27b0',
                                                      padding: '2px',
                                                      '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' }
                                                    }}
                                                  >
                                                    <PhoneInTalk sx={{ fontSize: 16 }} />
                                                  </IconButton>
                                                </MuiTooltip>
                                              ) : (
                                                getCollectionMethodIcon(answer.raw_data)
                                              )}
                                            </Typography>
                                            <Typography variant="body2" color="primary">
                                              A: {formatAnswer(answer.cast_answer ?? answer.raw_answer)}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Stack>
                                  </AccordionDetails>
                                </Accordion>
                                {/* PHA-1234: the call's full transcript, under the answers it produced */}
                                {renderVoiceCallTranscript(voiceCallLogIdOf(sub.answers), { mt: 1 })}
                              </React.Fragment>
                            );
                          })}
                        </CardContent>
                      </Card>
                    );
                  }

                  // Standalone card (time_tracking, survey_failed, survey_deleted, etc.)
                  const action = item.event;
                  const isAutoTracked = (action as any).isAutoTracked;

                  const getStandaloneStatusBadge = () => {
                    switch (action.type) {
                      case 'survey sent':
                        return { icon: '📩', label: 'Survey Sent', bgcolor: '#e3f2fd', color: '#1976d2' };
                      case 'survey failed':
                        return { icon: '⚠️', label: 'Survey Not Sent', bgcolor: '#fff3e0', color: '#e65100' };
                      case 'survey deleted':
                        return { icon: '🗑️', label: 'Survey Deleted', bgcolor: '#ffebee', color: '#d32f2f' };
                      case 'voice call':
                        return { icon: '📞', label: `Voice AI Call: ${voiceCallOutcomeLabel(action.call_outcome)}`, bgcolor: '#f3e5f5', color: '#7b1fa2' };
                      case 'time_tracking':
                        if (isAutoTracked) {
                          return { icon: '🕐', label: 'Auto-tracked', bgcolor: '#f5f5f5', color: '#757575' };
                        }
                        return { icon: '⏱️', label: 'Manual Time', bgcolor: '#e3f2fd', color: '#1976d2' };
                      case 'answers submitted':
                      default:
                        return { icon: '✅', label: 'Survey Submitted', bgcolor: '#e8f5e9', color: '#2e7d32' };
                    }
                  };

                  const statusBadgeStyles = getStandaloneStatusBadge();

                  return (
                    <Card key={`standalone-${index}`} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" color="primary">
                                {action.name}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  bgcolor: statusBadgeStyles.bgcolor,
                                  color: statusBadgeStyles.color,
                                }}
                              >
                                {statusBadgeStyles.icon} {statusBadgeStyles.label}
                              </Box>
                            </Box>
                            {action.method && (
                              <Typography variant="body2" color="text.secondary">
                                Method: {formatContactMethod(action.method)}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(action.created_at)}
                            </Typography>
                            {/* PHA-1149: who performed the event */}
                            <EventProvenance name={action.user_name} testId="event-log-user" />

                            {/* PHA-1231: a call with no saved answers, and why */}
                            {action.type === 'voice call' && (
                              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }} data-testid="event-log-voice-call">
                                <Typography variant="body2" color="text.secondary">
                                  No answers saved{action.ended_reason || action.error ? ` (${action.error || action.ended_reason})` : ''}
                                </Typography>
                              </Box>
                            )}

                            {/* Failed survey error reason */}
                            {action.type === 'survey failed' && action.error && (
                              <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
                                <Typography variant="body2" color="error">
                                  Reason: {surveyFailureReason(action.error)}
                                </Typography>
                              </Box>
                            )}

                            {/* Time tracking specific fields */}
                            {action.type === 'time_tracking' && (
                              <Box sx={{ mt: 1 }}>
                                {action.duration_seconds !== undefined && (
                                  <Typography variant="body2" color="text.secondary">
                                    Duration: {formatDuration(action.duration_seconds)}
                                  </Typography>
                                )}
                                {action.notes && (
                                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                      Notes:
                                    </Typography>
                                    <Typography variant="body2">
                                      {action.notes}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Show answers accordion only if answers exist */}
                        {action.answers && action.answers.length > 0 && (
                          <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography variant="subtitle2">
                                Responses ({action.answers.filter(a => !isConditionallySkipped(a)).length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Stack spacing={1}>
                                {action.answers.filter(a => a.prompt && !isConditionallySkipped(a)).map((answer, answerIndex) => {
                                  const isVoiceCall = answer.raw_data?.source === 'voice_call';
                                  const voiceCallLogId = answer.raw_data?.voice_call_log_id;
                                  return (
                                    <Box key={answerIndex} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                        Q: {answer.prompt}
                                        {isVoiceCall ? (
                                          <MuiTooltip
                                            title={
                                              voiceCallLogId
                                                ? 'View call transcript and recording'
                                                : 'No transcript or recording data available'
                                            }
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={() => handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA)}
                                              sx={{
                                                color: '#9c27b0',
                                                padding: '2px',
                                                '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' }
                                              }}
                                            >
                                              <PhoneInTalk sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </MuiTooltip>
                                        ) : (
                                          getCollectionMethodIcon(answer.raw_data)
                                        )}
                                      </Typography>
                                      <Typography variant="body2" color="primary">
                                        A: {formatAnswer(answer.cast_answer ?? answer.raw_answer)}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        )}

                        {/* PHA-1234: the call's full transcript — for a call with no saved
                            answers too, where it is the only record of what was said */}
                        {renderVoiceCallTranscript(
                          action.type === 'voice call' ? action.voice_call_log_id : voiceCallLogIdOf(action.answers),
                          action.answers && action.answers.length > 0 ? { mt: 1 } : undefined
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No events recorded for this patient.
              </Typography>
            )}
          </Box>
        )}

        {/* Answers Tab */}
        {currentTab === 7 && (
          <Box sx={{ mt: 2 }}>

            {/* PHA-833 (WS5): Person-centered goal tracking (GAS)
                Renders one card per gas_definition with scale anchors and a response-history
                line chart. Additive — appears above the existing per-prompt answer cards. */}
            {(() => {
              const gasDefs = Array.isArray((patient as any).gas_definitions)
                ? ((patient as any).gas_definitions as Array<{
                    pco_ref: string;
                    goal_text?: string | null;
                    level_minus2?: string | null;
                    level_minus1?: string | null;
                    level_zero?: string | null;
                    level_plus1?: string | null;
                    level_plus2?: string | null;
                    baseline_level?: number | null;
                  }>)
                : [];

              if (gasDefs.length === 0) return null;

              const pcoGoals = Array.isArray((patient as any).person_centered_goals)
                ? ((patient as any).person_centered_goals as Array<{ pco_ref: string; goal_text: string }>)
                : [];
              const goalTitleMap = new Map(pcoGoals.map(g => [g.pco_ref, g.goal_text]));

              const measurementPoints = Array.isArray((patient as any).gas_measurement_points)
                ? ((patient as any).gas_measurement_points as Array<{
                    pco_ref: string;
                    measured_at: string;
                    level: number;
                    source?: string | null;
                  }>)
                : [];

              const GAS_LEVEL_STYLES: Record<number, { bg: string; color: string }> = {
                [-2]: { bg: '#ffebee', color: '#c62828' },
                [-1]: { bg: '#fff3e0', color: '#e65100' },
                [0]:  { bg: '#eceff1', color: '#37474f' },
                [1]:  { bg: '#e8f5e9', color: '#2e7d32' },
                [2]:  { bg: '#c8e6c9', color: '#1b5e20' },
              };

              const LEVEL_FIELD: Record<number, 'level_minus2' | 'level_minus1' | 'level_zero' | 'level_plus1' | 'level_plus2'> = {
                [-2]: 'level_minus2',
                [-1]: 'level_minus1',
                [0]:  'level_zero',
                [1]:  'level_plus1',
                [2]:  'level_plus2',
              };

              const fmtLevel = (lvl: number) => lvl > 0 ? `+${lvl}` : String(lvl);

              return (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Person-centered goal tracking (GAS)
                  </Typography>
                  <Stack spacing={2}>
                    {gasDefs.map(def => {
                      // goal_text is denormalized onto gas_definitions; fall back to the
                      // person_centered_goals lookup, then the pco_ref string itself.
                      const goalTitle = def.goal_text || goalTitleMap.get(def.pco_ref) || def.pco_ref;

                      const points = measurementPoints
                        .filter(p => p.pco_ref === def.pco_ref)
                        .sort((a, b) => new Date(a.measured_at).getTime() - new Date(b.measured_at).getTime());

                      const chartData = points.map(p => ({
                        month: format(new Date(p.measured_at), 'MMM'),
                        level: p.level,
                      }));

                      const latestPoint = points.length > 0 ? points[points.length - 1] : null;

                      // Compare last two points to derive trend label.
                      let trendText = '';
                      if (points.length >= 2) {
                        const last = points[points.length - 1].level;
                        const prev = points[points.length - 2].level;
                        if (last > prev) trendText = '↑ improving';
                        else if (last < prev) trendText = '↓ declining';
                        else trendText = '→ steady';
                      } else if (points.length === 1) {
                        trendText = '→ baseline';
                      }

                      return (
                        <Card key={def.pco_ref} variant="outlined">
                          <CardContent>
                            {/* Header: compact pco_ref identifier chip + readable goal title */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <Chip
                                label={def.pco_ref}
                                size="small"
                                sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }}
                              />
                              <Typography sx={{ fontWeight: 500, fontSize: '0.9375rem' }}>
                                {goalTitle}
                              </Typography>
                            </Box>

                            {/* Two-column layout: scale anchors (left) + response history (right) */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>

                              {/* ── Left: Scale anchor table ── */}
                              <Box sx={{ flex: '1 1 240px', minWidth: 200 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    mb: 1,
                                    display: 'block',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                  }}
                                >
                                  Scale anchors
                                </Typography>
                                {/* PHA-905: highest level first so the anchors read top-to-bottom in
                                    the same direction as the chart's Y axis (+2 at top, −2 at bottom). */}
                                {([2, 1, 0, -1, -2] as const).map(lvl => {
                                  const { bg, color } = GAS_LEVEL_STYLES[lvl];
                                  const anchorText = def[LEVEL_FIELD[lvl]];
                                  const isBaseline = Number(def.baseline_level) === lvl;
                                  return (
                                    <Box key={lvl} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                                      <Box
                                        sx={{
                                          bgcolor: bg,
                                          color,
                                          px: 0.75,
                                          py: 0.125,
                                          borderRadius: 0.5,
                                          fontSize: '0.7rem',
                                          fontWeight: 700,
                                          fontFamily: 'monospace',
                                          whiteSpace: 'nowrap',
                                          flexShrink: 0,
                                          lineHeight: '1.6',
                                        }}
                                      >
                                        GAS {fmtLevel(lvl)}
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: anchorText ? 'text.primary' : 'text.disabled',
                                          fontStyle: isBaseline ? 'italic' : 'normal',
                                          fontSize: '0.8125rem',
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {anchorText || '—'}
                                        {isBaseline && (
                                          <Box component="span" sx={{ color: 'text.secondary' }}> (baseline)</Box>
                                        )}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>

                              {/* ── Right: Response history chart ── */}
                              <Box sx={{ flex: '1 1 240px', minWidth: 200 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    mb: 1,
                                    display: 'block',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                  }}
                                >
                                  Response history
                                </Typography>
                                {chartData.length > 0 ? (
                                  <>
                                    <ResponsiveContainer width="100%" height={160}>
                                      <LineChart
                                        data={chartData}
                                        margin={{ top: 8, right: 16, left: -12, bottom: 4 }}
                                      >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                        <YAxis
                                          domain={[-2, 2]}
                                          ticks={[-2, -1, 0, 1, 2]}
                                          tickFormatter={(v: any) => fmtLevel(Number(v))}
                                          tick={{ fontSize: 11 }}
                                        />
                                        <Tooltip
                                          content={({ active, payload }: any) => {
                                            if (active && payload && payload.length) {
                                              const d = payload[0].payload;
                                              return (
                                                <Box
                                                  sx={{
                                                    bgcolor: 'white',
                                                    p: 1,
                                                    border: '1px solid #ccc',
                                                    borderRadius: 1,
                                                    boxShadow: 1,
                                                  }}
                                                >
                                                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                                    {d.month}
                                                  </Typography>
                                                  <Typography variant="caption">
                                                    GAS {fmtLevel(d.level)}
                                                  </Typography>
                                                </Box>
                                              );
                                            }
                                            return null;
                                          }}
                                        />
                                        <Line
                                          type="monotone"
                                          dataKey="level"
                                          stroke="#546e7a"
                                          strokeWidth={2}
                                          dot={{ r: 4, fill: '#546e7a' }}
                                          activeDot={{ r: 6 }}
                                        />
                                      </LineChart>
                                    </ResponsiveContainer>
                                    {latestPoint && (
                                      <Box sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                          Latest: {fmtLevel(latestPoint.level)} ({format(new Date(latestPoint.measured_at), 'MMM')})
                                          {trendText && (
                                            <Box component="span" sx={{ ml: 1.5 }}>
                                              · Trend: {trendText}
                                            </Box>
                                          )}
                                        </Typography>
                                      </Box>
                                    )}
                                  </>
                                ) : (
                                  <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', mt: 1 }}>
                                    No measurements yet
                                  </Typography>
                                )}
                              </Box>

                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })()}

            {mergedAnswersByPrompt && Object.keys(mergedAnswersByPrompt).length > 0 && (() => {
              // PHA-795: GAS tracking answers are rendered in the dedicated
              // "Person-centered goal tracking (GAS)" card above. Exclude their
              // prompts from this generic chart so the same answer isn't shown
              // twice (the generic chart also auto-scales its Y-axis, unlike the
              // GAS card's fixed [-2, 2] domain). The survey prompt key mirrors
              // backend _gas_question_prompt: the trimmed tracking_question
              // verbatim, else a deterministic fallback from goal_text. Gated on
              // gas_definitions so we only hide prompts the GAS card renders.
              const gasDefRefs = new Set(
                (Array.isArray((patient as any).gas_definitions) ? (patient as any).gas_definitions : [])
                  .map((d: any) => d?.pco_ref)
              );
              const gasPrompts = new Set(
                (Array.isArray((patient as any).person_centered_goals) ? (patient as any).person_centered_goals : [])
                  .filter((g: any) => gasDefRefs.has(g?.pco_ref))
                  .flatMap((g: any) => {
                    const tq = (g?.tracking_question || '').trim();
                    const base = (g?.goal_text || 'your goal').trim().replace(/\.+$/, '');
                    const fallback = `Your goal: ${base}. How are you progressing toward this goal?`;
                    return tq ? [tq, fallback] : [fallback];
                  })
              );
              return (
              <Stack spacing={2}>
                {Object.entries(mergedAnswersByPrompt)
                  .filter(([question]) =>
                    question !== 'What was the visit for?' &&
                    question !== 'What day did you go?' &&
                    !gasPrompts.has(question)
                  )
                  .sort(([qA, aA], [qB, aB]) => {
                    const scoreA = getAnswerSetUrgencyScore(qA, aA, {
                      hospitalizationQuestion: HOSPITALIZATION_QUESTION,
                      positiveHospitalizationAnswers: POSITIVE_HOSPITALIZATION_ANSWERS,
                      getAnswerValue,
                    });
                    const scoreB = getAnswerSetUrgencyScore(qB, aB, {
                      hospitalizationQuestion: HOSPITALIZATION_QUESTION,
                      positiveHospitalizationAnswers: POSITIVE_HOSPITALIZATION_ANSWERS,
                      getAnswerValue,
                    });
                    return scoreB - scoreA;
                  })
                  .map(([question, dateAnswers], index) => {
                  const answerSet = { question, ...dateAnswers };
                  const isNumeric = isNumericAnswerSet(answerSet);
                  const isBloodPressure = isBloodPressureAnswerSet(answerSet);
                  const isHospitalizationQuestion = question === HOSPITALIZATION_QUESTION;
                  const hasLegacyAnswers = isHospitalizationQuestion && Object.entries(dateAnswers).some(([, v]) => {
                    const a = getAnswerValue(v);
                    return typeof a === 'string' && (a as string).startsWith('yes_');
                  });
                  const chartData = isNumeric ? prepareChartData(answerSet) : [];
                  const bpChartData = isBloodPressure ? prepareBloodPressureChartData(answerSet)
                    .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) : [];
                  // Count device points in this answer set for the caption
                  const devicePointCount = Object.entries(dateAnswers)
                    .filter(([, v]: [string, any]) => v?.raw_data?.source === 'device').length;

                  return (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {answerSet.question}
                        </Typography>
                        
                        {isBloodPressure && bpChartData.length > 0 ? (
                          <Box sx={{ mt: 2, mb: 2 }}>
                            <ResponsiveContainer width="100%" height={400}>
                              <LineChart
                                data={bpChartData}
                                margin={{ top: 20, right: 30, left: 60, bottom: 60 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="displayDate"
                                  label={{ value: 'Date', position: 'insideBottom', offset: -10 }}
                                  angle={-45}
                                  textAnchor="end"
                                  height={80}
                                  padding={getXAxisPadding(bpChartData.length)}
                                />
                                <YAxis 
                                  label={{ value: 'mmHg', angle: -90, position: 'insideLeft' }}
                                  domain={[60, 200]}
                                  padding={{ top: 20, bottom: 10 }}
                                />
                                <Tooltip
                                  content={({ active, payload }: any) => {
                                    if (active && payload && payload.length) {
                                      const data = payload[0].payload;
                                      const isVoiceCall = data.rawData?.source === 'voice_call';
                                      return (
                                        <Box
                                          sx={{
                                            bgcolor: 'white',
                                            p: 1.5,
                                            border: '1px solid #ccc',
                                            borderRadius: 1,
                                            boxShadow: 1,
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                              {data.displayDate}
                                            </Typography>
                                            {getCollectionMethodIcon(data.rawData)}
                                          </Box>
                                          {data.isInvalid && (
                                            <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 'bold', mb: 0.5 }}>
                                              ⚠️ Values are out of range: "{data.rawInput}"
                                            </Typography>
                                          )}
                                          {data.systolic !== null && (
                                            <Typography variant="body2" sx={{ color: '#d32f2f' }}>
                                              Systolic: {data.systolic} mmHg
                                            </Typography>
                                          )}
                                          {data.diastolic !== null && (
                                            <Typography variant="body2" sx={{ color: '#1976d2' }}>
                                              Diastolic: {data.diastolic} mmHg
                                            </Typography>
                                          )}
                                          {data.validationError && (
                                            <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}>
                                              {data.validationError}
                                            </Typography>
                                          )}
                                          {isVoiceCall && (
                                            <Typography variant="caption" sx={{ color: '#9c27b0', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                              💬 Click the data point to view transcript
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Legend 
                                  verticalAlign="top" 
                                  height={36}
                                  formatter={(value) => {
                                    if (value === 'systolic') return 'Systolic';
                                    if (value === 'diastolic') return 'Diastolic';
                                    return value;
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="systolic"
                                  stroke="#d32f2f"
                                  strokeWidth={2}
                                  name="systolic"
                                  dot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    const isVoiceCall = payload.rawData?.source === 'voice_call';
                                    const voiceCallLogId = payload.rawData?.voice_call_log_id;
                                    const hasValue = payload?.systolic !== null && payload?.systolic !== undefined;

                                    if (!hasValue || !Number.isFinite(cx) || !Number.isFinite(cy)) {
                                      return <circle cx={0} cy={0} r={0} fill="transparent" />;
                                    }

                                    if (isVoiceCall) {
                                      return (
                                        <g
                                          style={{ cursor: 'pointer' }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA);
                                          }}
                                        >
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={8}
                                            fill="#9c27b0"
                                            stroke="white"
                                            strokeWidth={2}
                                          />
                                          <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="white"
                                            fontSize="10"
                                            fontWeight="bold"
                                            style={{ cursor: 'pointer', pointerEvents: 'none' }}
                                          >
                                            📞
                                          </text>
                                        </g>
                                      );
                                    }
                                    if (payload?.rawData?.source === 'device') {
                                      return <rect x={cx - 5} y={cy - 5} width={10} height={10} fill="#0288d1" stroke="white" strokeWidth={1} rx={2} />;
                                    }
                                    return <circle cx={cx} cy={cy} r={6} fill="#d32f2f" />;
                                  }}
                                  activeDot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    const isVoiceCall = payload?.rawData?.source === 'voice_call';
                                    const voiceCallLogId = payload?.rawData?.voice_call_log_id;
                                    const hasValue = payload?.systolic !== null && payload?.systolic !== undefined;

                                    if (!hasValue || !Number.isFinite(cx) || !Number.isFinite(cy)) {
                                      return <circle cx={0} cy={0} r={0} fill="transparent" />;
                                    }

                                    if (isVoiceCall) {
                                      return (
                                        <g
                                          style={{ cursor: 'pointer' }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA);
                                          }}
                                        >
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={12}
                                            fill="#9c27b0"
                                            stroke="white"
                                            strokeWidth={3}
                                            style={{ cursor: 'pointer' }}
                                          />
                                          <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="white"
                                            fontSize="12"
                                            fontWeight="bold"
                                            style={{ pointerEvents: 'none' }}
                                          >
                                            📞
                                          </text>
                                        </g>
                                      );
                                    }
                                    if (payload?.rawData?.source === 'device') {
                                      return <rect x={cx - 6} y={cy - 6} width={12} height={12} fill="#0288d1" stroke="white" strokeWidth={2} rx={2} />;
                                    }
                                    return <circle cx={cx} cy={cy} r={8} fill="#d32f2f" stroke="white" strokeWidth={2} />;
                                  }}
                                  connectNulls
                                />
                                <Line
                                  type="monotone"
                                  dataKey="diastolic"
                                  stroke="#1976d2"
                                  strokeWidth={2}
                                  name="diastolic"
                                  dot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    const isVoiceCall = payload.rawData?.source === 'voice_call';
                                    const voiceCallLogId = payload.rawData?.voice_call_log_id;
                                    const hasValue = payload?.diastolic !== null && payload?.diastolic !== undefined;

                                    if (!hasValue || !Number.isFinite(cx) || !Number.isFinite(cy)) {
                                      return <circle cx={0} cy={0} r={0} fill="transparent" />;
                                    }

                                    if (isVoiceCall) {
                                      return (
                                        <g
                                          style={{ cursor: 'pointer' }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA);
                                          }}
                                        >
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={8}
                                            fill="#9c27b0"
                                            stroke="white"
                                            strokeWidth={2}
                                          />
                                          <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="white"
                                            fontSize="10"
                                            fontWeight="bold"
                                            style={{ cursor: 'pointer', pointerEvents: 'none' }}
                                          >
                                            📞
                                          </text>
                                        </g>
                                      );
                                    }
                                    if (payload?.rawData?.source === 'device') {
                                      return <rect x={cx - 5} y={cy - 5} width={10} height={10} fill="#0288d1" stroke="white" strokeWidth={1} rx={2} />;
                                    }
                                    return <circle cx={cx} cy={cy} r={6} fill="#1976d2" />;
                                  }}
                                  activeDot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    const isVoiceCall = payload?.rawData?.source === 'voice_call';
                                    const voiceCallLogId = payload?.rawData?.voice_call_log_id;
                                    const hasValue = payload?.diastolic !== null && payload?.diastolic !== undefined;

                                    if (!hasValue || !Number.isFinite(cx) || !Number.isFinite(cy)) {
                                      return <circle cx={0} cy={0} r={0} fill="transparent" />;
                                    }

                                    if (isVoiceCall) {
                                      return (
                                        <g
                                          style={{ cursor: 'pointer' }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA);
                                          }}
                                        >
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={12}
                                            fill="#9c27b0"
                                            stroke="white"
                                            strokeWidth={3}
                                            style={{ cursor: 'pointer' }}
                                          />
                                          <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="white"
                                            fontSize="12"
                                            fontWeight="bold"
                                            style={{ pointerEvents: 'none' }}
                                          >
                                            📞
                                          </text>
                                        </g>
                                      );
                                    }
                                    if (payload?.rawData?.source === 'device') {
                                      return <rect x={cx - 6} y={cy - 6} width={12} height={12} fill="#0288d1" stroke="white" strokeWidth={2} rx={2} />;
                                    }
                                    return <circle cx={cx} cy={cy} r={8} fill="#1976d2" stroke="white" strokeWidth={2} />;
                                  }}
                                  connectNulls
                                />
                              </LineChart>
                            </ResponsiveContainer>
                            {devicePointCount > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Blue squares = device readings ({devicePointCount}) | Red/Blue circles = self-reported
                              </Typography>
                            )}
                          </Box>
                        ) : isNumeric && chartData.length > 0 ? (
                          <Box sx={{ mt: 2, mb: 2 }}>
                            <ResponsiveContainer width="100%" height={400}>
                              <LineChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 60, bottom: 60 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="displayDate"
                                  label={{ value: 'Date', position: 'insideBottom', offset: -10 }}
                                  height={80}
                                  padding={getXAxisPadding(chartData.length)}
                                  tick={(props: any) => {
                                    const { x, y, payload } = props;
                                    const dataPoint = chartData.find((d: any) => d.displayDate === payload.value);
                                    const hasValue = dataPoint?.hasValue ?? true;
                                    return (
                                      <g transform={`translate(${x},${y})`}>
                                        <text
                                          x={0} y={0} dy={16}
                                          textAnchor="end"
                                          fill={hasValue ? '#666' : '#bbb'}
                                          transform="rotate(-45)"
                                          style={{ fontSize: '12px' }}
                                        >
                                          {payload.value}
                                        </text>
                                      </g>
                                    );
                                  }}
                                />
                                <YAxis
                                  label={{ value: 'Value', angle: -90, position: 'insideLeft' }}
                                />
                                <Tooltip
                                  content={({ active, payload }: any) => {
                                    if (active && payload && payload.length) {
                                      const data = payload[0].payload;
                                      const isVoiceCall = data.rawData?.source === 'voice_call';
                                      return (
                                        <Box
                                          sx={{
                                            bgcolor: 'white',
                                            p: 1.5,
                                            border: '1px solid #ccc',
                                            borderRadius: 1,
                                            boxShadow: 1,
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                              {data.displayDate}
                                            </Typography>
                                            {getCollectionMethodIcon(data.rawData)}
                                          </Box>
                                          <Typography variant="body2" color={data.hasValue ? "primary" : "text.secondary"}>
                                            {data.hasValue ? `Value: ${data.value}` : 'No response'}
                                          </Typography>
                                          {isVoiceCall && (
                                            <Typography variant="caption" sx={{ color: '#9c27b0', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                              💬 Click the data point to view transcript
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="value"
                                  stroke="#1976d2"
                                  strokeWidth={2}
                                  dot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    const isVoiceCall = payload.rawData?.source === 'voice_call';
                                    const voiceCallLogId = payload.rawData?.voice_call_log_id;
                                    const hasValue = payload?.hasValue === true;

                                    if (!hasValue || !Number.isFinite(cx) || !Number.isFinite(cy)) {
                                      return <circle cx={0} cy={0} r={0} fill="transparent" />;
                                    }

                                    if (isVoiceCall) {
                                      return (
                                        <g
                                          style={{ cursor: 'pointer' }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA);
                                          }}
                                        >
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={8}
                                            fill="#9c27b0"
                                            stroke="white"
                                            strokeWidth={2}
                                          />
                                          <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="white"
                                            fontSize="10"
                                            fontWeight="bold"
                                            style={{ cursor: 'pointer', pointerEvents: 'none' }}
                                          >
                                            📞
                                          </text>
                                        </g>
                                      );
                                    }
                                    if (payload?.rawData?.source === 'device') {
                                      return <rect x={cx - 5} y={cy - 5} width={10} height={10} fill="#0288d1" stroke="white" strokeWidth={1} rx={2} />;
                                    }
                                    return <circle cx={cx} cy={cy} r={6} fill="#1976d2" />;
                                  }}
                                  activeDot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    const isVoiceCall = payload?.rawData?.source === 'voice_call';
                                    const voiceCallLogId = payload?.rawData?.voice_call_log_id;
                                    const hasValue = payload?.hasValue === true;

                                    if (!hasValue || !Number.isFinite(cx) || !Number.isFinite(cy)) {
                                      return <circle cx={0} cy={0} r={0} fill="transparent" />;
                                    }

                                    if (isVoiceCall) {
                                      return (
                                        <g
                                          style={{ cursor: 'pointer' }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA);
                                          }}
                                        >
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={12}
                                            fill="#9c27b0"
                                            stroke="white"
                                            strokeWidth={3}
                                            style={{ cursor: 'pointer' }}
                                          />
                                          <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="white"
                                            fontSize="12"
                                            fontWeight="bold"
                                            style={{ pointerEvents: 'none' }}
                                          >
                                            📞
                                          </text>
                                        </g>
                                      );
                                    }
                                    if (payload?.rawData?.source === 'device') {
                                      return <rect x={cx - 6} y={cy - 6} width={12} height={12} fill="#0288d1" stroke="white" strokeWidth={2} rx={2} />;
                                    }

                                    return (
                                      <circle
                                        cx={cx}
                                        cy={cy}
                                        r={8}
                                        fill="#1976d2"
                                        stroke="white"
                                        strokeWidth={2}
                                      />
                                    );
                                  }}
                                  connectNulls
                                />
                                {/* Line for missing values - has invisible but hoverable dot for tooltip */}
                                <Line
                                  type="monotone"
                                  dataKey="placeholderY"
                                  stroke="none"
                                  dot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    if (payload.hasValue) {
                                      return <circle cx={cx} cy={cy} r={0} fill="transparent" />;
                                    }
                                    // Show purple phone dot for voice call no-response data points
                                    const isVoiceCall = payload?.rawData?.source === 'voice_call';
                                    const voiceCallLogId = payload?.rawData?.voice_call_log_id;
                                    if (isVoiceCall && voiceCallLogId && Number.isFinite(cx) && Number.isFinite(cy)) {
                                      return (
                                        <g
                                          style={{ cursor: 'pointer' }}
                                          onClick={(event: any) => {
                                            event.stopPropagation();
                                            handleOpenVoiceCallModal(voiceCallLogId);
                                          }}
                                        >
                                          <circle cx={cx} cy={cy} r={8} fill="#9c27b0" stroke="white" strokeWidth={2} opacity={0.5} />
                                          <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="white"
                                            fontSize="10"
                                            fontWeight="bold"
                                            style={{ cursor: 'pointer', pointerEvents: 'none' }}
                                          >
                                            📞
                                          </text>
                                        </g>
                                      );
                                    }
                                    return (
                                      <circle
                                        cx={cx}
                                        cy={cy}
                                        r={10}
                                        fill="transparent"
                                        style={{ cursor: 'pointer' }}
                                      />
                                    );
                                  }}
                                  activeDot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    if (payload.hasValue) {
                                      return <circle cx={cx} cy={cy} r={0} fill="transparent" />;
                                    }
                                    // Show larger purple phone dot on hover for voice call no-response data points
                                    const isVoiceCall = payload?.rawData?.source === 'voice_call';
                                    const voiceCallLogId = payload?.rawData?.voice_call_log_id;
                                    if (isVoiceCall && voiceCallLogId && Number.isFinite(cx) && Number.isFinite(cy)) {
                                      return (
                                        <g
                                          style={{ cursor: 'pointer' }}
                                          onClick={(event: any) => {
                                            event.stopPropagation();
                                            handleOpenVoiceCallModal(voiceCallLogId);
                                          }}
                                        >
                                          <circle cx={cx} cy={cy} r={12} fill="#9c27b0" stroke="white" strokeWidth={2} opacity={0.6} />
                                          <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="white"
                                            fontSize="12"
                                            fontWeight="bold"
                                            style={{ cursor: 'pointer', pointerEvents: 'none' }}
                                          >
                                            📞
                                          </text>
                                        </g>
                                      );
                                    }
                                    return (
                                      <circle
                                        cx={cx}
                                        cy={cy}
                                        r={10}
                                        fill="transparent"
                                      />
                                    );
                                  }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                            {(() => {
                              const numericDeviceCount = Object.entries(dateAnswers)
                                .filter(([, v]: [string, any]) => v?.raw_data?.source === 'device').length;
                              return numericDeviceCount > 0 ? (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                  Blue squares = device readings ({numericDeviceCount}) | Blue circles = self-reported
                                </Typography>
                              ) : null;
                            })()}
                          </Box>
                        ) : isHospitalizationQuestion ? (() => {
                          const sortedEntries = Object.entries(answerSet)
                            .filter(([key]) => key !== 'question')
                            .filter(([date]) => !isNaN(new Date(date).getTime()))
                            .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime());
                          // Use legacy 5-column table if any entry has the old multi-choice format
                          const hasLegacyAnswers = sortedEntries.some(([, answerData]) => {
                            const ans = getAnswerValue(answerData);
                            return typeof ans === 'string' && ans in ENCOUNTER_TYPE_FROM_ANSWER;
                          });
                          return (
                          <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Hospitalization</TableCell>
                                  {hasLegacyAnswers && <TableCell>Type</TableCell>}
                                  {hasLegacyAnswers && <TableCell>Date of Visit</TableCell>}
                                  <TableCell>Reason</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sortedEntries.map(([date, answerData]) => {
                                    const answer = getAnswerValue(answerData);
                                    const rawData = getRawData(answerData);
                                    const isVoiceCall = rawData?.source === 'voice_call';
                                    const voiceCallLogId = rawData?.voice_call_log_id;
                                    const isLegacyRow = typeof answer === 'string' && (answer as string).startsWith('yes_');
                                    const isPositiveNew = answer === true || answer === 'yes';
                                    const matchedEvent = (isLegacyRow || isPositiveNew)
                                      ? findMatchingCareTransitionEvent(date, patient.care_transition_events || [])
                                      : null;
                                    const encounterType = matchedEvent?.encounter_type || (isLegacyRow ? ENCOUNTER_TYPE_FROM_ANSWER[answer as string] : null);
                                    const answerKey = String(answer);
                                    const chipLabel = HOSPITALIZATION_ANSWER_LABELS[answerKey] || answerKey;
                                    const isPositiveChip = isLegacyRow || isPositiveNew;
                                    const reasonText = isLegacyRow
                                      ? matchedEvent?.reason_text
                                      : (patient.answers_by_prompt?.[HOSPITALIZATION_REASON_QUESTION]?.[date] as any)?.value || matchedEvent?.reason_text;
                                    return (
                                    <TableRow key={date}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {formatDateTime(date)}
                                          {isVoiceCall ? (
                                            <MuiTooltip
                                              title={
                                                voiceCallLogId
                                                  ? 'View call transcript and recording'
                                                  : 'No transcript or recording data available'
                                              }
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() => handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA)}
                                                sx={{
                                                  color: '#9c27b0',
                                                  padding: '2px',
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(156, 39, 176, 0.1)'
                                                  }
                                                }}
                                              >
                                                <PhoneInTalk sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </MuiTooltip>
                                          ) : (
                                            getCollectionMethodIcon(rawData)
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={chipLabel}
                                          size="small"
                                          color={isPositiveChip ? 'error' : 'default'}
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      {hasLegacyAnswers && (
                                        <TableCell>
                                          {encounterType ? (
                                            <Chip
                                              label={ENCOUNTER_TYPE_LABELS[encounterType] || encounterType}
                                              size="small"
                                              color={ENCOUNTER_TYPE_CHIP_COLOR[encounterType] || 'default'}
                                              variant="outlined"
                                            />
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">N/A</Typography>
                                          )}
                                        </TableCell>
                                      )}
                                      {hasLegacyAnswers && (
                                        <TableCell>
                                          {matchedEvent?.reported_date ? (
                                            format(new Date(matchedEvent.reported_date + 'T00:00:00'), 'MMM dd, yyyy')
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">N/A</Typography>
                                          )}
                                        </TableCell>
                                      )}
                                      <TableCell>
                                        {reasonText || (
                                          <Typography variant="body2" color="text.secondary">N/A</Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                    );
                                  })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          );
                        })()
                        : (
                          <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Answer</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(answerSet)
                                  .filter(([key]) => key !== 'question')
                                  .filter(([date]) => !isNaN(new Date(date).getTime()))
                                  .filter(([, answerData]) => {
                                    const rawData = getRawData(answerData);
                                    if (rawData?.source !== 'voice_call') return true;
                                    const val = getAnswerValue(answerData);
                                    return val !== 'skipped' && val !== 'no_response' && rawData?.skipped !== true;
                                  })
                                  .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
                                  .map(([date, answerData]) => {
                                    const answer = getAnswerValue(answerData);
                                    const rawData = getRawData(answerData);
                                    const isVoiceCall = rawData?.source === 'voice_call';
                                    const voiceCallLogId = rawData?.voice_call_log_id;
                                    return (
                                    <TableRow key={date}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {formatDateTime(date)}
                                          {isVoiceCall ? (
                                            <MuiTooltip
                                              title={
                                                voiceCallLogId
                                                  ? 'View call transcript and recording'
                                                  : 'No transcript or recording data available'
                                              }
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() => handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA)}
                                                sx={{
                                                  color: '#9c27b0',
                                                  padding: '2px',
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(156, 39, 176, 0.1)'
                                                  }
                                                }}
                                              >
                                                <PhoneInTalk sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </MuiTooltip>
                                          ) : (
                                            getCollectionMethodIcon(rawData)
                                          )}
                                          {rawData?.source === 'device' && (
                                            <Chip label="Device" size="small" color="info" variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }} />
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        {answer !== null && answer !== undefined ? (
                                          typeof answer === 'boolean' ? (
                                            <Chip
                                              label={answer ? 'Yes' : 'No'}
                                              size="small"
                                              variant="outlined"
                                            />
                                          ) : typeof answer === 'object' && answer !== null && ('systolic' in answer || 'diastolic' in answer || 'isInvalid' in answer) ? (
                                            (() => {
                                              const parsedBP = parseBloodPressure(answer);
                                              
                                              // Check if this is an invalid input that requires clinician attention
                                              if (parsedBP.isInvalid) {
                                                return (
                                                  <Box>
                                                    <Chip 
                                                      label={`⚠️ Reading Outside Target Range: "${parsedBP.rawInput}"`}
                                                      size="small" 
                                                      color="error"
                                                      variant="filled"
                                                      sx={{ fontWeight: 'bold' }}
                                                    />
                                                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                                      {parsedBP.validationError || 'Patient needs education on proper BP format'}
                                                    </Typography>
                                                  </Box>
                                                );
                                              }
                                              
                                              // Valid BP input
                                              return (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                  {parsedBP.systolic !== undefined && (
                                                    <Chip 
                                                      label={`Systolic: ${parsedBP.systolic}`} 
                                                      size="small" 
                                                      sx={{ bgcolor: '#ffebee', color: '#d32f2f' }}
                                                      variant="outlined" 
                                                    />
                                                  )}
                                                  {parsedBP.diastolic !== undefined && (
                                                    <Chip 
                                                      label={`Diastolic: ${parsedBP.diastolic}`} 
                                                      size="small" 
                                                      sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
                                                      variant="outlined" 
                                                    />
                                                  )}
                                                </Box>
                                              );
                                            })()
                                          ) : (
                                            <Chip
                                              /* PHA-992: route through formatAnswer so an
                                                 object-valued answer renders as "No response"
                                                 instead of String({}) -> "[object Object]". */
                                              label={formatAnswerDisplay(formatAnswer(answer))}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                            />
                                          )
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            No response
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                  })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
              );
            })()}

            {/* Patient Outreach Answers */}
            {patient.custom_answers_by_prompt && Object.keys(patient.custom_answers_by_prompt).length > 0 && (
              <Box sx={{ mt: 6 }}>
                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
                  Patient Outreach Answers
                </Typography>
                <Stack spacing={2}>
                  {Object.entries(patient.custom_answers_by_prompt)
                  .sort(([qA, aA], [qB, aB]) => {
                    const scoreA = getAnswerSetUrgencyScore(qA, aA, {
                      hospitalizationQuestion: HOSPITALIZATION_QUESTION,
                      positiveHospitalizationAnswers: POSITIVE_HOSPITALIZATION_ANSWERS,
                      getAnswerValue,
                    });
                    const scoreB = getAnswerSetUrgencyScore(qB, aB, {
                      hospitalizationQuestion: HOSPITALIZATION_QUESTION,
                      positiveHospitalizationAnswers: POSITIVE_HOSPITALIZATION_ANSWERS,
                      getAnswerValue,
                    });
                    return scoreB - scoreA;
                  })
                  .map(([question, dateAnswers], index) => {
                    const answerSet = { question, ...dateAnswers };
                    return (
                      <Card key={index} variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {answerSet.question}
                          </Typography>
                          <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Answer</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(answerSet)
                                  .filter(([key]) => key !== 'question')
                                  .filter(([date]) => !isNaN(new Date(date).getTime()))
                                  .filter(([, answerData]) => {
                                    const rawData = getRawData(answerData);
                                    if (rawData?.source !== 'voice_call') return true;
                                    const val = getAnswerValue(answerData);
                                    return val !== 'skipped' && val !== 'no_response' && rawData?.skipped !== true;
                                  })
                                  .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
                                  .map(([date, answerData]) => {
                                    const answer = getAnswerValue(answerData);
                                    const rawData = getRawData(answerData);
                                    const isVoiceCall = rawData?.source === 'voice_call';
                                    const voiceCallLogId = rawData?.voice_call_log_id;
                                    return (
                                      <TableRow key={date}>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {formatDateTime(date)}
                                            {isVoiceCall ? (
                                              <MuiTooltip
                                                title={
                                                  voiceCallLogId
                                                    ? 'View call transcript and recording'
                                                    : 'No transcript or recording data available'
                                                }
                                              >
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleOpenVoiceCallModal(voiceCallLogId ?? NO_VOICE_CALL_DATA)}
                                                  sx={{
                                                    color: '#9c27b0',
                                                    padding: '2px',
                                                    '&:hover': {
                                                      backgroundColor: 'rgba(156, 39, 176, 0.1)'
                                                    }
                                                  }}
                                                >
                                                  <PhoneInTalk sx={{ fontSize: 16 }} />
                                                </IconButton>
                                              </MuiTooltip>
                                            ) : (
                                              getCollectionMethodIcon(rawData)
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          {answer !== null && answer !== undefined ? (
                                            typeof answer === 'boolean' ? (
                                              <Chip
                                                label={answer ? 'Yes' : 'No'}
                                                size="small"
                                                color={answer ? 'success' : 'default'}
                                                variant="outlined"
                                              />
                                            ) : (
                                              <Chip
                                                /* PHA-992: the Patient Outreach Answers table
                                                   (ad-hoc surveys) still used String(answer),
                                                   so an object-valued answer rendered as
                                                   "[object Object]". formatAnswer unwraps the
                                                   nested shape and yields the real answer. */
                                                label={formatAnswerDisplay(formatAnswer(answer))}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                              />
                                            )
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">
                                              No response
                                            </Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* No answers fallback */}
            {(!patient.answers_by_prompt || Object.keys(patient.answers_by_prompt).length === 0) &&
             (!patient.custom_answers_by_prompt || Object.keys(patient.custom_answers_by_prompt).length === 0) && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No answer history available for this patient.
              </Typography>
            )}
          </Box>
        )}

        {/* Device Readings Tab */}
        {currentTab === 8 && (
          <PatientDeviceReadings
            patientId={patient.patient_id}
            clinicName={patient.organization_name}
            logoPath={patient.logo_path}
            patientPhone={patient.phone_number}
            currentUserName={user?.name || user?.email || undefined}
          />
        )}

        {/* PHA-795 / PHA-831 (WS3): Voice of the patient tab — staged hybrid wizard.
            Structure (PHA-831): goals panel FIRST (de-emphasise narrative inputs),
            compact narrative/source/context section BELOW.
            GAS history charts REMOVED (measurements belong in the Answers tab). */}
        {voiceOfPatientEnabled && currentTab === PATIENT_DETAIL_TAB_INDEX.VOICE_OF_PATIENT && (
          <Box sx={{ mt: 2 }}>
            <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Person-centered care</Typography>

                {/* ── Saved Voice-of-Patient goal (read view) ── */}
                {Array.isArray(patient.person_centered_goals) &&
                  patient.person_centered_goals.length > 0 && (() => {
                    // PHA-850: barriers render under their goal; `unassigned` covers
                    // barriers not attributable to a single goal (multi-goal, no link).
                    const { perGoal: vopPerGoal, unassigned: vopUnassigned } = groupBarriersByGoal(
                      patient.person_centered_goals as any[],
                      (patient.pcm_barriers as any[]) ?? []
                    );
                    return (
                    <Stack spacing={2} sx={{ mb: 2 }}>
                      {vopPerGoal.map(({ goal: g, barriers }: any, gi: number) => {
                        const gas = Array.isArray(patient.gas_definitions)
                          ? (patient.gas_definitions as any[]).find(
                              (d: any) => d.pco_ref === g.pco_ref
                            )
                          : null;
                        return (
                          <Card key={g.pco_ref ?? gi} variant="outlined">
                            <CardContent>
                              {/* PHA-851: per-goal edit/remove (goals are additive). */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                  {g.goal_text}
                                </Typography>
                                <Box sx={{ flexShrink: 0 }}>
                                  <IconButton size="small" aria-label="Edit goal" onClick={() => openVopGoalEditor(g)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    aria-label="Remove goal"
                                    disabled={removingVopRef === g.pco_ref}
                                    onClick={() => handleRemoveVopGoal(g)}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                              {g.tracking_question && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Tracking question: {g.tracking_question}
                                </Typography>
                              )}
                              {gas && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Goal Attainment Scale
                                  </Typography>
                                  {/* PHA-905: highest level first, matching the chart's Y axis. */}
                                  {[
                                    ['+2', gas.level_plus2],
                                    ['+1', gas.level_plus1],
                                    ['0', gas.level_zero],
                                    ['−1', gas.level_minus1],
                                    ['−2', gas.level_minus2],
                                  ]
                                    .filter(([, v]) => v)
                                    .map(([lbl, v]) => (
                                      <Typography key={lbl as string} variant="body2">
                                        <strong>{lbl}</strong>: {v as string}
                                      </Typography>
                                    ))}
                                </Box>
                              )}
                              {/* PHA-850: this goal's barriers, nested in context. */}
                              {barriers.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                    Barriers we&apos;re working on together
                                  </Typography>
                                  {barriers.map((b: any, bi: number) => (
                                    <Typography key={b.barrier_ref ?? bi} variant="body2">
                                      <Chip label={b.barrier_category} size="small" sx={{ mr: 1 }} />
                                      {b.barrier_text}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                      {vopUnassigned.length > 0 && (
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Barriers
                              </Typography>
                              {vopUnassigned.map((b: any, bi: number) => (
                                <Typography key={b.barrier_ref ?? bi} variant="body2">
                                  <Chip label={b.barrier_category} size="small" sx={{ mr: 1 }} />
                                  {b.barrier_text}
                                </Typography>
                              ))}
                            </CardContent>
                          </Card>
                        )}
                    </Stack>
                    );
                  })()}

                {/* ── Staged wizard (opens on "+ Create Voice of Patient Goals") ── */}
                {voiceWizardOpen ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <PcmIntakeReviewPanel
                      // PHA-851: remount when switching between add/edit targets so the
                      // wizard re-seeds its state from the right goal.
                      key={editingVopGoal ? `edit-${editingVopGoal.pco_ref}` : 'add'}
                      patientId={patient.patient_id}
                      editingGoal={editingVopGoal}
                      // PHA-910: the wizard needs the saved goals to keep them out of the
                      // AI suggestion list and to show the next reference number.
                      existingGoals={
                        Array.isArray((patient as any).person_centered_goals)
                          ? ((patient as any).person_centered_goals as any[])
                          : []
                      }
                      onLocked={async () => {
                        if (onPatientUpdate) {
                          try {
                            const refreshed = await patientApi.get(patient.patient_id);
                            onPatientUpdate(refreshed);
                          } catch {
                            // ignore refresh errors — wizard toast already confirmed save
                          }
                        }
                      }}
                      onClose={() => {
                        setVoiceWizardOpen(false);
                        setEditingVopGoal(null);
                        setVoiceTabHasUnsaved(false);
                      }}
                      onUnsavedChangesChange={setVoiceTabHasUnsaved}
                    />
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<Add />}
                      onClick={() => {
                        setEditingVopGoal(null); // add mode — append a fresh goal
                        setVoiceWizardOpen(true);
                      }}
                    >
                      {Array.isArray(patient.person_centered_goals) && patient.person_centered_goals.length > 0
                        ? 'Add another goal'
                        : 'Create Voice of Patient Goals'}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* PHA-573: AI Insights Tab (only for orgs with smart_hub_ai_enabled) */}
        {aiInsightsEnabled && currentTab === PATIENT_DETAIL_TAB_INDEX.AI_INSIGHTS && (
          <AiInsightChartTab
            patientId={patient.patient_id}
            organizationId={patient.organization_id || patient.organization}
          />
        )}
      </DialogContent>

      {!inline && (
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      )}

      {/* Send Survey Dialog */}
      <Dialog open={sendSurveyDialogOpen} onClose={handleCloseSendSurveyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send Survey Link</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Enter patient's phone number:
            </Typography>
            <TextField
              fullWidth
              label="Phone Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="5513145855"
              helperText="Enter phone number without spaces or dashes"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSendSurveyDialog} disabled={sendingSurvey}>
            Cancel
          </Button>
          <Button
            onClick={handleSendSurvey}
            variant="contained"
            color="primary"
            disabled={sendingSurvey || !phoneNumber}
            startIcon={sendingSurvey ? <CircularProgress size={20} /> : <Send />}
          >
            {sendingSurvey ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VAPI AI Call Dialog */}
      <Dialog open={vapiCallDialogOpen} onClose={handleCloseVapiCallDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Start AI Phone Call</DialogTitle>
        <DialogContent>
          {vapiCallError && (
            <Alert severity="error" sx={{ mt: 1 }} data-testid="vapi-call-error">
              {vapiCallError}
            </Alert>
          )}
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Start an AI-powered voice call with the patient for: <strong>{selectedSurveyName}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Phone Number"
              value={vapiPhoneNumber}
              onChange={(e) => setVapiPhoneNumber(e.target.value)}
              placeholder="+15513145855"
              helperText="Enter phone number with country code (e.g., +1 for US)"
              autoFocus
              sx={{ mt: 2 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Note: This will initiate an AI voice assistant call to collect health information from the patient.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVapiCallDialog} disabled={callingPatient}>
            Cancel
          </Button>
          <Button
            onClick={handleStartVapiCall}
            variant="contained"
            color="secondary"
            disabled={callingPatient || !vapiPhoneNumber}
            startIcon={callingPatient ? <CircularProgress size={20} /> : <Phone />}
          >
            {callingPatient ? 'Starting Call...' : 'Start Call'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PHA-1043: care-plan refresh source picker. Refresh/update flow only — the
          first-time onboarding wizard never reaches this. */}
      {patient && (
        <Dialog
          open={refreshSourceDialogOpen}
          onClose={() => setRefreshSourceDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Regenerate care plan</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which problem list and medication list the new care plan should be
              built from.{' '}
              {/* PHA-1119: a mid-onboarding patient has no approved plan to reassure the
                  clinician about — their existing plan is the unfinished draft, and
                  regenerating replaces it. Saying otherwise would be a false promise. */}
              {needsContinueOnboarding(patient)
                ? 'This patient has no approved care plan yet, so the new plan replaces the draft they currently have.'
                : 'The current care plan stays in place until you approve the new one.'}
            </Typography>
            <RadioGroup
              value={refreshSource}
              onChange={(e) => setRefreshSource(e.target.value as CarePlanRefreshSource)}
            >
              {getCarePlanRefreshSourceOptions(
                (patient as any)?.source === 'FHIR',
                orgHasEhrIntegration
              ).map(
                (option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    sx={{ alignItems: 'flex-start', mb: 1.5, mr: 0 }}
                    label={
                      <Box sx={{ pt: 0.75 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    }
                  />
                )
              )}
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRefreshSourceDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                setRefreshSourceDialogOpen(false);
                handleClose();
                sessionStorage.setItem(
                  REGENERATE_PATIENT_ID_STORAGE_KEY,
                  patient.patient_id
                );
                sessionStorage.setItem(REGENERATE_SOURCE_STORAGE_KEY, refreshSource);
                navigate('/?tab=onboarding&_resume=' + Date.now());
              }}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Time Tracking Dialog */}
      {patient && (
        <TimeTrackingDialog
          open={timeTrackingDialogOpen}
          onClose={() => setTimeTrackingDialogOpen(false)}
          patientId={patient.patient_id}
          patientName={`${patient.first_name} ${patient.last_name}`}
          onSuccess={() => {
            console.log('Time logged successfully');
            // Refresh action report to show new time tracking entry in Event Log
            refreshActionReport();
            // Refresh patient data to show new time tracking entry in Event Log
            if (onPatientUpdate && patient) {
              // Trigger a refresh by passing the patient object
              // The parent component should refetch the patient data
              onPatientUpdate(patient);
            }
          }}
        />
      )}

      {/* Voice Call Details Modal */}
      <Dialog
        open={voiceCallModalOpen}
        onClose={handleCloseVoiceCallModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneInTalk color="secondary" />
            <Typography variant="h6">Voice Call Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedVoiceCallLogId === NO_VOICE_CALL_DATA ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No transcript or recording data available for this voice call.
            </Alert>
          ) : selectedVoiceCallLogId && loadingVoiceCallDetails[selectedVoiceCallLogId] ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedVoiceCallLogId && voiceCallDetails[selectedVoiceCallLogId] ? (
            <VoiceCallDetails
              voiceCallLogId={selectedVoiceCallLogId}
              hasRecording={voiceCallDetails[selectedVoiceCallLogId].hasRecording}
              transcript={voiceCallDetails[selectedVoiceCallLogId].transcript}
              annotatedTranscript={voiceCallDetails[selectedVoiceCallLogId].annotatedTranscript}
              durationSeconds={voiceCallDetails[selectedVoiceCallLogId].durationSeconds}
              status={voiceCallDetails[selectedVoiceCallLogId].status}
              callId={voiceCallDetails[selectedVoiceCallLogId].callId}
            />
          ) : (
            <Alert severity="info">
              No voice call details available.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVoiceCallModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Custom Survey Creation Modal */}
      <Dialog
        open={customSurveyModalOpen}
        onClose={() => setCustomSurveyModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingSurveyId ? 'Edit Custom Survey' : 'Create Custom Survey'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Survey Name"
            fullWidth
            value={customSurveyName}
            onChange={(e) => setCustomSurveyName(e.target.value)}
            sx={{ mb: 3, mt: 1 }}
          />

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>Questions</Typography>

          {customSurveyQuestions.map((question, index) => (
            <Card key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack spacing={2}>
                <TextField
                  label="Question"
                  fullWidth
                  multiline
                  rows={2}
                  value={question.prompt}
                  onChange={(e) => handleQuestionChange(index, 'prompt', e.target.value)}
                  placeholder="Enter the question to ask the patient"
                />
                <Stack direction="row" spacing={2} alignItems="center">
                  {/* PHA-992: no default Answer Type. This used to default to "Text", so a
                      clinician who wrote "please answer yes or no" and never opened the dropdown
                      got a free-text question — the answer was then stored and rendered as a bare
                      string instead of a Yes/No. The type must be chosen explicitly. */}
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!question.prompt.trim() && !question.data_type}
                  >
                    <InputLabel>Answer Type</InputLabel>
                    <Select
                      value={question.data_type}
                      label="Answer Type"
                      onChange={(e) => handleQuestionChange(index, 'data_type', e.target.value)}
                    >
                      <MenuItem value="" disabled>
                        <em>Choose an answer type…</em>
                      </MenuItem>
                      <MenuItem value="str">Text</MenuItem>
                      <MenuItem value="bool">Yes/No</MenuItem>
                      <MenuItem value="int">Number (whole)</MenuItem>
                      <MenuItem value="float">Number (decimal)</MenuItem>
                      <MenuItem value="blood pressure">Blood Pressure</MenuItem>
                    </Select>
                    {!!question.prompt.trim() && !question.data_type && (
                      <FormHelperText>
                        Pick how the patient answers — Yes/No questions must be set to Yes/No
                      </FormHelperText>
                    )}
                  </FormControl>
                  {customSurveyQuestions.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveQuestion(index)}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Card>
          ))}

          <Button
            variant="text"
            startIcon={<Add />}
            onClick={handleAddQuestion}
            sx={{ mt: 1 }}
          >
            Add Question
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCustomSurveyModalOpen(false); setEditingSurveyId(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingSurveyId ? handleUpdateCustomSurvey : handleCreateCustomSurvey}
            disabled={
              creatingSurvey ||
              !customSurveyName.trim() ||
              (!editingSurveyId && customSurveyQuestions.every(q => !q.prompt.trim())) ||
              // PHA-992: a question with text but no chosen Answer Type must not be submittable —
              // that silent "Text" default is what turned intended Yes/No questions into free text.
              customSurveyQuestions.some(q => !!q.prompt.trim() && !q.data_type)
            }
          >
            {creatingSurvey ? (editingSurveyId ? 'Updating...' : 'Creating...') : (editingSurveyId ? 'Update Survey' : 'Create Survey')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PHA-870: Add/Edit Care Plan Note modal (PHA-1109 copy) */}
      <Dialog
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingReviewId != null ? 'Edit Care Plan Note' : 'Add Care Plan Note'}</DialogTitle>
        <DialogContent>
          {/* PHA-1109: notes are patient-facing — they land on the printed care plan. */}
          <Typography variant="body2" color="text.secondary">
            The notes entered here will be added to the care plan printout shared with the patient.
          </Typography>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Date of Note"
              fullWidth
              value={reviewDateMasked}
              onChange={(e) => setReviewDateMasked(maskDateInput(e.target.value))}
              placeholder="MM/DD/YYYY"
              error={!!reviewDateMasked && (isFutureMaskedDate(reviewDateMasked) || !maskedDateToIso(reviewDateMasked))}
              helperText={
                reviewDateMasked && isFutureMaskedDate(reviewDateMasked)
                  ? 'Note date cannot be in the future'
                  : 'MM/DD/YYYY'
              }
            />
            <TextField
              label="Patient Comments"
              fullWidth
              multiline
              rows={3}
              value={reviewPatientComments}
              onChange={(e) => setReviewPatientComments(e.target.value)}
              placeholder="Comments from the patient about their care plan"
            />
            <TextField
              label="Notes / Actions Taken"
              fullWidth
              multiline
              rows={3}
              value={reviewActionTaken}
              onChange={(e) => setReviewActionTaken(e.target.value)}
              placeholder="Notes and actions taken by the care team"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCarePlanReview}
            disabled={
              savingReview ||
              !reviewDateMasked.trim() ||
              !maskedDateToIso(reviewDateMasked) ||
              isFutureMaskedDate(reviewDateMasked)
            }
          >
            {savingReview ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PHA-870: Delete Care Plan Note confirmation (soft delete — history retained) */}
      <Dialog
        open={!!deletingReview}
        onClose={() => (deletingReviewBusy ? undefined : setDeletingReview(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Care Plan Note?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the note from the Care Plans tab. The record and its history are
            retained in the database and can be recovered if needed.
          </Typography>
          {deletingReview?.review_date && (
            <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
              Note Date: {isoToMaskedDate(deletingReview.review_date)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingReview(null)} disabled={deletingReviewBusy}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteReview}
            disabled={deletingReviewBusy}
          >
            {deletingReviewBusy ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Custom Survey Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setDeletingSurveyId(null); }}
      >
        <DialogTitle>Delete Custom Survey</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this custom survey? This will also delete any collected answers and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirmOpen(false); setDeletingSurveyId(null); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteCustomSurvey}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* PHA-1213: "Push Care Plan to EMR" waits until the EHR files or rejects the upload.
          The outcome is read from the same status the Pending poller refreshes, matched on
          this push's id. Closing early only stops waiting; the tab keeps the Pending state. */}
      {ehrPushWait && (() => {
        const lastPush = carePlanPushStatus?.last_push;
        const mine = lastPush?.push_id === ehrPushWait.pushId ? lastPush : null;
        const outcome = carePlanEhrPushStateOf(mine);
        const stillWaiting = outcome !== 'uploaded' && outcome !== 'failed';
        // A NEWER push replacing ours (ids come from the `ids` sequence), or 90s without an
        // answer: hand over to the tab. Merely "not ours" is not enough — until the refresh
        // after the push lands, the status still holds the previous, older push.
        const supersededByNewerPush =
          !mine && Number(lastPush?.push_id) > Number(ehrPushWait.pushId);
        const handedOff = stillWaiting && (ehrPushWait.timedOut || supersededByNewerPush);
        const close = () => setEhrPushWait(null);
        const failure = outcome === 'failed' ? describeEhrPushFailure(mine) : null;
        return (
          <Dialog open onClose={close} maxWidth="xs" fullWidth data-testid="care-plan-ehr-push-wait">
            <DialogTitle>
              {outcome === 'uploaded'
                ? 'Care Plan Uploaded'
                : outcome === 'failed'
                  ? 'Failed to Upload Care Plan'
                  : handedOff
                    ? 'Upload Still Pending'
                    : 'Uploading Care Plan to the EHR'}
            </DialogTitle>
            <DialogContent>
              {outcome === 'uploaded' && (
                <Alert severity="success">
                  {EHR_PUSH_UPLOADED_DETAIL}
                  {carePlanPushStatus?.folder_name ? ` (${carePlanPushStatus.folder_name})` : ''}.
                </Alert>
              )}
              {/* PHA-1223: clinician wording, never Cobalt's "Please contact support". */}
              {outcome === 'failed' && failure && (
                <Alert severity={failure.autoRetryScheduled ? 'warning' : 'error'}>
                  {failure.reason}. {failure.next}
                </Alert>
              )}
              {stillWaiting && handedOff && (
                <Typography variant="body2">{EHR_PUSH_SUBMITTED_DETAIL}</Typography>
              )}
              {stillWaiting && !handedOff && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2">
                    {EHR_PUSH_PENDING_DETAIL} The EHR usually confirms within a minute. You can
                    close this window — the result will appear in the Care Plans tab.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {outcome === 'failed' && (
                <Button
                  variant="contained"
                  onClick={() => { close(); void handlePushCarePlanToEhr(false); }}
                >
                  Retry
                </Button>
              )}
              <Button onClick={close}>{stillWaiting && !handedOff ? 'Close' : 'OK'}</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Snackbar for goal/task completion feedback */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarMessage(null)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );

  // PHA-509: render inline (no Dialog wrapper) when hosted inside a Dashboard
  // patient tab; legacy modal callers fall back to the full Dialog.
  if (inline) {
    return <Box sx={{ width: '100%' }}>{body}</Box>;
  }
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
    >
      {body}
    </Dialog>
  );
};

export default PatientDetail;
